/* ============================================================
   sm-vel-sida.js - page controller of the one product page kit
   (sections/sm-vel.liquid, assets/sm-sida.css, docs/sidukerfi.md).

   window.SMVelSida
     skra(kodi, adapter)   register a preview adapter (engine wrapper)
     byrja(rot)            boot every [data-sm-sida] under rot (auto on load)
     kr(aurar)             price text, 399000 -> "3.990 kr."
     sidur                 page instances: { rot, stada(), setja(lykill, gildi),
                           vandamal(), afbrigdi(), teikna() }

   Owns once for every page: steps and step bar, options by stable codes
   (never by option text), variant lookup, conditional steps, validation,
   price and quantity, the sticky buy bar, add to cart with status handling
   (never leaves the page) and the production file (window.SMFram, see
   FRAM_TEXTI below), the one cart sheet (window.SMKarfa from
   sm-main.js), focus handling and the live region. Events on document:
   'sm:karfa' { fjoldi, lina } after every successful add.

   The form keeps action="/cart/add" so apps can find it; the submit is
   handled here and stopped before sm-main.js sees it.

   Adapter contract (docs/sidukerfi.md 5), all optional except teikna:
     gerd 'svg'|'dom'|'ljosmynd', skuggi bool, lifandi bool (live badge)
     hlada(ctx) -> Promise|null       fonts, photos, geometry
     teikna(ctx, s)                   draw into ctx.teikning, idempotent
     lysing(s, ctx) -> text           spoken description (role=img)
     malband(s, ctx) -> {b, h, eining}|null
     smamynd(s, ctx) -> {src}|{svg}|null   cart sheet thumbnail
     athuga(s, ctx) -> [{kodi, reitur, stopp, texti, hnappur, strax, fyrst}]
                                       (strax false: shown after the first attempt;
                                       fyrst: said before a sold-out combination)
     eiginleikar(s, ctx) -> {name: value}  extra cart properties
     skrar(s, ctx) -> [{eiginleiki, skra}]  extra files
     framleidsla(s, ctx, grunnur) -> spec|Promise|null  production file:
                                       an SMFram spec (mm) built on grunnur;
                                       null = the JSON of grunnur only
     veggur(s, ctx) -> 'dokkur'|'ljos'|'hvitur'|null  (owner rule: light
                                       material on the darker wall; null lets
                                       the variant's colour swatch decide)
   K2 extensions (docs/sidukerfi.md, K2 kort E1 to E13), all optional:
     fylla bool                       the drawing fills the whole drawing box
                                       (engines that size themselves to a box)
     upphaf(ctx)                      once, after the steps exist, before the
                                       first sync (read the URL, build bodies)
     lifandi(s, ctx) -> bool          live badge for this state (else the
                                       adapter's lifandi flag)
     afbrigdi(s, ctx) -> {vara, id}|null   the adapter picks the product and
                                       variant from its own steps (null = not
                                       offered); undefined = the kit decides
     syna(lykill, s, ctx) -> bool     show or hide any step
     skref: { lykill: { teikna(el, s, ctx), gildi(s, ctx) -> text,
              buid(s, ctx) -> bool, fokus(el, kodi, ctx) } }  bodies of the
                                       'sersnid' steps (the kit owns the shell)
     eiginleikar may return an ARRAY of [name, value]: then it is the whole
                                       visible list, in order; the kit adds
                                       only files and production data after it
     framleidsla may return { json: {...} } (JSON only) or false (nothing)
     forhlada(s, ctx)                 once, on the first focus or pointer in
                                       the form (fonts, libraries)
     litaval(valkostir, ctx) -> index the option shown as colours and on the
                                       colour rail; litur(nafn) -> CSS background
     halda(nafn, ctx) -> bool         keep this option's value when the product
                                       changes (default true)
     mynd(ctx) / myndir(ctx)          the photo on slide 0 / the other slides
     hjalp(s, ctx) -> {lykill: text}  help lines that follow the state
     slod(s, ctx) -> {param: value|null}  extra URL parameters (never typed text)
   Samraeming (Kjarni v1, 2026-09-29; samraeming/kjarni/SAMNINGUR.md), all optional:
     utlit: { teikna(s2, ctx, o) -> {svg}|{html}|{src, klippa:[x,y,w,h], mal:[W,H]}|null
                (null: the tile keeps its fallback picture),
              lykill(s2, ctx, o) -> string of the look fields only (REQUIRED when the adapter keeps
                state of its own in sersnid steps; else the kit key cannot see that state) }
                                       one tile of a material / look / frame grid (markup with
                                       [data-vs-smamynd]): s2 = the page state with this tile's value
                                       applied (s2[o.lykill] = o.gildi, s2.rammi on frame tiles, kodar /
                                       afbrigdi / vara for option and cross product tiles), o = { id
                                       (unique id prefix; the kit rewrites every id of the drawing with
                                       it anyway), lykill, gildi, tegund 'utlit'|'rammi', vara, letur,
                                       smamynd: true }
     Kjarni v1.1 (2026-09-30), all optional:
     framTimi: ms            time for the production file (default 2400)
     framleidsla(...) may resolve { stopp: texti }: nothing is posted, texti shows as a cart error;
       a spec may carry json (string|object) next to hlutir: that JSON is sent as _framleidsla
       (SMVelSida.framJson)
     rammiEftir: key         the Rammi property goes right after this key of an adapter's own list
     section setting syna_valkosti off: option steps stay hidden after a product switch too
     letur: { texti(s, ctx) -> the customer's main line ('' = the sample),
              teikna(s2, ctx, o) -> {svg}|{html}|null (null = CSS mode: the webfont),
              css(letur, ctx) -> {fontFamily, fontWeight, fontStyle, textTransform, letterSpacing},
              flotur(s, ctx) -> {bak, blek} (the chosen material behind the letters),
              falid: bool|fn(s, ctx) (true: the real font id goes as the hidden _letur),
              lykill(s2, ctx, o) }       letur tiles ([data-vs-letur]): the customer's text in each font
     Kjarni v1.2 (2026-10-01), optional: aukaverd(s, ctx) -> aurar per piece added to every price (an add-on
       sold as its own cart line); aukalinur(s, ctx) -> [{ id, properties }] posted as lines of the same
       quantity right after the piece (see aukaSenda)
     rammi(s, ctx) -> { gerd 'lina'|'malmur'|null, inset, insetY, r, form 'rett'|'hringur'|'sporaskja',
              verd 'okeypis'|'malmur' }  frame of the current material and shape; gerd null hides the
                                       Rammi step (no plate). No hook: the step shows, free, property only
   Kit helpers: SMVelSida.einkvaemId(svg, prefix), SMVelSida.rammi(W, H, g) -> {hlutir, svg, lysing}
   (one geometry for preview and file, from SMFram 1.2.0), SMVelSida.leturLykill(id); ctx.leturHeiti(id),
   ctx.rammiGildi() -> 'med'|'an'|'', ctx.rammiGeo(), ctx.flisar() (redraw the tiles),
   ctx.veljaIVoru(handle, codes) -> variant. html.kostir takes birting 'utlit' | 'letur' (tiles, with
   hopur per choice for grouped grids, vara + o for cross product tiles, letur for font tiles).
   Kit units K0 (lota 0, 2026-10-08; yfirferd/KIT-UTTEKT.md 2), all on ctx.html, each returns HTML; gogn {name: value}
   -> data-*, attr -> plain attributes, ids as the adapter passes them, data-vs-fokus on every control:
     reitur({id, heiti, valfrjalst, gerd 'texti'|'svaedi'|'tala', hamark, synishorn, gildi, hjalp, villa, lysing, skylda,
       gogn, attr, reitGogn, falid}) .vs-reitur (label + counter + .vs-inntak + .vs-hjalp + .vs-villa; the kit keeps
       the counter right); reitir(listi, {dalkar}); villa(inntak|id, texti) error line + aria-invalid;
     rofi({id, nafn, gildi, heiti|html, undir, valid, ovirkt, lysing, gogn, klasi}) label.vs-rofi checkbox row;
     tillogur({texti, kostir:[{gildi, heiti, gogn}], gogn}) button.vs-tillaga[data-vs-tillaga];
     bod(texti, 'hjalp'|'vidvorun'|'villa', {id, gogn, falid, html}) p.vs-bod aria-live, bodSetja(el, texti, stig, html);
     listi({nafn, heiti, linur, hamark, hamarkLinur, lagmark, synishorn, gogn, baetaGogn}) .vs-listi with
       [data-vs-listi-lina|-fjarlaegja|-baeta] (the adapter keeps the lines); velja({id, heiti, kostir, gildi, autt})
       select.vs-velja; dags({id, heiti, ar:[fra, til], gildi:{d,m,a}, gogn, hlutiGogn}) three selects;
     stadfesting({inngangur, yfirlit:[[heiti, gildi|null]], vantar, hak: rofi options}) dl.vs-yfirlit + boxed rofi.
   ctx.heiti(lykill, texti, stutt) also renames the step bar chip when stutt is given (true: the same text).
   SMVelSida.kit(ord) returns the same builders (tests).
   Product family (E1): gogn.fjolskylda = { hopar, vorur } from
   snippets/sm-sida-fjolskylda.liquid; ctx.skipta(handle, id) swaps product,
   variants, photos and option steps in place and keeps typed text and quantity.

   ES5, ASCII only on disk. No customer text lives in this file: every
   string comes from the page data (snippets/sm-sida-ord.liquid).
   ============================================================ */
(function () {
  'use strict';
  if (window.SMVelSida) return;

  var ADAPTERS = {};
  var SIDUR = [];
  var LITIL_HREYFING = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  /* Emoji and other pictographs cannot be cut or engraved. Astral plane
     symbols, variation selector 16 and zero width joiner. */
  /* CK 19.10 (QA5): also the one-unit pictographs (U+2600-27BF hearts, stars, dingbats; U+2B50/2B55), the text/emoji
     selectors U+FE0E/FE0F and the keycap U+20E3. One test for every field of every line (tjakn below, ctx.tjakn). */
  var TJAKN = /[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27bf\u2b50\u2b55\u20e3\ufe0e\ufe0f\u200d]/;
  var TJAKN_G = /[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27bf\u2b50\u2b55\u20e3\ufe0e\ufe0f\u200d]/g;
  /* CW 19.10 (owner): the paw U+1F43E is the one symbol allowed, on the lines that engrave a name (LOPPA_VELAR); it is
     drawn as a vector (SMFram.loppa, the cross symbol "Loppa"), counts as one character, and the emoji selector after it
     (U+FE0F/FE0E) is dropped as it is typed, so every browser draws the same paw. Every other emoji stops as before. */
  var TJAKN_L = /(?!\uD83D\uDC3E)[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27bf\u2b50\u2b55\u20e3\ufe0e\ufe0f\u200d]/;
  var TJAKN_LG = /(?!\uD83D\uDC3E)[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27bf\u2b50\u2b55\u20e3\ufe0e\ufe0f\u200d]/g;
  var LOPPA_RE = /\uD83D\uDC3E/g, LOPPA_VS = /\uD83D\uDC3E[\uFE0E\uFE0F]+/g, LOPPA_VS1 = /\uD83D\uDC3E[\uFE0E\uFE0F]/;
  /* owner 20.10: ONLY door signs (every material). Crosses (the pet crosses have their own paw symbols), house numbers (cut
     through, 3 mm rule), mailbox and every other line stop the paw like any emoji, with the plain message and no button. */
  var LOPPA_VELAR = { hurd: 1 };
  var loppaNotud = false;
  /* characters as the customer counts them: the paw (two UTF-16 units) is one */
  function stafafjoldi(t) { t = String(t == null ? '' : t); var m = t.match(LOPPA_RE); return t.length - (m ? m.length : 0); }
  var tjakn = { test: function (t, loppa) { return (loppa ? TJAKN_L : TJAKN).test(String(t == null ? '' : t)); },
    hreinsa: function (t, loppa) { return String(t == null ? '' : t).replace(loppa ? TJAKN_LG : TJAKN_G, '').replace(/[ \t]{2,}/g, ' '); },
    loppa: function (nafn) { return !!LOPPA_VELAR[nafn]; }, stafir: stafafjoldi };
  /* a text field with the paw: the selector after it goes, maxlength grows by one per paw (the paw is one character) */
  function loppaReitur(x, inn) {
    if (!textaReitur(x)) return;
    var v = x.value || '';
    if (LOPPA_VS1.test(v)) {
      var pos = typeof x.selectionStart === 'number' ? x.selectionStart : v.length, fyrir = v.slice(0, pos).replace(LOPPA_VS, '\uD83D\uDC3E');
      v = v.replace(LOPPA_VS, '\uD83D\uDC3E'); x.value = v;
      try { if (document.activeElement === x) x.setSelectionRange(fyrir.length, fyrir.length); } catch (e) { }
    }
    var m0 = x.getAttribute('data-sm-hamark0');
    if (m0 == null && x.maxLength > 0 && (v.indexOf('\uD83D\uDC3E') >= 0 || inn)) { m0 = String(x.maxLength); x.setAttribute('data-sm-hamark0', m0); }
    if (m0 != null) { var n = v.length - stafafjoldi(v) + (inn || 0); x.maxLength = (+m0) + n; }
    if (v.indexOf('\uD83D\uDC3E') >= 0) loppaNotud = true;
  }
  /* the paw button (owner 19.10, "so people find it"): " \uD83D\uDC3E" at the caret, or at the end of the line the caret was
     last in when the field is not focused. Put in as typing (execCommand insertText: beforeinput/input), so every engine
     takes it like a typed paw: its own caps and words apply ("if full: the same message as otherwise"). */
  function loppaSetja(x) {
    if (!textaReitur(x)) return false;
    var v = x.value || '', fok = document.activeElement === x;
    var p = fok && typeof x.selectionStart === 'number' ? x.selectionStart : (typeof x.__smBendill === 'number' ? Math.min(x.__smBendill, v.length) : v.length);
    var q = fok && typeof x.selectionEnd === 'number' ? x.selectionEnd : p;
    if (!fok) { var e = v.indexOf('\n', p); p = q = e < 0 ? v.length : e; while (p > 0 && v.charAt(p - 1) === ' ' && v.charAt(p - 2) === ' ') { p--; q = p; } }
    var a = v.charAt(p - 1), inn = (p === 0 || a === ' ' || a === '\n' ? '' : ' ') + '\uD83D\uDC3E';
    var m0 = +(x.getAttribute('data-sm-hamark0') || (x.maxLength > 0 ? x.maxLength : 0));
    try { x.focus({ preventScroll: true }); } catch (e1) { x.focus(); }
    try { x.setSelectionRange(p, q); } catch (e2) { }
    if (m0 && stafafjoldi(v.slice(0, p) + v.slice(q)) + stafafjoldi(inn) > m0) return false; /* full: the counter says so */
    var ok = false;
    try { ok = document.execCommand && document.execCommand('insertText', false, inn); } catch (e3) { ok = false; }
    if (!ok || x.value === v) {
      loppaReitur(x, 1);
      x.value = v.slice(0, p) + inn + v.slice(q);
      try { x.setSelectionRange(p + inn.length, p + inn.length); } catch (e4) { }
      x.dispatchEvent(new Event('input', { bubbles: true }));
    }
    return true;
  }
  if (document.addEventListener) {
    /* the caret of a field when it loses focus: the paw button puts the paw at the end of that line */
    document.addEventListener('focusout', function (e) { var x = e && e.target; if (x && typeof x.selectionStart === 'number' && textaReitur(x)) x.__smBendill = x.selectionStart; }, true);
    /* capture: before the engines read the field */
    document.addEventListener('beforeinput', function (e) {
      var d = e && e.data;
      if (d && d.indexOf('\uD83D\uDC3E') >= 0) loppaReitur(e.target, (d.match(LOPPA_RE) || []).length);
    }, true);
    document.addEventListener('input', function (e) {
      var x = e && e.target;
      if (x && x.getAttribute && (String(x.value || '').indexOf('\uD83D\uDC3E') >= 0 || x.getAttribute('data-sm-hamark0') != null)) loppaReitur(x, 0);
    }, true);
  }
  /* a field a customer types into (text-like input or textarea), shown and enabled */
  function textaReitur(x) {
    if (!x || x.disabled || x.readOnly) return false;
    if (x.tagName === 'TEXTAREA') return true;
    return x.tagName === 'INPUT' && /^(text|tel|email|search|url)?$/i.test(x.getAttribute('type') || '');
  }
  /* CK 19.10: decimal commas in the engines' own words (7,5 x 12 cm, 1,6 mm) read as points outside Icelandic.
     Only the page's own text: never a field, the drawing, a tile or the cart sheet (the customer's text is there). */
  var ERIS = !/^[a-z]/i.test(document.documentElement.lang || '') || /^is\b/i.test(document.documentElement.lang || '');
  var TUGA_RE = /(\d),(\d)/g, TUGA_PROF = /\d,\d/;
  var TUGA_UTAN = 'input,textarea,script,style,svg,[contenteditable],[data-vs-teikning],[data-vs-smamynd],[data-vs-letur],.vs-hurd-flis,[data-hn],[data-sm-cart-sheet],[data-vs-ekki-tuga]';
  function tugaPunktar(rot) {
    if (ERIS || !rot || !document.createTreeWalker) return;
    var w = document.createTreeWalker(rot, 4, null), n, l = [];
    while ((n = w.nextNode())) {
      if (!TUGA_PROF.test(n.nodeValue)) continue;
      var p = n.parentNode;
      if (!p || (p.closest && p.closest(TUGA_UTAN))) continue;
      l.push(n);
    }
    l.forEach(function (x) { x.nodeValue = x.nodeValue.replace(TUGA_RE, '$1.$2'); });
    [].forEach.call(rot.querySelectorAll('[aria-label],[title]'), function (el) {
      if (el.closest('input,textarea,[data-sm-cart-sheet],[data-vs-smamynd],[data-vs-letur]')) return;
      ['aria-label', 'title'].forEach(function (a) { var v = el.getAttribute(a); if (v && TUGA_PROF.test(v)) el.setAttribute(a, v.replace(TUGA_RE, '$1.$2')); });
    });
  }
  /* T1 (owner 18.10): the English option translations stay on /en. snippets/sm-valkostir-ordabok.liquid (rendered only
     outside the primary language) turns the page data into Icelandic before any script reads it and sets
     window.SMValkostir. kanon(text, handle, teg) gives the Icelandic text (logic, cart properties, production file),
     syn() the shop-language text for display. teg 'o' = option name, 'v' = value. In Icelandic both return the text. */
  function kanon(t, h, teg) { var W = window.SMValkostir; return W && W.kanon ? W.kanon(t, h, teg) : t; }
  function syn(t, h, teg) { var W = window.SMValkostir; return W && W.syn ? W.syn(t, h, teg) : t; }

  /* ---- production file (window.SMFram: assets/sm-framleidsla.js through snippets/sm-framleidsla.liquid) ----
     Every add carries the hidden JSON property _framleidsla (the order line as an SMFram spec, the same
     spec sm-fram-almennt.js sends from the old page). When the piece can be drawn, the SVG file
     _Framleidsluskra and the line token _framleidsla_nr go with it, and the add goes through
     SMFram.senda (bounded wait, a lost answer is looked up in /cart.js, the line is never added twice,
     a refused file is sent again without it). The file never blocks a purchase: a time-out or any error
     leaves the JSON only, and without SMFram the add is exactly as before.
     Pieces come from adapter.framleidsla(s, ctx, grunnur) -> spec | Promise | null, or, on pages with no
     drawing engine, from FRAM_TEXTI: the customer's text cut as outlines, by product handle. */
  var FRAM_TIMI = 2400;
  var FRAM_TEXTI = {
    /* cake topper: the name in Dancing Script 700, cut (SKERA), words in lines as on the product photos
       (widest line at most 170 mm, else the type shrinks). Numbers as in sm-fram-almennt.js. */
    kokutoppur: { eig: 'Nafn', snid: 'kokutoppur@1', letur: 'dancingscript-700', mm: 45, lina: 0.98, hamark: 170, spassia: 10, upp: 0.92, nidur: 0.28 }
  };
  function r3(n) { return Math.round(n * 1000) / 1000; }
  function framVidvorun(t) { try { if (window.console) console.warn('[SMFram] ' + t); } catch (e) {} }
  function innan(p, ms) {
    return new Promise(function (res, rej) {
      var t = setTimeout(function () { var e = new Error(ms + ' ms'); e.kodi = 'timi'; rej(e); }, ms);
      p.then(function (x) { clearTimeout(t); res(x); }, function (e) { clearTimeout(t); rej(e); });
    });
  }
  function fdHefur(fd, k) { try { return !!fd.has(k); } catch (e) { return false; } }
  /* the base spec: product, the variant's option values by their shop names, SKU; no pieces */
  function grunnSpec(SM, gogn, afb) {
    var o = {}, litur = '', staerd = '';
    (gogn.valkostir || []).forEach(function (v, i) {
      var kodi = afb && afb.o ? afb.o[i] : null, g = null;
      if (kodi == null || v.nafn === 'Title') return;
      (v.gildi || []).forEach(function (x) { if (!g && String(x.kodi) === String(kodi)) g = x; });
      if (!g) return;
      o[v.nafn] = g.nafn;
      if (!litur && /litur|efni/i.test(v.nafn)) litur = g.nafn;
      if (!staerd && /st(\u00e6|ae)r(\u00f0|d)/i.test(v.nafn)) staerd = g.nafn;
    });
    return { vara: gogn.vara.handle, efni: litur ? SM.ascii(litur) : '', staerd: staerd ? SM.ascii(staerd) : '', snid: 'almennt@1',
      gogn: { afbrigdi: o, sku: (afb && afb.sku) || '' }, hlutir: [] };
  }
  /* text cut as outlines: words into lines of at most K.hamark mm, centred, baselines K.lina apart */
  function textaSpec(SM, sp, K, texti) {
    var ordin = texti.split(/\s+/).filter(Boolean), linur = [], nu = '', p = Promise.resolve();
    var maela = function (t) { return SM._innri.uppsetning(K.letur, { texti: t, mm: K.mm }).then(function (u) { return u.breidd; }); };
    ordin.forEach(function (o) {
      p = p.then(function () {
        if (!nu) { nu = o; return; }
        return maela(nu + ' ' + o).then(function (b) { if (b <= K.hamark) nu += ' ' + o; else { linur.push(nu); nu = o; } });
      });
    });
    return p.then(function () {
      linur.push(nu);
      return Promise.all(linur.map(maela));
    }).then(function (br) {
      var mest = Math.max.apply(null, br), mm = mest > K.hamark ? K.mm * K.hamark / mest : K.mm, bil = mm * K.lina;
      var w = r3(Math.min(mest, K.hamark) + 2 * K.spassia), h = r3(2 * K.spassia + (K.upp + K.nidur) * mm + (linur.length - 1) * bil);
      sp.w = w; sp.h = h; sp.staerd = ''; sp.snid = K.snid;
      sp.lysing = [[K.eig, texti], ['L\u00ednur', String(linur.length)]];
      sp.hlutir = linur.map(function (t, i) {
        return { gerd: 'texti', lag: 'SKERA-YTRA', texti: t, letur: K.letur, mm: r3(mm), x: r3(w / 2), y: r3(K.spassia + K.upp * mm + i * bil), jofnun: 'midja' };
      });
      return sp;
    });
  }

  function $(sel, rot) { return (rot || document).querySelector(sel); }
  function $$(sel, rot) { return Array.prototype.slice.call((rot || document).querySelectorAll(sel)); }
  function trim(t) { return String(t == null ? '' : t).replace(/^\s+|\s+$/g, ''); }
  function kr(aurar) {
    var n = Math.round((Number(aurar) || 0) / 100);
    return (n < 0 ? '-' : '') + String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' kr.';
  }
  function fylla(texti, b) {
    return String(texti || '').replace(/\{(\w+)\}/g, function (m, k) { return b && b[k] != null ? String(b[k]) : m; });
  }
  function esc(t) {
    return String(t == null ? '' : t).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; });
  }
  function hastafir(t) {
    return String(t).toLowerCase().replace(/(^|\s)([a-zA-Z\u00c0-\u024f])/g, function (m) { return m.toUpperCase(); });
  }
  /* ---- B1 (17.10): the cart shows the customer's own design ----
     When a line is added, the adapter's thumbnail SVG (the one the cart sheet shows) is drawn to a small
     picture and kept in this browser's localStorage under the line's _framleidsla_nr (else its key; sm-karfa-myndir); sections/sm-cart.liquid
     shows it in place of the product card picture. Nothing goes into the line properties, so the cart line and
     the production file are exactly as before. An SVG drawn as a picture loads nothing itself, so the fonts and
     pictures it uses are inlined as data: URLs first; when a font cannot be found (fonts loaded through the
     FontFace API have no readable source) nothing is kept and the cart shows the card picture as before. */
  var KARFA_MYNDIR = 'sm-karfa-myndir', KARFA_HAMARK = 40, KARFA_PX = 240;
  var SVG_NS = 'http://www.w3.org/2000/svg', XLINK_NS = 'http://www.w3.org/1999/xlink';
  function lesaBlob(b) {
    return new Promise(function (res, rej) { var f = new FileReader(); f.onload = function () { res(f.result); }; f.onerror = rej; f.readAsDataURL(b); });
  }
  function inlinaSlod(url, grunnur) {
    if (!url || /^data:/i.test(url)) return Promise.resolve(url);
    var u;
    try { u = new URL(url, grunnur || document.baseURI).href; } catch (e) { return Promise.reject(e); }
    return innan(fetch(u, { credentials: 'same-origin' }).then(function (r) { if (!r.ok) throw new Error('svar ' + r.status); return r.blob(); }).then(lesaBlob), 5000);
  }
  var ALMENN_LETUR = /^(serif|sans-serif|monospace|cursive|fantasy|system-ui|inherit|initial)$/i;
  function leturNofn(svg) {
    var nofn = {};
    var baeta = function (v) {
      String(v || '').split(',').forEach(function (x) { x = trim(x).replace(/^["']|["']$/g, ''); if (x && !ALMENN_LETUR.test(x)) nofn[x] = 1; });
    };
    $$('[font-family]', svg).forEach(function (el) { baeta(el.getAttribute('font-family')); });
    $$('[style]', svg).forEach(function (el) { var m = /font-family\s*:\s*([^;]+)/i.exec(el.getAttribute('style') || ''); if (m) baeta(m[1]); });
    $$('style', svg).forEach(function (el) { String(el.textContent || '').replace(/font-family\s*:\s*([^;}]+)/gi, function (m, v) { baeta(v); return m; }); });
    return Object.keys(nofn);
  }
  /* every @font-face rule of the page for these families, with its url() sources inlined */
  function leturReglur(nofn, laus) {
    if (!nofn.length) return Promise.resolve([]);
    var fundid = {}, bid = [];
    [].forEach.call(document.styleSheets, function (sh) {
      var rr;
      try { rr = sh.cssRules; } catch (e) { return; }
      [].forEach.call(rr || [], function (r) {
        if (r.type !== 5 || !r.style) return;
        var fam = trim(String(r.style.getPropertyValue('font-family') || '')).replace(/^["']|["']$/g, '');
        if (nofn.indexOf(fam) === -1) return;
        fundid[fam] = 1;
        var css = r.cssText, slodir = [], grunnur = sh.href || document.baseURI;
        css.replace(/url\(\s*(["']?)([^"')]+)\1\s*\)/g, function (m, q, u) { if (!/^data:/i.test(u)) slodir.push(u); return m; });
        bid.push(Promise.all(slodir.map(function (u) { return inlinaSlod(u, grunnur).then(function (d) { css = css.split(u).join(d); }); }))
          .then(function () { return css; }));
        /* CW 19.10: the paw (U+1F43E) as the same small font the page uses for it, so the cart picture shows the vector paw */
        var LP = loppaNotud && window.SMFram && window.SMFram.loppa;
        if (LP) try {
          var lw = r.style.getPropertyValue('font-weight') || '400', ls = r.style.getPropertyValue('font-style') || 'normal';
          var lb = new Uint8Array(LP.ttf(LP.hlutfall(fam, String(lw).split(' ')[0]))), lbin = '';
          for (var li = 0; li < lb.length; li++) lbin += String.fromCharCode(lb[li]);
          bid.push(Promise.resolve('@font-face{font-family:"' + fam + '";font-weight:' + lw + ';font-style:' + ls + ';unicode-range:U+1F43E,U+FE0F;src:url(data:font/ttf;base64,' + window.btoa(lbin) + ') format("truetype")}'));
        } catch (eL) { }
      });
    });
    var vantar = nofn.filter(function (f) { return !fundid[f]; });
    if (vantar.length && !laus) return Promise.reject(new Error('letur fannst ekki: ' + vantar.join(', ')));
    return Promise.all(bid);
  }
  /* QA18 P2 (CS 19.10): an engine picture (SVG, or HTML for door signs and house numbers) never goes in as raw HTML. It is parsed
     in an inert document (DOMParser: no script runs, no picture loads, no onerror fires) and cleaned like
     verkstaedi/app/shared/hreinsaSvg.js: script, foreignObject, iframe/object/embed and the like out, every on* attribute out,
     javascript:/vbscript:/data:text/html values out, animate/set that target href out. Then the nodes move into el.
     A clean picture gives the same DOM as innerHTML did (the same HTML parser). */
  /* P2-G1 21.10 (V06, QA5 nr 10): a visible cart property carries the text the production file carries: NFC, no invisible characters
     (the same set as sm-framleidsla.js OSYNILEGIR: BOM, zero width, soft hyphen...), tabs as spaces, each line trimmed and empty lines
     out. Spaces inside a line stay (the engines decide those). Hidden properties (_framleidsla, _listi...) are never touched. */
  var OSYNILEG_EIG = /[\u00ad\u034f\u061c\u115f\u1160\u17b4\u17b5\u180e\u200b-\u200f\u202a-\u202e\u2060-\u2064\ufe00-\ufe0f\ufeff]/g;
  function hreinsaEig(v) {
    var t = String(v == null ? '' : v);
    try { if (t.normalize) t = t.normalize('NFC'); } catch (e) {}
    return t.replace(OSYNILEG_EIG, '').replace(/\r\n?/g, '\n').replace(/\t/g, ' ').split('\n')
      .map(function (l) { return l.replace(/^[ \u00a0]+|[ \u00a0]+$/g, ''); }).filter(function (l) { return l !== ''; }).join('\n');
  }
  var HREINS_BANN = /^(script|foreignobject|iframe|frame|frameset|object|embed|applet|audio|video|handler|listener|base|meta|link|noscript|template|portal)$/;
  var HREINS_HREYFING = /^(animate|set|animatemotion|animatetransform|animatecolor)$/;
  function hreinsaInn(el, texti) {
    var doc = null;
    try { doc = new DOMParser().parseFromString('<!doctype html><html><head></head><body>' + String(texti || '') + '</body></html>', 'text/html'); } catch (e) {}
    while (el.firstChild) el.removeChild(el.firstChild);
    if (!doc || !doc.body) return el;
    [].slice.call(doc.body.querySelectorAll('*')).forEach(function (n) {
      var nafn = String(n.localName || n.nodeName || '').replace(/^.*:/, '').toLowerCase();
      if (HREINS_BANN.test(nafn) || (HREINS_HREYFING.test(nafn) && /href/i.test(n.getAttribute('attributeName') || ''))) {
        if (n.parentNode) n.parentNode.removeChild(n);
        return;
      }
      [].slice.call(n.attributes).forEach(function (a) {
        var an = a.name.toLowerCase(), gildi = String(a.value || '').replace(/[\s\u0000-\u001f\u007f]+/g, '');
        if (an.indexOf('on') === 0 || /(javascript|vbscript):|data:text\/html/i.test(gildi)) n.removeAttribute(a.name);
      });
    });
    while (doc.body.firstChild) el.appendChild(doc.body.firstChild);
    return el;
  }
  function rastaSvg(svgTexti) {
    var hylki = document.createElement('div');
    hreinsaInn(hylki, svgTexti);
    var svg = hylki.querySelector('svg');
    if (!svg) return Promise.reject(new Error('engin svg'));
    var vb = String(svg.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
    var w = vb.length === 4 && vb[2] > 0 ? vb[2] : parseFloat(svg.getAttribute('width')) || 0;
    var h = vb.length === 4 && vb[3] > 0 ? vb[3] : parseFloat(svg.getAttribute('height')) || 0;
    if (!(w > 0 && h > 0)) return Promise.reject(new Error('stærð vantar'));
    if (vb.length !== 4) svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
    var k = KARFA_PX / Math.max(w, h), cw = Math.max(1, Math.round(w * k)), ch = Math.max(1, Math.round(h * k));
    svg.setAttribute('width', String(cw));
    svg.setAttribute('height', String(ch));
    svg.removeAttribute('style');
    svg.removeAttribute('class');
    /* <use> of a symbol drawn elsewhere on the page: the symbol goes along */
    var defs = null;
    $$('use', svg).forEach(function (u) {
      var ref = u.getAttribute('href') || u.getAttributeNS(XLINK_NS, 'href') || '';
      if (ref.charAt(0) !== '#' || svg.querySelector('[id="' + ref.slice(1).replace(/"/g, '') + '"]')) return;
      var src = document.getElementById(ref.slice(1));
      if (!src) return;
      if (!defs) { defs = document.createElementNS(SVG_NS, 'defs'); svg.insertBefore(defs, svg.firstChild); }
      defs.appendChild(src.cloneNode(true));
    });
    var myndir = $$('image', svg).map(function (im) {
      var ref = im.getAttribute('href') || im.getAttributeNS(XLINK_NS, 'href') || '';
      return inlinaSlod(ref).then(function (d) { im.removeAttributeNS(XLINK_NS, 'href'); im.setAttribute('href', d); });
    });
    return Promise.all(myndir).then(function () { return leturReglur(leturNofn(svg)); }).then(function (reglur) {
      if (reglur.length) {
        var st = document.createElementNS(SVG_NS, 'style');
        st.textContent = reglur.join('\n');
        svg.insertBefore(st, svg.firstChild);
      }
      var texti = new XMLSerializer().serializeToString(svg);
      return new Promise(function (res, rej) {
        var img = new Image();
        img.onload = function () {
          /* Safari applies fonts inside an SVG picture a moment after load */
          setTimeout(function () {
            try {
              var c = document.createElement('canvas');
              c.width = cw; c.height = ch;
              c.getContext('2d').drawImage(img, 0, 0, cw, ch);
              var d = c.toDataURL('image/webp', 0.9);
              if (d.indexOf('data:image/webp') !== 0) d = c.toDataURL('image/png');
              res(d);
            } catch (e) { rej(e); }
          }, 120);
        };
        img.onerror = function () { rej(new Error('svg teiknaðist ekki')); };
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(texti);
      });
    });
  }
  /* ---- B1 for HTML previews (CK 19.10: door signs and house numbers draw with HTML and CSS, not SVG) ----
     The drawing is copied with every computed style written on the element (and ::before/::after as spans),
     pictures and backgrounds inlined, the page's @font-face rules for the fonts used, all inside an SVG
     foreignObject. Chrome and Firefox draw it to the canvas (a small webp as for SVG lines); Safari will not
     let a canvas read a foreignObject picture, so there the SVG picture itself is kept (it shows in an <img>). */
  var ERFD = ['color', 'font-family', 'font-size', 'font-style', 'font-weight', 'font-variant', 'font-stretch', 'font-feature-settings',
    'font-variation-settings', 'font-kerning', 'line-height', 'letter-spacing', 'word-spacing', 'text-align', 'text-transform', 'text-indent',
    'text-shadow', 'white-space', 'direction', 'visibility', '-webkit-text-stroke-width', '-webkit-text-stroke-color', '-webkit-text-fill-color'];
  var GRUNNSTILL = {};
  function grunnStill(tag) {
    if (GRUNNSTILL[tag]) return GRUNNSTILL[tag];
    var box = document.getElementById('vs-grunnstill');
    if (!box) { box = document.createElement('div'); box.id = 'vs-grunnstill'; box.setAttribute('aria-hidden', 'true'); box.style.cssText = 'all:initial;position:fixed;left:-99999px;top:0;width:10px;height:10px;overflow:hidden'; document.body.appendChild(box); }
    var e = document.createElement(tag); box.appendChild(e);
    var cs = window.getComputedStyle(e), m = {};
    for (var i = 0; i < cs.length; i++) m[cs[i]] = cs.getPropertyValue(cs[i]);
    box.removeChild(e);
    GRUNNSTILL[tag] = m;
    return m;
  }
  function stillTexti(cs, grunn) {
    var t = '';
    for (var i = 0; i < cs.length; i++) {
      var p = cs[i];
      if (/^(--|transition|animation|cursor|pointer-events|will-change|caret|outline|user-select|-webkit-user|touch-action|view-transition|-webkit-locale)/.test(p)) continue;
      /* the logical twins (inset-inline, margin-block ...) repeat the physical values: left out so nothing fights them */
      if (/-(block|inline)(-start|-end)?$|^(block|inline)-size$|^inset$/.test(p)) continue;
      var v = cs.getPropertyValue(p);
      if (grunn && grunn[p] === v && ERFD.indexOf(p) === -1) continue;
      t += p + ':' + v + ';';
    }
    return t;
  }
  function domAfrit(el, bid, fam) {
    if (el.nodeType === 3) return document.createTextNode(el.nodeValue);
    if (el.nodeType !== 1) return null;
    var tag = el.tagName.toLowerCase();
    if (/^(script|style|noscript|template|iframe|button-hidden)$/.test(tag)) return null;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none') return null;
    if (el instanceof SVGElement) {
      var s = el.cloneNode(true);
      s.setAttribute('style', 'width:' + cs.width + ';height:' + cs.height + ';display:' + cs.display + ';color:' + cs.color + ';vertical-align:' + cs.verticalAlign + ';' +
        (el.getAttribute('style') || ''));
      $$('image', s).forEach(function (im) {
        var ref = im.getAttribute('href') || im.getAttributeNS(XLINK_NS, 'href') || '';
        bid.push(inlinaSlod(ref).then(function (d) { im.removeAttributeNS(XLINK_NS, 'href'); im.setAttribute('href', d); }));
      });
      return s;
    }
    var nyr = document.createElement(tag === 'img' ? 'img' : (/^(input|textarea|select|button|label|a|picture|source|video|canvas)$/.test(tag) ? 'span' : tag));
    var st = stillTexti(cs, grunnStill(tag));
    /* only the fonts that letters are drawn in (an element with its own text) */
    var eiginTexti = [].some.call(el.childNodes, function (n) { return n.nodeType === 3 && trim(n.nodeValue); });
    if (eiginTexti) String(cs.fontFamily || '').split(',').forEach(function (x) { x = trim(x).replace(/^["']|["']$/g, ''); if (x && !ALMENN_LETUR.test(x)) fam[x] = 1; });
    st.replace(/url\(\s*(["']?)([^"')]+)\1\s*\)/g, function (m, q, u) {
      if (!/^data:/i.test(u)) bid.push(inlinaSlod(u).then(function (d) { nyr.setAttribute('style', nyr.getAttribute('style').split(u).join(d)); }, function () {}));
      return m;
    });
    nyr.setAttribute('style', st);
    if (tag === 'img') {
      var src = el.currentSrc || el.src || '';
      nyr.setAttribute('alt', '');
      if (src) { nyr.setAttribute('src', src); bid.push(inlinaSlod(src).then(function (d) { nyr.setAttribute('src', d); })); }
      return nyr;
    }
    ['::before', '::after'].forEach(function (ps, j) {
      var pc = window.getComputedStyle(el, ps), c = pc.getPropertyValue('content');
      if (!c || c === 'none' || c === 'normal') return;
      var sp = document.createElement('span');
      var pst = stillTexti(pc, grunnStill('span'));
      sp.setAttribute('style', pst);
      pst.replace(/url\(\s*(["']?)([^"')]+)\1\s*\)/g, function (m, q, u) {
        if (!/^data:/i.test(u)) bid.push(inlinaSlod(u).then(function (d) { sp.setAttribute('style', sp.getAttribute('style').split(u).join(d)); }, function () {}));
        return m;
      });
      var tx = /^["']([\s\S]*)["']$/.exec(c);
      if (tx && trim(tx[1])) String(pc.fontFamily || '').split(',').forEach(function (x) { x = trim(x).replace(/^["']|["']$/g, ''); if (x && !ALMENN_LETUR.test(x)) fam[x] = 1; });
      if (tx) sp.textContent = tx[1].replace(/\\([0-9a-f]{1,6})\s?/gi, function (m, h) { return String.fromCharCode(parseInt(h, 16)); });
      if (j === 0) nyr.appendChild(sp); else nyr.__eftir = sp;
    });
    var bornin = el.childNodes;
    for (var i = 0; i < bornin.length; i++) { var b = domAfrit(bornin[i], bid, fam); if (b) nyr.appendChild(b); }
    if (nyr.__eftir) { nyr.appendChild(nyr.__eftir); nyr.__eftir = null; }
    return nyr;
  }
  function domMynd(el) {
    var r = el.getBoundingClientRect(), w = Math.ceil(r.width), h = Math.ceil(r.height);
    if (!(w > 0 && h > 0)) return Promise.reject(new Error('stærð vantar'));
    var bid = [], fam = {};
    var afrit = domAfrit(el, bid, fam);
    if (!afrit) return Promise.reject(new Error('ekkert að teikna'));
    var hylki = document.createElement('div');
    hylki.setAttribute('style', 'width:' + w + 'px;height:' + h + 'px;overflow:hidden;position:relative');
    hylki.appendChild(afrit);
    afrit.setAttribute('style', afrit.getAttribute('style') + 'position:relative;left:0;top:0;right:auto;bottom:auto;margin:0;');
    var k = KARFA_PX / Math.max(w, h), cw = Math.max(1, Math.round(w * k)), ch = Math.max(1, Math.round(h * k));
    return Promise.all(bid.map(function (p) { return p.then(null, function () {}); })).then(function () {
      return leturReglur(Object.keys(fam), true);
    }).then(function (reglur) {
      var texti = '<svg xmlns="' + SVG_NS + '" width="' + cw + '" height="' + ch + '" viewBox="0 0 ' + w + ' ' + h + '">' +
        (reglur.length ? '<style>' + reglur.join('\n').replace(/</g, '\\3c ') + '</style>' : '') +
        '<foreignObject x="0" y="0" width="' + w + '" height="' + h + '">' + new XMLSerializer().serializeToString(hylki) + '</foreignObject></svg>';
      return new Promise(function (res, rej) {
        var img = new Image();
        img.onload = function () {
          setTimeout(function () {
            var d = null;
            try {
              if (window.__smKarfaSvg) throw new Error('prof');
              var c = document.createElement('canvas');
              c.width = cw; c.height = ch;
              var g = c.getContext('2d');
              g.fillStyle = '#fff'; g.fillRect(0, 0, cw, ch);
              g.drawImage(img, 0, 0, cw, ch);
              d = c.toDataURL('image/webp', 0.9);
              if (d.indexOf('data:image/webp') !== 0) d = c.toDataURL('image/png');
            } catch (e) {
              /* Safari: the canvas is tainted by foreignObject; the SVG picture itself, when it is small enough */
              try { d = 'data:image/svg+xml;base64,' + window.btoa(unescape(encodeURIComponent(texti))); } catch (e2) { d = null; }
              if (d && d.length > 1200000 && window.__smKarfaSvg !== 'allt') d = null;
            }
            if (d) res(d); else rej(new Error('mynd of stór'));
          }, 150);
        };
        img.onerror = function () { rej(new Error('html teiknaðist ekki')); };
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(texti);
      });
    });
  }
  /* an adapter's HTML thumbnail drawn off screen inside the page root (its CSS is scoped there), then copied */
  function htmlMynd(html, rot) {
    var box = document.createElement('div');
    box.setAttribute('aria-hidden', 'true');
    box.className = 'vs-karfa-teikning';
    box.style.cssText = 'position:fixed;left:-10000px;top:0;width:160px;height:120px;display:grid;place-items:center;background:#fff;padding:6px;box-sizing:border-box;overflow:hidden;pointer-events:none';
    hreinsaInn(box, html);
    (rot || document.body).appendChild(box);
    var bida = function () {
      var imgs = $$('img', box).filter(function (im) { return !im.complete; });
      return Promise.all(imgs.map(function (im) { return new Promise(function (r) { im.onload = im.onerror = r; setTimeout(r, 2000); }); }))
        .then(function () { return document.fonts && document.fonts.ready ? document.fonts.ready : null; });
    };
    return bida().then(function () { return domMynd(box); }).then(function (u) { box.parentNode.removeChild(box); return u; },
      function (e) { if (box.parentNode) box.parentNode.removeChild(box); throw e; });
  }
  function erSvg(t) { return /^\s*<svg[\s>]/i.test(String(t || '')); }
  function karfaMyndGeyma(lykill, url) {
    if (!lykill || !url || !/^data:image\/(webp|png|svg\+xml);base64,/.test(url)) return;
    try {
      var m = {};
      try { m = JSON.parse(window.localStorage.getItem(KARFA_MYNDIR) || '{}') || {}; } catch (e) { m = {}; }
      m[lykill] = { u: url, t: Date.now() };
      var rod = function () { return Object.keys(m).sort(function (a, b) { return (m[b].t || 0) - (m[a].t || 0); }); };
      rod().slice(KARFA_HAMARK).forEach(function (x) { delete m[x]; });
      for (var i = 0; i < KARFA_HAMARK; i++) {
        try { window.localStorage.setItem(KARFA_MYNDIR, JSON.stringify(m)); return; }
        catch (e) { var r = rod(); if (r.length <= 1) return; delete m[r[r.length - 1]]; }
      }
    } catch (e2) {}
  }
  function skraAdapter(kodi, a) { ADAPTERS[kodi] = a; }
  function kalla(a, nafn, args, sjalfgefid) {
    if (!a || typeof a[nafn] !== 'function') return sjalfgefid;
    try { var r = a[nafn].apply(a, args); return r === undefined ? sjalfgefid : r; }
    catch (e) { if (window.console) console.warn('SMVelSida adapter.' + nafn, e); return sjalfgefid; }
  }
  function scrollTil(y) {
    try { window.scrollTo({ top: Math.max(0, y), behavior: LITIL_HREYFING ? 'auto' : 'smooth' }); }
    catch (e) { window.scrollTo(0, Math.max(0, y)); }
  }

  /* One image into a box: reuses the <img data-vs-mynd>, sets src, srcset and size. */
  function setjaMynd(box, m, textiEfEngin) {
    var el = $('[data-vs-mynd]', box);
    if (!m) {
      if (!el || el.tagName !== 'DIV') {
        box.innerHTML = '';
        el = document.createElement('div');
        el.className = 'vs-spjald-texti';
        el.setAttribute('data-vs-mynd', '');
        box.appendChild(el);
      }
      el.textContent = textiEfEngin || '';
      return;
    }
    if (!el || el.tagName !== 'IMG') {
      box.innerHTML = '';
      el = document.createElement('img');
      el.setAttribute('data-vs-mynd', '');
      el.alt = '';
      el.decoding = 'async';
      box.appendChild(el);
    }
    if (el.getAttribute('data-vs-src') === m.src) return;
    el.setAttribute('data-vs-src', m.src);
    if (m.b && m.h) { el.width = m.b; el.height = m.h; }
    el.sizes = '(min-width: 1001px) 520px, 78vw';
    el.srcset = (m.s6 ? m.s6 + ' 600w, ' : '') + (m.s9 ? m.s9 + ' 900w, ' : '') + m.src + ' 1200w';
    el.src = m.src;
  }

  var FALIN_OVIRK = true;
  /* ---- markup for adapter step bodies (E5), the same look as the kit's own choices ----
     kostir({ nafn, gerd 'radio'|'checkbox', birting 'pillur'|'litir'|'spjold', dalkar, merki (group label),
       kostir: [{ gildi, heiti, undir, flis (CSS background), klasi (chip class), mynd (src), sjonlaust (hidden
       prefix for screen readers), merki (badge), valid, ovirkt, lysing (aria-describedby id) }] }) -> HTML
     setja(el, html): sets the body only when it changed and keeps the focused choice focused */
  function kostirHtml(o) {
    /* owner 30.09: a choice that is not offered now (ovirkt) is left out, never shown disabled; the chosen one
       always stays. FALIN_OVIRK = false brings back the disabled look. A choice may say ekki (not offered: no
       variant) apart from ovirkt, so a sold-out choice (ovirkt, not ekki) stays shown disabled as on the kit's own
       option radios; without ekki, ovirkt decides. */
    var ekkiK = function (k) { return !!k && !k.valid && (k.ekki !== undefined ? !!k.ekki : !!k.ovirkt); };
    var siad = false;
    if (FALIN_OVIRK && o && o.kostir && o.kostir.some(ekkiK)) {
      var o2 = {}, x;
      for (x in o) if (Object.prototype.hasOwnProperty.call(o, x)) o2[x] = o[x];
      o2.kostir = o.kostir.filter(function (k) { return !ekkiK(k); });
      o = o2;
      siad = true;
    }
    /* tile groups carry the same mark, so a tile step left with one choice is hidden too (herbergisnumer Festing) */
    if (o.birting === 'utlit' || o.birting === 'letur') { var fh = flisarHtml(o); return siad ? fh.replace(/^(\s*<div)\b/, '$1 data-vs-siad') : fh; }
    var gerd = o.gerd || 'radio', birt = o.birting || 'pillur';
    return '<div class="vs-val vs-val--' + birt + (o.klasi ? ' ' + o.klasi : '') + '" style="--dalkar:' + (o.dalkar || 3) + '"' + (siad ? ' data-vs-siad' : '') +
      (o.merki ? ' role="group" aria-label="' + esc(o.merki) + '"' : '') + '>' +
      (o.kostir || []).map(function (k) {
        var inn = '<input type="' + gerd + '" name="' + esc(o.nafn) + '" value="' + esc(k.gildi) + '" data-vs-lyk="' + esc(k.gildi) + '"' +
          (k.valid ? ' checked' : '') + (k.ovirkt ? ' disabled' : '') + (k.lysing ? ' aria-describedby="' + esc(k.lysing) + '"' : '') + '>';
        var sj = k.sjonlaust ? '<span class="vs-sjonlaust">' + esc(k.sjonlaust) + '</span>' : '';
        var merki = k.merki ? '<span class="vs-kostur-merki" aria-hidden="true">' + esc(k.merki) + '</span>' : '';
        if (birt === 'litir') {
          return '<label class="vs-kostur">' + inn + merki + '<span class="vs-flis' + (k.klasi ? ' ' + k.klasi : '') + '"' + (k.flis ? ' style="background:' + esc(k.flis) + '"' : '') + '></span>' +
            '<span class="vs-kostur-heiti">' + sj + esc(k.heiti) + '</span><span class="vs-kostur-verd">' + esc(k.undir || '') + '</span></label>';
        }
        if (birt === 'spjold') {
          /* 07.10: myndin fær heiti kostsins sem alt (leitarvélar) en er falin skjálesurum, textinn við hliðina segir það sama */
          var kMynd = String(k.mynd || '').replace(/ alt=""/, function () { return ' alt="' + esc(k.heiti) + '" aria-hidden="true"'; });
          return '<label class="vs-kostur">' + inn + merki + '<span class="vs-kostur-mynd">' + kMynd + '</span>' +
            '<span class="vs-kostur-texti"><span class="vs-kostur-heiti">' + sj + esc(k.heiti) + '</span><span class="vs-kostur-verd">' + esc(k.undir || '') + '</span></span></label>';
        }
        return '<label class="vs-kostur">' + inn + merki + '<span class="vs-kostur-heiti">' + sj + esc(k.heiti) + '</span>' +
          (k.undir2 ? '<small>' + esc(k.undir2) + '</small>' : '') + '<small>' + esc(k.undir || '') + '</small></label>';
      }).join('') + '</div>';
  }
  /* Takka-hamur: on a phone the pill row is ONE row; when it does not fit it scrolls sideways inside itself (never the page) with a soft fade at the
     edge that still has more (data-skrun, data-skrun-endi: CSS in sm-vs-hurd.css). Also used by the door sign's own bar (window.SMTakkarSkrun). */
  function takkarSkrun(bar) {
    if (!bar || bar.__skrun) return;
    function f() {
      var yfir = bar.scrollWidth > bar.clientWidth + 2, endi = bar.scrollLeft + bar.clientWidth >= bar.scrollWidth - 2;
      if (bar.hasAttribute('data-skrun') !== yfir) { if (yfir) bar.setAttribute('data-skrun', ''); else bar.removeAttribute('data-skrun'); }
      if (bar.hasAttribute('data-skrun-endi') !== (yfir && endi)) { if (yfir && endi) bar.setAttribute('data-skrun-endi', ''); else bar.removeAttribute('data-skrun-endi'); }
    }
    bar.__skrun = f;
    bar.addEventListener('scroll', f, { passive: true });
    window.addEventListener('resize', f);
    if (window.ResizeObserver) new ResizeObserver(f).observe(bar);
    f();
  }
  window.SMTakkarSkrun = takkarSkrun;
  /* Takkarnir (13.10, eigandi: profendur sau ekki ad takkarnir breyta skiltinu): rodin fer AF myndinni a ljosa rond beint undir henni
     (systkini .vs-rammi inni i .vs-svid), med fyrirsogn, pillurnar i .vs-takkar-rod (skrunanleg a sima) og einni leidbeiningarlinu
     thar til fyrst er smellt a takka (munad i sessionStorage, pulsa lika adeins tha). Skilar rodinni sem pillurnar fara i;
     bar.__skrun visar a skrun rodarinnar. Utlit i sm-vs-hurd.css. Notad af kit-takkunum her og hurdaskiltavelinni (SMTakkarSmida). */
  function takkarSmida(bar, rammi, texti) {
    var haus = document.createElement('span'), rod = document.createElement('div'), leid = document.createElement('p'), sed = false;
    haus.className = 'vs-takkar-haus';
    haus.textContent = (texti && texti.haus) || 'Prófaðu á skiltinu';
    haus.setAttribute('data-stutt', (texti && texti.stutt) || 'Prófaðu:');
    rod.className = 'vs-takkar-rod';
    leid.className = 'vs-takkar-leid';
    leid.textContent = (texti && texti.leid) || 'Smelltu á takka og sjáðu breytinguna á skiltinu.';
    bar.appendChild(haus); bar.appendChild(rod); bar.appendChild(leid);
    try { sed = !!window.sessionStorage.getItem('sm_takkar_sed'); } catch (e) {}
    if (!sed) bar.setAttribute('data-nytt', '');
    bar.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('.vs-hurd-takki') : null;
      if (t) bar.removeAttribute('data-kynning');
      if (!t || !bar.hasAttribute('data-nytt')) return;
      bar.removeAttribute('data-nytt');
      try { window.sessionStorage.setItem('sm_takkar_sed', '1'); } catch (e2) {}
    });
    /* CS 19.10 (hraði umferð 2): sm-vel.liquid reserves the bar's height on a phone ([data-vs-takkar-stadur] right after the
       picture, sm-sida.css), so the bar takes its place and nothing below moves (CLS 0,04 on door signs) */
    var stadur = rammi && rammi.nextElementSibling;
    if (stadur && stadur.hasAttribute('data-vs-takkar-stadur')) stadur.parentNode.replaceChild(bar, stadur);
    else if (rammi && rammi.parentNode) rammi.parentNode.insertBefore(bar, rammi.nextSibling);
    takkarSkrun(rod);
    bar.__skrun = rod.__skrun;
    /* CY 20.10: valkostabola a tolvu (section stilling takkar_bola -> data-takkar-bola a .vs; eigandi 20.10): SAMA rond, somu takkar og
       atburdir, faerd INN i rammann (absolut haegra megin, utlit i sm-vs-hurd.css) a 1001 px og breidara; a sima situr hun afram undir
       myndinni (fraetekna plassid ad ofan). data-bola-opin: hylkid stendur opid med merkjunum thegar opid hylki (182 px) naer ekki inn a
       skiltid (platan + 12 px), annars sjast taknin ein og merkin koma vid mus eda fokus. data-kynning: opid i 3 s (P2-G1, var 7) fra thvi hylkid
       kemur i sjonmal i fyrstu heimsokn lotunnar (data-nytt), smellur lokar kynningunni. Merkin lika sem title (snertitolvur). */
    var bolaRot = rammi && rammi.closest ? rammi.closest('[data-sm-sida]') : null;
    /* Læsileiki 21.10: símabólan (data-takkar-bola-simi, eigandi 21.10 prufa á hurðaskilti): sama rönd lárétt inni í rammanum neðst undir 1001 px,
       alltaf opin, útlit í sm-vs-hurd.css; sniðmátið sleppir þá frátekna plássinu undir myndinni (CLS 0) */
    var bolaTolva = !!(bolaRot && bolaRot.hasAttribute('data-takkar-bola')), bolaSimi = !!(bolaRot && bolaRot.hasAttribute('data-takkar-bola-simi'));
    if (bolaRot && (bolaTolva || bolaSimi) && window.matchMedia) {
      var bolaMq = window.matchMedia('(min-width:1001px)'), BOLA_OPIN = 192, BOLA_BIL = 12, bolaTimi = null, bolaPlata = null, bolaFaersla = { s: 1, dx: 0 }, bolaFaerslaL = { s: 1, dx: 0 };
      /* glerid og klippingin eru a .vs-bola-inni (display:contents a sima); kassinn sjalfur breytist aldrei (CLS 0) */
      var inni = document.createElement('div');
      inni.className = 'vs-bola-inni';
      while (bar.firstChild) inni.appendChild(bar.firstChild);
      bar.appendChild(inni);
      /* BOLA-ALLT 22.10: a long row (many layouts, categories, colours) scrolls INSIDE the bubble: the row may be as tall as the frame
         leaves between the bubble's top and bottom gaps (sm-vs-bola.css); data-bola-skrun gives the soft edge where there is more */
      var bolaRodMax = function () {
        if (bar.parentNode !== rammi || !bolaMq.matches) return;
        var cs = window.getComputedStyle ? getComputedStyle(bar) : null, bil = cs ? (parseFloat(cs.top) || 0) + (parseFloat(cs.bottom) || 0) : 128;
        /* first the tighter rows (44 px, as on low screens: data-bola-thett), and only if that is not enough the row scrolls */
        if (bar.hasAttribute('data-bola-thett')) bar.removeAttribute('data-bola-thett');
        var maxAf = function () { return Math.floor(rammi.clientHeight - bil - (inni.offsetHeight - rod.clientHeight)); };
        var max = maxAf(), yfir = max > 80 && rod.scrollHeight > max + 2;
        if (yfir) { bar.setAttribute('data-bola-thett', ''); max = maxAf(); yfir = rod.scrollHeight > max + 2; }
        if (yfir) { if (rod.style.getPropertyValue('--bola-rod-max') !== max + 'px') rod.style.setProperty('--bola-rod-max', max + 'px'); }
        if (rod.hasAttribute('data-bola-skrun') !== yfir) { if (yfir) rod.setAttribute('data-bola-skrun', ''); else rod.removeAttribute('data-bola-skrun'); }
        bolaRodEndi();
      };
      var bolaRodEndi = function () {
        var s = rod.hasAttribute('data-bola-skrun'), byrjun = s && rod.scrollTop > 2, endi = s && rod.scrollTop + rod.clientHeight >= rod.scrollHeight - 2;
        if (rod.hasAttribute('data-bola-skrun-byrjun') !== byrjun) { if (byrjun) rod.setAttribute('data-bola-skrun-byrjun', ''); else rod.removeAttribute('data-bola-skrun-byrjun'); }
        if (rod.hasAttribute('data-bola-skrun-endi') !== endi) { if (endi) rod.setAttribute('data-bola-skrun-endi', ''); else rod.removeAttribute('data-bola-skrun-endi'); }
      };
      rod.addEventListener('scroll', bolaRodEndi, { passive: true });
      var bolaMal = function () {
        if (bar.parentNode !== rammi || !bolaMq.matches) return;
        bolaRodMax();
        var h = bar.offsetHeight, topp = Math.max(0, rod.offsetTop - 10), lina = bar.querySelector('.vs-takka-par-lina'), botn = 0;
        if (!h) return;
        /* Læsileiki 21.10: línan undir parinu (sm-vs-hurd.js) er utan lokaðrar bólu; klippt neðan af, 10 px undir síðasta takka */
        if (lina && lina.offsetHeight) { var br = bar.getBoundingClientRect(), lr = lina.getBoundingClientRect(); botn = Math.max(0, Math.round(br.bottom - lr.top - 8)); }
        var t = topp + 'px', bt = botn + 'px', sy = String(Math.round((h - topp - botn) / h * 1000) / 1000);
        if (bar.style.getPropertyValue('--bola-topp-lokad') !== t) bar.style.setProperty('--bola-topp-lokad', t);
        if (bar.style.getPropertyValue('--bola-botn-lokad') !== bt) bar.style.setProperty('--bola-botn-lokad', bt);
        if (bar.style.getPropertyValue('--bola-sy-lokad') !== sy) bar.style.setProperty('--bola-sy-lokad', sy);
      };
      if (window.ResizeObserver) new ResizeObserver(bolaMal).observe(bar);
      var bolaPlass = function () {
        if (!bolaMq.matches || bar.parentNode !== rammi) return;
        var t = rammi.querySelector('[data-vs-teikning]'), p = t && (t.querySelector('.vs-hurd-plata') || t.firstElementChild);
        if (!p) return;
        if (p !== bolaPlata && window.ResizeObserver) { bolaPlata = p; new ResizeObserver(bolaPlassBradum).observe(p); }
        /* FG 21.10 (F4): the plate is measured without the shift below (the drawing is moved only by transform, origin left centre).
           BOLA-ALLT 22.10: on the other engines the first child is often a full-width wrapper or svg; the drawing itself is measured (bolaTeiknRect) */
        var pr = p.classList && p.classList.contains('vs-hurd-plata') ? p.getBoundingClientRect() : bolaTeiknRect(t, p), rr = rammi.getBoundingClientRect(), tr = t.getBoundingClientRect(), kv = bolaKvardi(t), ox = rr.left + rammi.clientLeft + t.offsetLeft;
        if (!pr) return;
        var pl = ox + (pr.left - tr.left) / kv, pw = pr.width / kv, haegri = rr.right - 20 - (bar.offsetWidth || BOLA_OPIN) - BOLA_BIL, vinstri = rr.left + 16;
        var laust = pw > 0 && pl + pw <= haegri;
        if (bar.hasAttribute('data-bola-opin') !== laust) { if (laust) bar.setAttribute('data-bola-opin', ''); else bar.removeAttribute('data-bola-opin'); }
        /* when the open bubble would cover the plate (mouse, keyboard focus, the first-visit intro, touch screens) the drawing slides left,
           and shrinks only when sliding is not enough, so the plate and the names are always whole (sm-vs-hurd.css uses the two values) */
        var reikna = function (h0) {
          var nyS = 1, nyDx = 0;
          if (pw > 0 && pl + pw > h0) {
            if (pw <= h0 - vinstri) nyDx = Math.max(h0 - (pl + pw), vinstri - pl);
            else { nyS = (h0 - vinstri) / pw; nyDx = vinstri - ox - (pl - ox) * nyS; }
          }
          return { s: Math.round(nyS * 1000) / 1000, dx: Math.round(nyDx) };
        };
        var ny = reikna(haegri);
        if (ny.s !== bolaFaersla.s || ny.dx !== bolaFaersla.dx) {
          bolaFaersla = ny;
          rammi.style.setProperty('--bola-faersla', ny.dx + 'px');
          rammi.style.setProperty('--bola-kvardi', String(ny.s));
        }
        /* BOLA-ALLT 22.10: a wide drawing (cross plates, 78 % hotel signs) also clears the CLOSED bubble (66 px): the same slide at rest, smaller */
        var nyL = reikna(rr.right - 20 - 66 - BOLA_BIL);
        if (nyL.s !== bolaFaerslaL.s || nyL.dx !== bolaFaerslaL.dx) {
          bolaFaerslaL = nyL;
          rammi.style.setProperty('--bola-faersla-lokad', nyL.dx + 'px');
          rammi.style.setProperty('--bola-kvardi-lokad', String(nyL.s));
        }
      };
      /* BOLA-ALLT 22.10: the screen box of what is DRAWN in the drawing (union of the visible svg content, images and canvases), so a
         full-width wrapper or a viewBox with empty margins does not count as plate; null when nothing is drawn yet */
      var bolaTeiknRect = function (t, p) {
        var u = null, tr0 = t.getBoundingClientRect();
        var baeta = function (l, tp, r, b) { l = Math.max(l, tr0.left); tp = Math.max(tp, tr0.top); r = Math.min(r, tr0.right); b = Math.min(b, tr0.bottom); if (!(r - l > 1 && b - tp > 1)) return; if (!u) u = { left: l, top: tp, right: r, bottom: b }; else { u.left = Math.min(u.left, l); u.top = Math.min(u.top, tp); u.right = Math.max(u.right, r); u.bottom = Math.max(u.bottom, b); } };
        /* engines that draw the plate as a box of its own (cross plates, house numbers): that box */
        var kassar = t.querySelectorAll('.vs-kross-plata, .hn-plata, .hn-carved');
        [].forEach.call(kassar, function (el) {
          /* the box can change size after the drawing settles (fonts, the number): measure again then */
          if (window.ResizeObserver && !el.__bolaRo) { el.__bolaRo = true; new ResizeObserver(bolaPlassBradum).observe(el); }
          var r = el.getBoundingClientRect(), cs = window.getComputedStyle ? getComputedStyle(el) : null;
          if (!(cs && (cs.visibility === 'hidden' || cs.display === 'none'))) baeta(r.left, r.top, r.right, r.bottom);
        });
        if (u) return { left: u.left, top: u.top, width: u.right - u.left, height: u.bottom - u.top };
        [].forEach.call(t.querySelectorAll('svg, img, canvas'), function (el) {
          if (el.ownerSVGElement || (el.closest && el.closest('[hidden]'))) return;
          var r = el.getBoundingClientRect(), cs = window.getComputedStyle ? getComputedStyle(el) : null;
          if (r.width < 2 || r.height < 2 || (cs && (cs.visibility === 'hidden' || cs.opacity === '0' || cs.display === 'none'))) return;
          if (el.tagName.toLowerCase() === 'svg' && el.getBBox && el.getScreenCTM) {
            try {
              var bb = el.getBBox(), m = el.getScreenCTM(), vb = el.viewBox && el.viewBox.baseVal;
              /* a drawing larger than its viewBox (a cut-out of a big artboard): only the part the viewBox shows */
              if (bb && vb && vb.width > 0 && vb.height > 0) {
                var x0 = Math.max(bb.x, vb.x), y0 = Math.max(bb.y, vb.y), x1 = Math.min(bb.x + bb.width, vb.x + vb.width), y1 = Math.min(bb.y + bb.height, vb.y + vb.height);
                bb = { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
              }
              if (bb && m && bb.width > 0 && bb.height > 0) {
                var xs = [bb.x * m.a + bb.y * m.c + m.e, (bb.x + bb.width) * m.a + bb.y * m.c + m.e, bb.x * m.a + (bb.y + bb.height) * m.c + m.e, (bb.x + bb.width) * m.a + (bb.y + bb.height) * m.c + m.e];
                var ys = [bb.x * m.b + bb.y * m.d + m.f, (bb.x + bb.width) * m.b + bb.y * m.d + m.f, bb.x * m.b + (bb.y + bb.height) * m.d + m.f, (bb.x + bb.width) * m.b + (bb.y + bb.height) * m.d + m.f];
                baeta(Math.max(r.left, Math.min.apply(null, xs)), Math.max(r.top, Math.min.apply(null, ys)), Math.min(r.right, Math.max.apply(null, xs)), Math.min(r.bottom, Math.max.apply(null, ys)));
                return;
              }
            } catch (e) {}
          }
          baeta(r.left, r.top, r.right, r.bottom);
        });
        if (!u && p) { var q = p.getBoundingClientRect(); baeta(q.left, q.top, q.right, q.bottom); }
        return u ? { left: u.left, top: u.top, width: u.right - u.left, height: u.bottom - u.top } : null;
      };
      var bolaKvardi = function (el) {
        var m = window.getComputedStyle ? getComputedStyle(el).transform : '', a = m && m !== 'none' ? parseFloat(m.replace(/^matrix\(/, '').split(',')[0]) : 1;
        return a > 0 ? a : 1;
      };
      var bolaPlassBradum = function () { if (bolaTimi) return; bolaTimi = setTimeout(function () { bolaTimi = null; bolaPlass(); }, 60); };
      /* BOLA-ALLT 22.10: without the phone bubble sm-vel.liquid adds sm-vs-bola.css (link[data-sm-bola]) only on a computer, from a script, so a
         phone never fetches it; until it has loaded the bar waits in the frame, absolute and invisible (no layout shift, no unstyled flash) */
      var bolaBida = function () {
        var bl = document.querySelector('link[data-sm-bola]');
        if (!bl || bl.sheet) { if (bar.style.visibility) { bar.style.visibility = ''; bar.style.position = ''; } return; }
        bar.style.position = 'absolute'; bar.style.visibility = 'hidden';
        if (!bl.__smBid) {
          bl.__smBid = true;
          var lokid = function () { bar.style.visibility = ''; bar.style.position = ''; bolaMal(); bolaPlassBradum(); };
          bl.addEventListener('load', lokid); bl.addEventListener('error', lokid);
        }
      };
      /* Bil og sveima 22.10 (Fable, eigandi): no title tooltips in the computer bubble (the name is always shown as soon as the bubble opens
         under the mouse, the tooltip covered the icon); the strip on a phone keeps them (colour dots). The original title waits in data-bola-title. */
      var bolaTitlar = function (fela) {
        [].forEach.call(bar.querySelectorAll('.vs-hurd-takki'), function (b) {
          if (fela) { if (b.hasAttribute('title')) { b.setAttribute('data-bola-title', b.getAttribute('title')); b.removeAttribute('title'); } }
          else if (b.hasAttribute('data-bola-title')) { b.setAttribute('title', b.getAttribute('data-bola-title')); b.removeAttribute('data-bola-title'); }
        });
      };
      var bolaStada = function () {
        var tolva = bolaMq.matches && bolaTolva, simi = !bolaMq.matches && bolaSimi;
        if (!tolva && bar.style.visibility) { bar.style.visibility = ''; bar.style.position = ''; }
        bolaTitlar(tolva);
        if (tolva) { bolaBida(); if (bar.parentNode !== rammi) rammi.appendChild(bar); bolaMal(); bolaPlassBradum(); }
        else if (simi) { if (bar.parentNode !== rammi) rammi.appendChild(bar); bar.removeAttribute('data-bola-opin'); if (bar.__skrun) bar.__skrun(); }
        else if (bar.parentNode === rammi && rammi.parentNode) { rammi.parentNode.insertBefore(bar, rammi.nextSibling); bar.removeAttribute('data-bola-opin'); }
      };
      bolaStada();
      /* BOLA-ALLT 22.10: the kit's buttons call this after each update (rows shown or hidden change the height and the scroll) */
      bar.__bolaMal = function () { bolaMal(); bolaPlassBradum(); };
      if (bolaMq.addEventListener) bolaMq.addEventListener('change', bolaStada); else if (bolaMq.addListener) bolaMq.addListener(bolaStada);
      var bolaTk = rammi.querySelector('[data-vs-teikning]');
      if (window.ResizeObserver && bolaTk) new ResizeObserver(bolaPlassBradum).observe(bolaTk);
      bolaRot.addEventListener('sm:sida:breyting', bolaPlassBradum);
      window.addEventListener('resize', bolaPlassBradum);
      setTimeout(function () { [].forEach.call(bar.querySelectorAll('.vs-hurd-takki'), function (b) { if (!b.title && !b.hasAttribute('data-bola-title') && b.textContent) b.title = b.textContent; }); bolaTitlar(bolaMq.matches && bolaTolva); bolaMal(); bolaPlassBradum(); }, 0);
      /* P2-G1 21.10 (A02, WCAG 1.4.13): Esc closes the open bubble (mouse over it or keyboard focus in it) without moving either;
         data-bola-lokud keeps it closed until the mouse or the focus leaves it (sm-vs-hurd.css). The intro alone just ends. */
      document.addEventListener('keydown', function (e) {
        if ((e.key !== 'Escape' && e.key !== 'Esc') || !bolaMq.matches || bar.parentNode !== rammi) return;
        bar.removeAttribute('data-kynning');
        var yfir = false;
        try { yfir = bar.matches(':hover'); } catch (e2) {}
        if (yfir || bar.contains(document.activeElement)) bar.setAttribute('data-bola-lokud', '');
      });
      bar.addEventListener('mouseleave', function () { if (!bar.contains(document.activeElement)) bar.removeAttribute('data-bola-lokud'); });
      bar.addEventListener('focusout', function (e) { if (!e.relatedTarget || !bar.contains(e.relatedTarget)) bar.removeAttribute('data-bola-lokud'); });
      {
        /* EIGANDI 23.10 (bóla opin við komu): the bubble is OPEN when the page is reached, on every visit and every product, so the
           customer sees what it is; it closes (icons only, as before) at the first choice made in it (click on a button, above),
           at Esc, and at the first key typed in the form (P2-G1 T04: never open over a sign the customer is writing on; a sign
           that already carries text, typed or brought from the home page, gets no intro). Was: 3 s, first visit of the session only. */
        var skrifad = function () {
          return [].some.call(bolaRot.querySelectorAll('input[type=text], textarea'), function (x) { return !!(x.value && x.value.trim()) && !(x.closest && x.closest('[hidden]')); });
        };
        /* only a real keystroke closes it: the kit and the engines dispatch synthetic input events while restoring the URL state */
        bolaRot.addEventListener('input', function (e) { if (e.isTrusted) bar.removeAttribute('data-kynning'); });
        var kynna = function () { if (bar.__kynnt || skrifad()) return; bar.__kynnt = true; bar.setAttribute('data-kynning', ''); };
        /* at once (next frame) and again after load and after 1,2 s, no longer when the bubble scrolls into view (it stays open until
           the first choice anyway): the engines fill and clear sample text while they start, so one early look is not enough */
        (window.requestAnimationFrame || setTimeout)(kynna);
        if (document.readyState === 'complete') setTimeout(kynna, 60); else window.addEventListener('load', function () { setTimeout(kynna, 60); });
        setTimeout(kynna, 1200);
      }
    }
    return rod;
  }
  window.SMTakkarSmida = takkarSmida;
  function setjaHtml(el, html) {
    if (!el || el.__vsHtml === html) return false;
    var f = document.activeElement, lyk = null;
    var bendill = null;
    if (f && el.contains(f)) {
      lyk = [f.name || '', f.getAttribute('data-vs-lyk') || '', f.getAttribute('data-vs-fokus') || ''];
      /* a field rebuilt under the customer's fingers keeps its caret (focus() alone puts it at the start) */
      try { if (typeof f.selectionStart === 'number') bendill = [f.selectionStart, f.selectionEnd, f.value]; } catch (e) {}
    }
    el.innerHTML = html;
    el.__vsHtml = html;
    kitTeljarar(el);
    if (lyk) {
      var aftur = null;
      $$('input, button, textarea, select', el).forEach(function (x) {
        if (aftur) return;
        if (lyk[2] && x.getAttribute('data-vs-fokus') === lyk[2]) aftur = x;
        else if (!lyk[2] && lyk[1] && (x.name || '') === lyk[0] && x.getAttribute('data-vs-lyk') === lyk[1]) aftur = x;
      });
      if (aftur && !aftur.disabled) {
        try { aftur.focus({ preventScroll: true }); } catch (e) {}
        if (bendill && aftur.setSelectionRange) {
          /* same text: same caret; changed text (normalised): the end, where the customer was typing */
          var n = aftur.value.length, a = aftur.value === bendill[2] ? Math.min(bendill[0], n) : n, b = aftur.value === bendill[2] ? Math.min(bendill[1], n) : n;
          try { aftur.setSelectionRange(a, b); } catch (e) {}
        }
      }
    }
    return true;
  }

  /* ================= samraeming (Kjarni v1): tiles, ids, frames, font keys ================= */
  /* Ids in one drawing made unique per tile: every id="x" becomes prefix + x and every reference to it
     (url(#x), href="#x", xlink:href="#x") follows, so gradients, patterns, clip paths and masks of one tile
     never show on another. The kit runs it on every tile drawing; adapters may use it for their own. */
  function einkvaemId(svg, fk) {
    svg = String(svg == null ? '' : svg);
    fk = String(fk || 'vs').replace(/[^A-Za-z0-9_-]+/g, '-');
    var ids = {}, n = 0;
    svg.replace(/\sid="([^"]+)"/g, function (m, x) { ids[x] = 1; n += 1; return m; });
    if (!n) return svg;
    return svg.replace(/(\sid=")([^"]+)(")/g, function (m, a, x, b) { return a + fk + x + b; })
      .replace(/url\(\s*(['"]?)#([^)'"\s]+)\1\s*\)/g, function (m, q, x) { return ids[x] ? 'url(' + q + '#' + fk + x + q + ')' : m; })
      .replace(/((?:xlink:)?href=")#([^"]+)(")/g, function (m, a, x, b) { return ids[x] ? a + '#' + fk + x + b : m; });
  }
  /* The key of a font in the shop's ONE list of style names (snippets/sm-sida-ord.liquid, letur_heiti):
     lower case without weight, italic, dashes or spaces, so cormorantgaramond-500 and "Cormorant Garamond"
     are the same font. Customers only ever see the style name. */
  function leturLykill(id) { return String(id == null ? '' : id).toLowerCase().replace(/italic/g, '').replace(/[0-9\- _]/g, ''); }
  /* One frame geometry for the preview and the production file (C6; numbers from SMFram 1.2.0):
     rammiRumfraedi(W, H, g), g = adapter.rammi(s) plus { litur (preview colour), breidd (line in mm,
     0.3), smamynd (tiles: 1 CSS px lines) } -> { hlutir (SMFram elements, mm), svg ('<g>' in mm, plate
     origin top left), lysing (['Rammi', text] for the file header) }. g.gerd empty: no frame. */
  function n3(n) { return String(Math.round(n * 1000) / 1000); }
  function rammiRumfraedi(Wd, H, g) {
    g = g || {};
    var F = window.SMFram;
    var lys = F && F.rammiLysing ? F.rammiLysing(g.gerd ? g : null) : ['Rammi', ''];
    if (!g.gerd) return { hlutir: [], svg: '', lysing: lys };
    if (!F || !F.linuRammi || !F.malmRammi) {
      if (window.console) console.warn('SMVelSida.rammi: SMFram 1.2.0 is not on the page, no frame drawn');
      return { hlutir: [], svg: '', lysing: lys };
    }
    var hlutir = g.gerd === 'malmur' ? F.malmRammi(Wd, H, g) : [F.linuRammi(Wd, H, g)];
    var lit = esc(g.litur || 'currentColor'), sm = !!g.smamynd, ve = sm ? ' vector-effect="non-scaling-stroke"' : '';
    var svg = '<g class="vs-ramma-teikning">' + hlutir.map(function (e) {
      if (e.lag === 'LINA') {
        var st = ' fill="none" stroke="' + lit + '" stroke-width="' + (sm ? '1' : n3(g.breidd || 0.3)) + '"' + ve;
        if (e.gerd === 'hringur') return '<circle cx="' + n3(e.cx) + '" cy="' + n3(e.cy) + '" r="' + n3(e.r) + '"' + st + '/>';
        if (e.gerd === 'sporaskja') return '<ellipse cx="' + n3(e.cx) + '" cy="' + n3(e.cy) + '" rx="' + n3(e.rx) + '" ry="' + n3(e.ry) + '"' + st + '/>';
        return '<rect x="' + n3(e.x) + '" y="' + n3(e.y) + '" width="' + n3(e.w) + '" height="' + n3(e.h) + '"' + (e.r > 0 ? ' rx="' + n3(e.r) + '" ry="' + n3(e.r) + '"' : '') + st + '/>';
      }
      /* the metal band, filled; on tiles also a 1 px outline so a 0.5 mm band stays visible */
      return '<rect x="' + n3(e.x) + '" y="' + n3(e.y) + '" width="' + n3(e.w) + '" height="' + n3(e.h) + '" fill="' + lit + '"' + (sm ? ' stroke="' + lit + '" stroke-width="1"' + ve : '') + '/>';
    }).join('') + '</g>';
    return { hlutir: hlutir, svg: svg, lysing: lys };
  }
  /* Tile markup for adapter steps (html.kostir, birting 'utlit' or 'letur'), the same as
     snippets/sm-sida-flis.liquid. A choice may carry hopur (grouped grid: sub-heading + role=group),
     vara + o (a tile of another product of the family: the kit switches product), letur (font id),
     mynd (fallback picture markup), nafn (data-nafn, default heiti). */
  function idHluti(t) { return String(t || '').replace(/[^A-Za-z0-9_-]+/g, '-'); }
  function flisHtml(o, k, hopur) {
    var nafn = k.nafn != null ? k.nafn : k.heiti;
    var inn = '<input type="' + (o.gerd || 'radio') + '" name="' + esc(o.nafn) + '" value="' + esc(k.gildi) + '" data-vs-lyk="' + esc(k.gildi) + '" data-nafn="' + esc(nafn) + '"' +
      (k.letur ? ' data-letur="' + esc(k.letur) + '"' : '') +
      (k.vara ? ' data-vs-vara="' + esc(k.vara) + '" data-vs-o="' + esc((k.o || []).join(',')) + '"' : '') +
      (k.valid ? ' checked' : '') + (k.ovirkt ? ' disabled' : '') + (k.lysing ? ' aria-describedby="' + esc(k.lysing) + '"' : '') + '>';
    var sj = k.sjonlaust != null ? k.sjonlaust : hopur || '';
    var merki = k.merki ? '<span class="vs-kostur-merki" aria-hidden="true">' + esc(k.merki) + '</span>' : '';
    var box = o.birting === 'letur' ?
      '<span class="vs-letur-flotur" data-vs-letur="' + esc(k.letur || k.gildi) + '" aria-hidden="true"><span class="vs-letur-texti">' + esc(o.synishorn || '') + '</span></span>' :
      '<span class="vs-kostur-mynd" data-vs-smamynd aria-hidden="true">' + String(k.mynd || '').replace(/ alt=""/, function () { return ' alt="' + esc(k.heiti) + '"'; }) + '</span>';
    return '<label class="vs-kostur">' + inn + merki + box + '<span class="vs-kostur-texti"><span class="vs-kostur-heiti">' +
      (sj ? '<span class="vs-sjonlaust">' + esc(sj) + ' </span>' : '') + esc(k.heiti) + '</span><span class="vs-kostur-verd" data-vs-undir>' + esc(k.undir || '') + '</span></span></label>';
  }
  function flisarHtml(o) {
    var birt = o.birting, hopar = [], hi = {};
    (o.kostir || []).forEach(function (k) {
      var h = k.hopur || '';
      if (!hi.hasOwnProperty(h)) { hi[h] = hopar.length; hopar.push({ heiti: h, skyring: k.hopur_skyring || '', kostir: [] }); }
      hopar[hi[h]].kostir.push(k);
    });
    var einn = hopar.length < 2 && !(hopar[0] && hopar[0].heiti);
    var html = '<div class="vs-val vs-val--' + birt + (o.klasi ? ' ' + o.klasi : '') + '"' + (o.dalkar ? ' style="--dalkar:' + o.dalkar + '"' : '') +
      (birt === 'letur' ? ' data-vs-leturval="' : ' data-vs-utlit="') + esc(o.lykill || '') + '"' + (o.merki && einn ? ' role="group" aria-label="' + esc(o.merki) + '"' : '') + '>';
    hopar.forEach(function (g, n) {
      var hid = idHluti(o.nafn) + '-h' + n + (g.heiti ? '-' + idHluti(g.heiti) : '');
      if (g.heiti) html += '<p class="vs-utlit-hopur" id="' + hid + '">' + esc(g.heiti) + (g.skyring ? ' <span>' + esc(g.skyring) + '</span>' : '') + '</p>';
      html += '<div class="vs-utlit-rod"' + (g.heiti ? ' role="group" aria-labelledby="' + hid + '"' : '') + '>' +
        g.kostir.map(function (k) { return flisHtml(o, k, g.heiti); }).join('') + '</div>';
    });
    return html + '</div>';
  }

  /* ================= kit units K0 (lota 0, 2026-10-08; yfirferd/KIT-UTTEKT.md 2) =================
     Builders for the controls adapters used to make themselves, so every page gets the same look from
     sm-sida.css. Each returns an HTML string; ctx.html.<name> passes the page dictionary (ord) in.
     Common options: gogn {name: value} -> data-<name>="value" (true: bare attribute, null/false: left out),
     attr {name: value} -> plain attributes on the control (autocomplete, spellcheck, inputmode ...),
     klasi -> extra class on the wrapper. Ids and data-* stay exactly as the adapter passes them, so its
     own listeners keep working. Every control carries data-vs-fokus, so ctx.html.setja keeps focus. */
  function kitGogn(g) {
    var s = '', k, v, n;
    if (!g) return s;
    for (k in g) {
      if (!Object.prototype.hasOwnProperty.call(g, k)) continue;
      v = g[k];
      if (v == null || v === false) continue;
      n = String(k).replace(/^data-/, '');
      if (!/^[A-Za-z0-9_.:-]+$/.test(n)) continue;
      s += ' data-' + n + (v === true ? '' : '="' + esc(v) + '"');
    }
    return s;
  }
  function kitAttr(a) {
    var s = '', k, v;
    if (!a) return s;
    for (k in a) {
      if (!Object.prototype.hasOwnProperty.call(a, k)) continue;
      v = a[k];
      if (v == null || v === false || !/^[A-Za-z][A-Za-z0-9_.:-]*$/.test(k)) continue;
      s += ' ' + k + (v === true ? '' : '="' + esc(v) + '"');
    }
    return s;
  }
  function kitKlasi(k) { return k ? ' ' + esc(k) : ''; }
  /* K-reitur: reitur({ id, heiti, valfrjalst (true: "(valfrjalst)" from the dictionary, or own text), gerd
       'texti'|'svaedi'|'tala', hamark (maxlength + counter), teljari (false: no counter), synishorn, gildi,
       hjalp, villa (default true: an empty error line; text: shown at once with aria-invalid; false: none),
       lysing (more aria-describedby ids), skylda (aria-required), linur (textarea rows, 4), nafn, fokus,
       gogn, attr (on the control), reitGogn (on the .vs-reitur wrapper), falid (wrapper hidden), klasi,
       inntakKlasi, sjonlaust (label for screen readers only) })
     -> .vs-reitur[data-vs-reitur] > .vs-reitur-rod (label.vs-reitur-heiti + .vs-teljari) + input|textarea.vs-inntak
        + p.vs-hjalp#<id>-h + p.vs-villa#<id>-v. The kit keeps the counter (<n>/<hamark>) right itself. */
  function kitReitur(o, ord) {
    o = o || {};
    var id = String(o.id || ('vs-reitur-' + idHluti(o.nafn || o.fokus || 'x')));
    var gerd = o.gerd || 'texti', ham = o.hamark > 0 ? Math.floor(o.hamark) : 0;
    var gildi = o.gildi == null ? '' : String(o.gildi);
    var villaT = typeof o.villa === 'string' ? o.villa : '';
    var lys = [];
    if (o.hjalp) lys.push(id + '-h');
    if (o.villa !== false) lys.push(id + '-v');
    if (ham && o.teljari !== false) lys.push(id + '-t');
    if (o.lysing) lys.push(String(o.lysing));
    var valfr = o.valfrjalst ? (typeof o.valfrjalst === 'string' ? o.valfrjalst : (ord && ord.valfrjalst_merki) || '') : '';
    var a = ' class="vs-inntak' + kitKlasi(o.inntakKlasi) + '" id="' + esc(id) + '"' + (o.nafn ? ' name="' + esc(o.nafn) + '"' : '') +
      (ham ? ' maxlength="' + ham + '"' : '') + (o.synishorn ? ' placeholder="' + esc(o.synishorn) + '"' : '') +
      (o.skylda ? ' aria-required="true"' : '') + (villaT ? ' aria-invalid="true"' : '') +
      (lys.length ? ' aria-describedby="' + esc(lys.join(' ')) + '"' : '') +
      ' data-vs-reitur-inntak data-vs-fokus="' + esc(o.fokus || id) + '"' + kitAttr(o.attr) + kitGogn(o.gogn);
    var stak = gerd === 'svaedi' ?
      '<textarea' + a + (o.attr && o.attr.rows ? '' : ' rows="' + (o.linur > 0 ? Math.floor(o.linur) : 4) + '"') + '>' + esc(gildi) + '</textarea>' :
      '<input type="text"' + (gerd === 'tala' && !(o.attr && o.attr.inputmode) ? ' inputmode="numeric"' : '') + a + ' value="' + esc(gildi) + '">';
    var merki = o.heiti ? '<label class="vs-reitur-heiti' + (o.sjonlaust ? ' vs-sjonlaust' : '') + '" for="' + esc(id) + '">' + esc(o.heiti) +
      (valfr ? ' <span class="vs-reitur-valfrjalst">' + esc(valfr) + '</span>' : '') + '</label>' : '';
    var telja = ham && o.teljari !== false ? '<span class="vs-teljari" id="' + esc(id) + '-t" data-vs-teljari aria-live="off">' + Math.min(gildi.length, ham) + '/' + ham + '</span>' : '';
    return '<div class="vs-reitur' + kitKlasi(o.klasi) + '" data-vs-reitur' + (o.falid ? ' hidden' : '') + kitGogn(o.reitGogn) + '>' +
      (merki || telja ? '<div class="vs-reitur-rod">' + merki + telja + '</div>' : '') + stak +
      (o.hjalp ? '<p class="vs-hjalp" id="' + esc(id) + '-h">' + esc(o.hjalp) + '</p>' : '') +
      (o.villa !== false ? '<p class="vs-villa" id="' + esc(id) + '-v" data-vs-reitur-villa aria-live="polite">' + esc(villaT) + '</p>' : '') + '</div>';
  }
  /* reitir(listi, { dalkar, klasi, gogn }): fields (objects for reitur, or ready HTML strings) in one .vs-reitir grid */
  function kitReitir(listi, o, ord) {
    o = o || {};
    return '<div class="vs-reitir' + kitKlasi(o.klasi) + '"' + (o.dalkar > 1 ? ' style="--dalkar:' + Math.floor(o.dalkar) + '"' : '') + kitGogn(o.gogn) + '>' +
      (listi || []).map(function (r) { return typeof r === 'string' ? r : kitReitur(r, ord); }).join('') + '</div>';
  }
  /* the counter of every kit field under rot (run by ctx.html.setja and on every update of the page) */
  function kitTeljari(inn) {
    var r = inn && inn.closest ? inn.closest('[data-vs-reitur]') : null, t = r ? $('[data-vs-teljari]', r) : null;
    if (!t) return;
    var ham = +(inn.getAttribute('data-sm-hamark0') || (inn.maxLength > 0 ? inn.maxLength : 0)), len = stafafjoldi(inn.value), txt = len + '/' + ham;
    if (t.textContent !== txt) t.textContent = txt;
    if (ham > 0 && len >= ham) t.setAttribute('data-fullt', ''); else t.removeAttribute('data-fullt');
  }
  function kitTeljarar(rot) { if (rot) $$('[data-vs-reitur-inntak]', rot).forEach(kitTeljari); }
  /* villa(inntak | id, texti): the error line of a kit field (#<id>-v) and aria-invalid; '' clears both */
  function kitVilla(inn, texti) {
    if (typeof inn === 'string') inn = document.getElementById(inn);
    if (!inn) return;
    texti = texti == null ? '' : String(texti);
    if (texti) inn.setAttribute('aria-invalid', 'true'); else inn.removeAttribute('aria-invalid');
    var v = inn.id ? document.getElementById(inn.id + '-v') : null;
    if (v && v.textContent !== texti) v.textContent = texti;
  }
  /* K-rofi: rofi({ id, nafn, gildi (value; left out when not given), heiti, html (trusted label markup instead
       of heiti), undir (second line), valid, ovirkt, lysing (aria-describedby), fokus, gogn, attr, ytraGogn (on
       the label), falid, klasi ('vs-rofi--kassi': boxed, as in a confirmation) })
     -> label.vs-rofi > input[type=checkbox] + .vs-rofi-texti; a 48 px row with a 20 px native box. */
  function kitRofi(o) {
    o = o || {};
    var fok = o.fokus || o.id || o.nafn || '';
    var g = o.gildi != null ? String(o.gildi) : null;
    return '<label class="vs-rofi' + kitKlasi(o.klasi) + '"' + (o.falid ? ' hidden' : '') + kitGogn(o.ytraGogn) + '><input type="checkbox"' +
      (o.id ? ' id="' + esc(o.id) + '"' : '') + (o.nafn ? ' name="' + esc(o.nafn) + '"' : '') +
      (g != null ? ' value="' + esc(g) + '" data-vs-lyk="' + esc(g) + '"' : '') + (fok ? ' data-vs-fokus="' + esc(fok) + '"' : '') +
      (o.valid ? ' checked' : '') + (o.ovirkt ? ' disabled' : '') + (o.lysing ? ' aria-describedby="' + esc(o.lysing) + '"' : '') +
      kitAttr(o.attr) + kitGogn(o.gogn) + '><span class="vs-rofi-texti"><span class="vs-rofi-heiti">' + (o.html != null ? String(o.html) : esc(o.heiti)) + '</span>' +
      (o.undir ? '<small class="vs-rofi-undir">' + esc(o.undir) + '</small>' : '') + '</span></label>';
  }
  /* K-tillogur: tillogur({ texti (lead, e.g. "Reyndu:"), merki (group name for screen readers), kostir: [{ gildi,
       heiti, gogn }], gogn, klasi }) -> .vs-tillogur > button.vs-tillaga[data-vs-tillaga]. No choices: ''.
       The adapter keeps listening to its buttons itself. */
  function kitTillogur(o) {
    o = o || {};
    var k = o.kostir || [];
    if (!k.length) return '';
    var m = o.merki || o.texti || '';
    return '<div class="vs-tillogur' + kitKlasi(o.klasi) + '"' + (m ? ' role="group" aria-label="' + esc(m) + '"' : '') + kitGogn(o.gogn) + '>' +
      (o.texti ? '<span class="vs-tillogur-texti" aria-hidden="true">' + esc(o.texti) + '</span>' : '') +
      k.map(function (x, i) {
        var gl = x.gildi != null ? String(x.gildi) : String(i);
        return '<button type="button" class="vs-tillaga" data-vs-tillaga="' + esc(gl) + '" data-vs-fokus="vs-tillaga-' + esc(idHluti(gl)) + '"' + kitGogn(x.gogn) + '>' + esc(x.heiti != null ? x.heiti : gl) + '</button>';
      }).join('') + '</div>';
  }
  /* K-bod: bod(texti, stig, { id, gogn, klasi, falid, html (trusted markup instead of texti, e.g. a mail link),
       live ('polite' default, 'off') }) with stig 'hjalp' | 'vidvorun' | 'villa'
     -> p.vs-bod.vs-bod--<stig>[data-vs-bod] aria-live; empty means not shown. bodSetja(el, texti, stig, html)
       changes it in place (only when it changed, so the live region does not repeat itself). */
  var BOD_STIG = { hjalp: 1, vidvorun: 1, villa: 1 };
  function kitBod(texti, stig, o) {
    o = o || {};
    stig = BOD_STIG[stig] ? stig : 'hjalp';
    return '<p class="vs-bod vs-bod--' + stig + kitKlasi(o.klasi) + '"' + (o.id ? ' id="' + esc(o.id) + '"' : '') +
      ' aria-live="' + (o.live === 'off' ? 'off' : 'polite') + '" data-vs-bod' + (o.falid ? ' hidden' : '') + kitGogn(o.gogn) + '>' +
      (o.html != null ? String(o.html) : esc(texti)) + '</p>';
  }
  function kitBodSetja(el, texti, stig, html) {
    if (!el) return;
    if (BOD_STIG[stig] && el.classList) ['hjalp', 'vidvorun', 'villa'].forEach(function (x) { el.classList.toggle('vs-bod--' + x, x === stig); });
    if (html != null) { if (el.innerHTML !== String(html)) el.innerHTML = String(html); }
    else { var t = texti == null ? '' : String(texti); if (el.textContent !== t) el.textContent = t; }
    el.hidden = !trim(el.textContent);
  }
  /* K-listi: listi({ nafn, heiti (shown above), merki (each line's hidden label, default heiti), linur: [text |
       { gildi, id, gogn, fjarlaegjaGogn }], hamark (maxlength per line), hamarkLinur, lagmark (lines that cannot be
       removed, 1), synishorn, id, gogn, baetaGogn, baeta / fjarlaegja (own words), klasi })
     -> .vs-listi[data-vs-listi] > .vs-listi-rod (input.vs-inntak[data-vs-listi-lina] + button.vs-listi-fjarlaegja)
        + button.vs-listi-baeta[data-vs-listi-baeta]. The adapter keeps the lines and listens to the buttons. */
  function kitListi(o, ord) {
    o = o || {};
    ord = ord || {};
    var nafn = String(o.nafn || 'listi'), grunn = String(o.id || ('vs-listi-' + idHluti(nafn)));
    var linur = (o.linur && o.linur.length ? o.linur : ['']).map(function (x) { return typeof x === 'object' && x ? x : { gildi: x }; });
    var lag = o.lagmark >= 0 ? Math.floor(o.lagmark) : 1, ham = o.hamark > 0 ? Math.floor(o.hamark) : 0;
    var merki = o.merki || o.heiti || '';
    var hid = grunn + '-heiti';
    var h = '<div class="vs-listi' + kitKlasi(o.klasi) + '" data-vs-listi="' + esc(nafn) + '"' + (o.heiti ? ' role="group" aria-labelledby="' + esc(hid) + '"' : '') + kitGogn(o.gogn) + '>' +
      (o.heiti ? '<p class="vs-reitur-heiti" id="' + esc(hid) + '">' + esc(o.heiti) + '</p>' : '');
    linur.forEach(function (l, i) {
      var id = String(l.id || (grunn + '-' + i)), n = i + 1;
      h += '<div class="vs-listi-rod"><label class="vs-sjonlaust" for="' + esc(id) + '">' + esc(merki + (merki ? ' ' : '') + n) + '</label>' +
        '<input class="vs-inntak" type="text" id="' + esc(id) + '" value="' + esc(l.gildi == null ? '' : l.gildi) + '"' + (ham ? ' maxlength="' + ham + '"' : '') +
        (o.synishorn ? ' placeholder="' + esc(o.synishorn) + '"' : '') + ' autocomplete="off" data-vs-listi-lina="' + i + '" data-vs-fokus="' + esc(id) + '"' + kitAttr(o.attr) + kitGogn(l.gogn) + '>' +
        (linur.length > lag ? '<button type="button" class="vs-listi-fjarlaegja" data-vs-listi-fjarlaegja="' + i + '" data-vs-fokus="' + esc(id) + '-x" aria-label="' +
          esc(o.fjarlaegja || fylla(ord.fjarlaegja_linu || '', { n: n })) + '"' + kitGogn(l.fjarlaegjaGogn) + '><span aria-hidden="true">\u00d7</span></button>' : '') + '</div>';
    });
    if (!(o.hamarkLinur > 0) || linur.length < o.hamarkLinur) {
      h += '<button type="button" class="vs-listi-baeta" data-vs-listi-baeta data-vs-fokus="' + esc(grunn) + '-baeta"' + kitGogn(o.baetaGogn) + '>' +
        '<span aria-hidden="true">+</span> ' + esc(o.baeta || ord.baeta_linu || '') + '</button>';
    }
    return h + '</div>';
  }
  /* K-velja: velja({ id, heiti, kostir: [{ gildi, heiti, ovirkt } | text], gildi, autt (first empty choice; true:
       "Veldu" from the dictionary), hjalp, villa, lysing, skylda, nafn, fokus, gogn, attr, reitGogn, falid, klasi,
       sjonlaust }) -> .vs-reitur with a styled select.vs-inntak.vs-velja (label, help and error as reitur).
     dags({ id, heiti, ar: [fra, til] (fra > til counts down), gildi: { d, m, a }, manudir (names; default the
       dictionary), merki: { d, m, a } (default Dagur / Manudur / Ar), gogn (on all three), hlutiGogn (data name
       that gets d / m / a, e.g. 'kr-hluti'), klasi }) -> fieldset.vs-dags > legend + .vs-dags-rod with three selects
       [data-vs-dags-hluti=d|m|a]; on narrow phones the year moves to its own line instead of being cut. */
  function kitValStak(o, ord, aukaAttr) {
    var gl = o.gildi == null ? '' : String(o.gildi);
    var h = '<select class="vs-inntak vs-velja' + kitKlasi(o.inntakKlasi) + '"' + (o.id ? ' id="' + esc(o.id) + '"' : '') + (o.nafn ? ' name="' + esc(o.nafn) + '"' : '') +
      ' data-vs-fokus="' + esc(o.fokus || o.id || o.nafn || '') + '"' + (aukaAttr || '') + kitAttr(o.attr) + kitGogn(o.gogn) + '>';
    if (o.autt) h += '<option value=""' + (gl === '' ? ' selected' : '') + '>' + esc(o.autt === true ? (ord && ord.velja_autt) || '' : o.autt) + '</option>';
    (o.kostir || []).forEach(function (k) {
      if (typeof k !== 'object' || !k) k = { gildi: k, heiti: k };
      var v = String(k.gildi == null ? '' : k.gildi);
      h += '<option value="' + esc(v) + '"' + (v === gl ? ' selected' : '') + (k.ovirkt ? ' disabled' : '') + '>' + esc(k.heiti != null ? k.heiti : v) + '</option>';
    });
    return h + '</select>';
  }
  function kitVelja(o, ord) {
    o = o || {};
    var id = String(o.id || ('vs-velja-' + idHluti(o.nafn || o.fokus || 'x')));
    var villaT = typeof o.villa === 'string' ? o.villa : '';
    var lys = [];
    if (o.hjalp) lys.push(id + '-h');
    if (o.villa !== false) lys.push(id + '-v');
    if (o.lysing) lys.push(String(o.lysing));
    var o2 = {}, x;
    for (x in o) if (Object.prototype.hasOwnProperty.call(o, x)) o2[x] = o[x];
    o2.id = id;
    var auka = (o.skylda ? ' aria-required="true"' : '') + (villaT ? ' aria-invalid="true"' : '') + (lys.length ? ' aria-describedby="' + esc(lys.join(' ')) + '"' : '');
    return '<div class="vs-reitur' + kitKlasi(o.klasi) + '" data-vs-reitur' + (o.falid ? ' hidden' : '') + kitGogn(o.reitGogn) + '>' +
      (o.heiti ? '<label class="vs-reitur-heiti' + (o.sjonlaust ? ' vs-sjonlaust' : '') + '" for="' + esc(id) + '">' + esc(o.heiti) + '</label>' : '') +
      kitValStak(o2, ord, auka) +
      (o.hjalp ? '<p class="vs-hjalp" id="' + esc(id) + '-h">' + esc(o.hjalp) + '</p>' : '') +
      (o.villa !== false ? '<p class="vs-villa" id="' + esc(id) + '-v" data-vs-reitur-villa aria-live="polite">' + esc(villaT) + '</p>' : '') + '</div>';
  }
  function kitDags(o, ord) {
    o = o || {};
    ord = ord || {};
    var id = String(o.id || 'vs-dags'), heiti = o.heiti || '', gl = o.gildi || {};
    var m = o.merki || {}, md = { d: m.d || ord.dags_dagur || '', m: m.m || ord.dags_manudur || '', a: m.a || ord.dags_ar || '' };
    var nofn = o.manudir || String(ord.manudir || '').split('|');
    var ar = o.ar || [new Date().getFullYear(), 1900], fra = Math.floor(ar[0]), til = Math.floor(ar[1]), skref = fra > til ? -1 : 1;
    var kd = [], km = [], ka = [], i;
    for (i = 1; i <= 31; i++) kd.push({ gildi: String(i), heiti: i + '.' });
    for (i = 1; i <= 12; i++) km.push({ gildi: String(i), heiti: nofn[i - 1] || String(i) });
    for (i = fra; skref < 0 ? i >= til : i <= til; i += skref) ka.push({ gildi: String(i), heiti: String(i) });
    var stak = function (h, kostir) {
      var g = {}, k;
      if (o.gogn) for (k in o.gogn) if (Object.prototype.hasOwnProperty.call(o.gogn, k)) g[k] = o.gogn[k];
      if (o.hlutiGogn) g[o.hlutiGogn] = h;
      g['vs-dags-hluti'] = h;
      return kitValStak({ id: id + '-' + h, fokus: id + '-' + h, kostir: kostir, gildi: gl[h], autt: md[h], gogn: g, attr: o.attr }, ord,
        ' aria-label="' + esc((heiti ? heiti + ', ' : '') + md[h]) + '"');
    };
    return '<fieldset class="vs-dags' + kitKlasi(o.klasi) + '"' + (o.falid ? ' hidden' : '') + kitGogn(o.reitGogn) + '>' + (heiti ? '<legend class="vs-reitur-heiti">' + esc(heiti) + '</legend>' : '') +
      '<div class="vs-dags-rod">' + stak('d', kd) + stak('m', km) + stak('a', ka) + '</div></fieldset>';
  }
  /* K-stadfesting: stadfesting({ inngangur, yfirlit: [[heiti, gildi | null] | { heiti, gildi, klasi }], vantar (word
       for a null value; default "Vantar"), hak: rofi options (shown boxed), gogn, yfirlitGogn, klasi })
     -> .vs-stadfesting > p.vs-hjalp + dl.vs-yfirlit (null -> span.vs-yfirlit-vantar) + K-rofi. */
  function kitStadfesting(o, ord) {
    o = o || {};
    ord = ord || {};
    var vant = o.vantar || ord.vantar_gildi || '';
    var h = '<div class="vs-stadfesting' + kitKlasi(o.klasi) + '"' + kitGogn(o.gogn) + '>' +
      (o.inngangur ? '<p class="vs-hjalp vs-stadfesting-inn">' + esc(o.inngangur) + '</p>' : '') +
      '<dl class="vs-yfirlit"' + kitGogn(o.yfirlitGogn) + '>';
    (o.yfirlit || []).forEach(function (r) {
      var x = r && r.length !== undefined && typeof r !== 'string' ? { heiti: r[0], gildi: r[1] } : (r || {});
      h += '<dt' + (x.klasi ? ' class="' + esc(x.klasi) + '"' : '') + '>' + esc(x.heiti) + '</dt><dd' + (x.klasi ? ' class="' + esc(x.klasi) + '"' : '') + '>' +
        (x.gildi == null || x.gildi === '' ? '<span class="vs-yfirlit-vantar">' + esc(vant) + '</span>' : esc(x.gildi)) + '</dd>';
    });
    h += '</dl>';
    if (o.hak) {
      var hk = {}, k;
      for (k in o.hak) if (Object.prototype.hasOwnProperty.call(o.hak, k)) hk[k] = o.hak[k];
      hk.klasi = 'vs-rofi--kassi' + (hk.klasi ? ' ' + hk.klasi : '');
      h += kitRofi(hk);
    }
    return h + '</div>';
  }
  /* the builders bound to one page's dictionary (ctx.html and SMVelSida.kit(ord) for tests) */
  function kitSmidir(ord) {
    ord = ord || {};
    return {
      reitur: function (o) { return kitReitur(o, ord); },
      reitir: function (l, o) { return kitReitir(l, o, ord); },
      villa: kitVilla,
      teljarar: kitTeljarar,
      rofi: kitRofi,
      tillogur: kitTillogur,
      bod: kitBod,
      bodSetja: kitBodSetja,
      listi: function (o) { return kitListi(o, ord); },
      velja: function (o) { return kitVelja(o, ord); },
      dags: function (o) { return kitDags(o, ord); },
      stadfesting: function (o) { return kitStadfesting(o, ord); }
    };
  }

  /* ---------------- built-in adapter: product photos ---------------- */
  skraAdapter('mynd', {
    gerd: 'ljosmynd',
    skuggi: true,
    lifandi: false,
    mynd: function (ctx) {
      var a = ctx.afbrigdi;
      return (a && a.mynd) || ctx.gogn.myndir[0] || null;
    },
    teikna: function (ctx) { setjaMynd(ctx.teikning, this.mynd(ctx), ctx.gogn.vara.titill); },
    lysing: function (s, ctx) { var m = this.mynd(ctx); return (m && m.alt) || ctx.gogn.vara.titill; },
    smamynd: function (s, ctx) { var m = this.mynd(ctx); return m ? { src: m.s6 || m.src } : null; }
  });

  /* ---- product family (E1): the data of one product (snippets/sm-sida-fjolskylda.liquid) in the
     shape of the page data, so everything that reads gogn.valkostir, gogn.afbrigdi and gogn.myndir
     works on the product shown ---- */
  function breiddir(u) {
    var t = String(u || '').indexOf('?') >= 0 ? '&' : '?';
    return { s2: u + t + 'width=200', s6: u + t + 'width=600', s9: u + t + 'width=900', src: u + t + 'width=1200' };
  }
  function lesaVoru(p, handle) {
    var myndir = (p.m || []).map(function (m) {
      var b = breiddir(m.u);
      return { id: m.id, s2: b.s2, s6: b.s6, s9: b.s9, src: b.src, b: m.b, h: m.h, alt: m.alt || p.t, f: String(m.u || '').split('?')[0].split('/').pop().toLowerCase() };
    });
    return {
      id: p.id, handle: handle || p.h, titill: p.t, url: p.u, lede: p.l || '', staerd: p.z || '', tags: p.g || [], sersnid: !!p.s,
      valkostir: (p.o || []).map(function (o) {
        return { nafn: o.n, gildi: (o.v || []).map(function (g) { return { kodi: String(g[0]), nafn: g[1] }; }) };
      }),
      afbrigdi: (p.a || []).map(function (a) {
        return { id: a.id, verd: a.p, til: !!a.t, heiti: a.h, sku: a.s || '', vg: null, o: (a.o || []).map(String), n: a.n || [],
          mynd: a.m != null && a.m >= 0 && myndir[a.m] ? myndir[a.m] : null };
      }),
      myndir: myndir
    };
  }
  function slodParam(k) {
    try { return new URL(window.location.href).searchParams.get(k); } catch (e) { return null; }
  }

  /* ---- lysandi slod (K3, 2026-10-01; review 2026-10-01): readable query params from the product's own
     option names and values, instead of ?variant=<id> (lysandi-slodir-PLAN.md). Icelandic letters mapped
     a/e/i/o/u/y/th/d/ae/o. Parenthetical notes (sizes, upcharges, "valfrjalst") never reach the slug.
     Never customer-typed text: only Shopify option names and values reach the URL. ---- */
  var IS_STAFIR = { '\u00e1': 'a', '\u00e9': 'e', '\u00ed': 'i', '\u00f3': 'o', '\u00fa': 'u', '\u00fd': 'y',
    '\u00fe': 'th', '\u00f0': 'd', '\u00e6': 'ae', '\u00f6': 'o' };
  var IS_STAFIR_RE = /[\u00e1\u00e9\u00ed\u00f3\u00fa\u00fd\u00fe\u00f0\u00e6\u00f6]/g;
  function handleizeIS(t) {
    t = String(t == null ? '' : t).replace(/\([^)]*\)/g, ' ').toLowerCase().replace(IS_STAFIR_RE, function (c) { return IS_STAFIR[c]; });
    return t.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  /* short, stable param keys for the option names seen across the shop's product lines (review
     2026-10-01: long titles like "Vilt baeta heimilisfangi vid? (valfrjalst)" made unreadable URLs).
     Matched as a substring of the lower-cased title, first match wins; an option whose title matches
     none of these keeps the full handleized title (still short on most products). */
  var STUTT_LYKLAR = [
    [/heimilisfang/, 'heimilisfang'], [/fj\u00f6ldi|fjoldi/, 'fjoldi'], [/st\u00e6r\u00f0|staerd/, 'staerd'],
    [/rammi/, 'rammi'], [/letur/, 'letur'], [/festing/, 'festing'], [/l\u00f6gun|logun/, 'logun'],
    [/litur/, 'litur'], [/efni/, 'efni'], [/kross/, 'kross'], [/mynd/, 'mynd']
  ];
  function lysandiLykill(nafn) {
    var lc = String(nafn || '').toLowerCase();
    for (var i = 0; i < STUTT_LYKLAR.length; i++) if (STUTT_LYKLAR[i][0].test(lc)) return STUTT_LYKLAR[i][1];
    return handleizeIS(nafn);
  }
  /* a plain yes/no add-on (gildi exactly { nei, ja }, any order): its "nei" (off) state is the default
     and never needs to show in the URL - only a customer's "ja" does */
  function erJaNei(o) {
    var g = (o && o.gildi) || [];
    if (g.length !== 2) return false;
    var a = handleizeIS(g[0].nafn), b = handleizeIS(g[1].nafn);
    return (a === 'nei' && b === 'ja') || (a === 'ja' && b === 'nei');
  }
  /* the readable { key: value } of product v (valkostir: [{nafn, gildi:[{kodi,nafn}]}]) with variant a;
     a yes/no add-on left at "nei" (its default) is left out of the URL entirely */
  function lysandiBreytur(v, a) {
    var ut = {};
    ((v && v.valkostir) || []).forEach(function (o, j) {
      if (!o || o.nafn === 'Title') return;
      var kodi = a && a.o ? a.o[j] : null;
      if (kodi == null) return;
      var g = null;
      (o.gildi || []).forEach(function (x) { if (!g && String(x.kodi) === String(kodi)) g = x; });
      if (!g) return;
      var gildiLykill = handleizeIS(g.nafn);
      if (erJaNei(o) && gildiLykill === 'nei') return;
      var lyk = lysandiLykill(o.nafn);
      if (lyk) ut[lyk] = gildiLykill;
    });
    return ut;
  }
  /* the variant of v whose readable params all match the URL; null when there are none or no full
     match (an unknown or partial combination falls back to the default variant, silently). A yes/no
     add-on with no param in the URL is read as its default "nei" (symmetric with lysandiBreytur, so a
     link that omitted it because it was off restores it as off, not as whichever variant sorts first) */
  /* CK 19.10 (CL): links written on /en (the JSON-LD variant URLs, Google) carry the shop-language option names
     and values (?color=gull, ?size=..., ?material=..., or a whole handleized option title). Before anything reads
     the address, each such key becomes the kit's own key with the Icelandic value (kanon), so the page opens on
     the same variant in both languages; history.replaceState, nothing reloads. A value that matches none of the
     option's values is left as it is. */
  var EN_LYKLAR = { litur: ['color', 'colour'], staerd: ['size', 'height'], efni: ['material'], letur: ['font'], rammi: ['border', 'frame'],
    logun: ['shape'], festing: ['mounting', 'fixing'], fjoldi: ['quantity'], heimilisfang: ['address'] };
  function slodGildi(t) {
    return String(t == null ? '' : t).toLowerCase().replace(IS_STAFIR_RE, function (c) { return IS_STAFIR[c]; }).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  function slodSamnefni(valkostir, handle) {
    var u;
    try { u = new URL(window.location.href); } catch (e) { return; }
    if (!u.search) return;
    var breytt = false;
    (valkostir || []).forEach(function (o) {
      if (!o || o.nafn === 'Title') return;
      var lyk = lysandiLykill(o.nafn);
      if (!lyk) return;
      var fundid = null;
      if (u.searchParams.has(lyk)) {
        /* B1 23.10: the kit's own key with a value in the shop language (the /en JSON-LD variant URLs, ?litur=gold&staerd=10-12-cm)
           gets the Icelandic value too; a value that already is one of the option's own values is left alone */
        var eigid = String(u.searchParams.get(lyk) || '');
        if (!eigid || (o.gildi || []).some(function (x) { return handleizeIS(x.nafn) === eigid; })) return;
        fundid = lyk;
      } else {
        var en = syn(o.nafn, handle, 'o'), nofn = [handleizeIS(o.nafn), slodGildi(o.nafn), handleizeIS(en), slodGildi(en)].concat(EN_LYKLAR[lyk] || []);
        nofn.forEach(function (n) { if (!fundid && n && n !== lyk && u.searchParams.has(n)) fundid = n; });
      }
      if (!fundid) return;
      var g = String(u.searchParams.get(fundid) || ''), gs = slodGildi(g), val = null;
      (o.gildi || []).forEach(function (x) {
        if (val) return;
        var e2 = syn(x.nafn, handle, 'v');
        if ([handleizeIS(x.nafn), slodGildi(x.nafn), handleizeIS(e2), slodGildi(e2)].indexOf(gs) !== -1 || handleizeIS(kanon(g, handle, 'v')) === handleizeIS(x.nafn)) val = x;
      });
      if (!val) return;
      u.searchParams.delete(fundid);
      u.searchParams.set(lyk, handleizeIS(val.nafn));
      breytt = true;
    });
    if (breytt && window.history && history.replaceState) {
      try { history.replaceState(history.state, '', u.toString().replace(/%2C/g, ',')); } catch (e) {}
    }
  }
  function afbUrLysandiSlod(v, afbrigdi, skyrUt) {
    var leitad = [], hefur = false;
    ((v && v.valkostir) || []).forEach(function (o, j) {
      if (skyrUt) skyrUt[j] = false;
      if (!o || o.nafn === 'Title') { leitad[j] = null; return; }
      var lyk = lysandiLykill(o.nafn), g = lyk ? slodParam(lyk) : null;
      if (g) { leitad[j] = g; hefur = true; if (skyrUt) skyrUt[j] = true; }
      else if (erJaNei(o)) leitad[j] = 'nei';
      else leitad[j] = null;
    });
    if (!hefur) return null;
    var best = null, bestStig = -1;
    (afbrigdi || []).forEach(function (a) {
      var stig = 0, ok = true;
      for (var j = 0; j < leitad.length; j++) {
        if (leitad[j] == null) continue;
        var g = null;
        (v.valkostir[j].gildi || []).forEach(function (x) { if (!g && String(x.kodi) === String(a.o[j])) g = x; });
        if (!g || handleizeIS(g.nafn) !== leitad[j]) { ok = false; break; }
        stig++;
      }
      if (ok && stig > bestStig) { best = a; bestStig = stig; }
    });
    return best;
  }

  /* ---------------- one page ---------------- */
  function Sida(rot) {
    var gogn;
    try { gogn = JSON.parse($('script[data-sm-sida-gogn]', rot).textContent); }
    catch (e) { if (window.console) console.warn('SMVelSida: page data missing', e); return null; }
    if (window.SMValkostir && window.SMValkostir.kanonGogn && !$('script[data-sm-sida-gogn]', rot).hasAttribute('data-sm-kanon')) window.SMValkostir.kanonGogn(gogn);
    var ord = gogn.ord || {};
    var form = $('[data-sm-sida-form]', rot);
    var idInntak = $('[data-vs-afbrigdi]', form);
    var svid = $('[data-vs-svid]', rot);
    var rammi = $('[data-vs-rammi]', rot);
    var teikning = $('[data-vs-teikning]', rot);
    var punktar = $('[data-vs-punktar]', rot);
    var smaRadir = $$('[data-vs-smamyndir]', rot);
    var malbandEl = $('[data-vs-malband]', rot);
    var kaupa = $('[data-vs-kaupa]', rot);
    var kaupstika = $('[data-vs-kaupstika]', rot);
    var hnappar = $$('[data-vs-hnappur]', form);
    /* "Kaupa núna" (18.10): aukahnappur undir kaupstikunni, sést aðeins þegar allt er rétt fyllt (sama gilding og
       aðalhnappurinn), keyrir sama körfuflæði og fer svo beint í greiðslu */
    var knHnappur = $('[data-vs-kaupa-nuna]', form);
    var knRod = $('[data-vs-kaupa-nuna-rod]', form);
    var verdEl = $$('[data-vs-verd]', form);
    var kvilla = $('[data-vs-kvilla]', rot);
    /* FB 21.10 (E2): the Icelandic words the adapters write on the button bar, by an ASCII key; ord.takka_ord = "rammi=Border|litur=Colour|..."
       (sm-vel.liquid, only outside Icelandic) gives the page language, so the locale file holds no Icelandic */
    var TAKKA_IS = { rammi: 'Rammi', litur: 'Litur', efni: 'Efni', festing: 'Festing', flokkur: 'Flokkur', texti: 'Texti', rautt: 'Rautt',
      hvitt: 'Hvítt', svart: 'Svart', gull: 'Gull', silfur: 'Silfur', blatt: 'Blátt', gult: 'Gult', graent: 'Grænt',
      got2: '2 göt', got4: '4 göt', limrond: 'Límrönd', got: 'Göt', lim: 'Lím', tappar: 'Tappar',
      tappar_silfur: 'Silfur tappar', tappar_svartir: 'Svartir tappar', enska: 'Enska líka' };
    var TAKKA_KK = { 'Svartur': 'svart', 'Hv\u00edtur': 'hvitt' };
    var TAKKA_ORD = null;
    function takkaOrd(x) {
      if (TAKKA_ORD === null) {
        TAKKA_ORD = {};
        String(ord.takka_ord || '').split('|').forEach(function (p) { var i = p.indexOf('='), k = trim(p.slice(0, i)); if (i > 0 && TAKKA_IS.hasOwnProperty(k)) TAKKA_ORD[TAKKA_IS[k]] = trim(p.slice(i + 1)); });
        /* FG 21.10: masculine colour names of variant values (Owala: Svartur, Hvitur) take the same word as the neuter key */
        for (var ka in TAKKA_KK) if (TAKKA_KK.hasOwnProperty(ka) && TAKKA_ORD.hasOwnProperty(TAKKA_IS[TAKKA_KK[ka]])) TAKKA_ORD[ka] = TAKKA_ORD[TAKKA_IS[TAKKA_KK[ka]]];
      }
      var k = trim(x == null ? '' : String(x));
      return k && TAKKA_ORD.hasOwnProperty(k) && TAKKA_ORD[k] ? TAKKA_ORD[k] : x;
    }
    var tilk = $('[data-vs-tilkynning]', rot);
    var stikaListi = $('[data-vs-stika-listi]', rot);
    var merkiEl = $('[data-vs-merki]', rot);
    var litarondEl = $('[data-vs-litarond]', rot);
    var titillEl = $('[data-vs-titill]', rot);
    var inngangurEl = $('[data-vs-inngangur]', rot);
    var molaEl = $('[data-vs-moli]', rot);
    /* FG 21.10 (C1): a named engine whose script never loaded (blocked, 404) gets the photo adapter so the page is not dead,
       but the buttons stay disabled and villa_hlada shows: never a cart line without the design and its production file */
    var adapterVantar = !!gogn.adapter && gogn.adapter !== 'mynd' && !ADAPTERS[gogn.adapter];
    var adapter = ADAPTERS[gogn.adapter] || ADAPTERS.mynd;
    var loppaLeyfd = !!LOPPA_VELAR[gogn.adapter];
    /* the paw font joins the page's web fonts only on a paw line (SMFram.loppa.vafri; SMFram loads deferred) */
    if (loppaLeyfd) {
      var loppaSkra = function () { if (window.SMFram && window.SMFram.loppa) { window.SMFram.loppa.vafri(); return true; } return false; };
      if (!loppaSkra()) { document.addEventListener('DOMContentLoaded', loppaSkra); window.addEventListener('load', loppaSkra); }
    }
    var villaTjakn = function () { return (loppaLeyfd && ord.villa_tjakn_loppa) || ord.villa_tjakn; };
    /* CW 19.10 (owner): a small "\uD83D\uDC3E Add a paw" chip under each name field of a paw line (adapter.loppa lists them) */
    function loppaTakkar() {
      if (!loppaLeyfd || !ord.loppa_takki || typeof adapter.loppa !== 'function') return;
      var reitir = kalla(adapter, 'loppa', [ctx, form], []) || [];
      $$('[data-vs-loppa]', form).forEach(function (k) { if (reitir.indexOf(k.__smReitur) < 0) k.hidden = true; });
      if (!document.getElementById('vs-loppa-still')) {
        var st = document.createElement('style'); st.id = 'vs-loppa-still';
        st.textContent = '.vs-loppa{display:flex;flex-wrap:wrap;align-items:center;gap:6px 12px;margin:8px 0 0}.vs-loppa[hidden]{display:none}' +
          '.vs-loppa .vs-tillaga{font-weight:500}.vs-loppa-hjalp{margin:0;font-size:var(--vs-t-hjalp,13px);line-height:1.35;color:var(--vs-ink-soft,#5c5a55)}';
        document.head.appendChild(st);
      }
      reitir.forEach(function (x) {
        if (!x || !x.parentNode) return;
        var k = x.__smLoppaTakki;
        if (!k || !k.parentNode) {
          k = document.createElement('div'); k.className = 'vs-loppa'; k.setAttribute('data-vs-loppa', ''); k.__smReitur = x;
          var hj = (x.id || 'vs-loppa') + '-loppa';
          k.innerHTML = '<button type="button" class="vs-tillaga vs-loppa-takki" aria-label="' + esc(ord.loppa_aria || ord.loppa_takki) + '" aria-describedby="' + esc(hj) + '">' + esc(ord.loppa_takki) + '</button>' +
            '<p class="vs-loppa-hjalp" id="' + esc(hj) + '">' + esc(ord.loppa_hjalp || '') + '</p>';
          var b = k.firstChild;
          /* the field keeps focus (and the phone keyboard) when the chip is pressed */
          b.addEventListener('mousedown', function (e) { if (document.activeElement === x) e.preventDefault(); });
          b.addEventListener('click', function () { loppaSetja(x); });
          /* under the field: after it, its help line and its own messages if they follow it */
          var eftir = x, n = x.nextElementSibling;
          while (n && n.tagName === 'P' && /vs-hjalp|vs-villa|vs-bod/.test(n.className || '')) { eftir = n; n = n.nextElementSibling; }
          eftir.parentNode.insertBefore(k, eftir.nextSibling);
          x.__smLoppaTakki = k;
        }
        k.hidden = false;
      });
    }
    var stillingar = {};
    try { stillingar = gogn.adapter_stillingar ? JSON.parse(gogn.adapter_stillingar) : {}; } catch (e) { stillingar = {}; }
    /* colour chips from the section setting litaflisar (one "Name = CSS" per line) */
    var flisar = {};
    String(gogn.litaflisar || '').split(/\r?\n/).forEach(function (l) {
      var i = l.indexOf('=');
      if (i > 0) flisar[trim(l.slice(0, i))] = trim(l.slice(i + 1));
    });

    var afbrigdi = gogn.afbrigdi || [];
    var afb = null;
    var kodar = [];
    var lysandiSed = {};
    var reynt = false;
    var sendir = false;
    var slide = 0;
    var skilaFokus = null;
    var komidTimi = null;
    var pref = {};

    /* product family: every product that can be shown on this page, and its groups */
    var fjol = null, vorur = {}, nuHandle = gogn.vara.handle, nuHopur = null, upphafH = nuHandle, allirSyndir = false;
    function sidanSjalf() {
      return { id: gogn.vara.id, handle: gogn.vara.handle, titill: gogn.vara.titill, url: gogn.vara.url || window.location.pathname,
        lede: gogn.vara.lede || '', staerd: gogn.vara.staerd || '', tags: gogn.vara.tags || [], sersnid: !!gogn.vara.sersnid,
        valkostir: gogn.valkostir || [], afbrigdi: gogn.afbrigdi || [], myndir: gogn.myndir || [] };
    }
    if (gogn.fjolskylda && gogn.fjolskylda.vorur) {
      Object.keys(gogn.fjolskylda.vorur).forEach(function (h) { vorur[h] = lesaVoru(gogn.fjolskylda.vorur[h], h); });
      var sjalf = sidanSjalf();
      if (vorur[nuHandle]) {
        /* the page product keeps the page data (option codes, walls, image sizes) with the family's facts */
        var fv = vorur[nuHandle];
        sjalf.url = fv.url; sjalf.lede = fv.lede; sjalf.staerd = fv.staerd; sjalf.tags = fv.tags; sjalf.sersnid = fv.sersnid;
        sjalf.myndir.forEach(function (m, i) { if (fv.myndir[i]) m.f = fv.myndir[i].f; });
      }
      vorur[nuHandle] = sjalf;
      var hopar = (gogn.fjolskylda.hopar || []).map(function (g) {
        var vs = (g.vorur || []).filter(function (h, i, a) { return !!vorur[h] && a.indexOf(h) === i; });
        vs = vs.filter(function (h) { return !vorur[h].sersnid; }).concat(vs.filter(function (h) { return vorur[h].sersnid; }));
        return { lykill: g.lykill, heiti: g.heiti || '', stutt: g.stutt || g.heiti || '', vorur: vs };
      }).filter(function (g) { return g.vorur.length; });
      fjol = { hopar: hopar, vorur: vorur };
    }
    function hopurVoru(h) {
      if (!fjol) return null;
      for (var i = 0; i < fjol.hopar.length; i++) if (fjol.hopar[i].vorur.indexOf(h) >= 0) return fjol.hopar[i];
      return null;
    }
    function nuVara() { return (fjol && vorur[nuHandle]) || sidanSjalf(); }
    if (fjol) {
      /* ?skilti=<handle> opens another product of the family; ?flokkur=<group> a group (as on the old pages) */
      var bedid = slodParam('skilti');
      if (bedid && vorur[bedid] && hopurVoru(bedid)) upphafH = bedid;
      nuHopur = hopurVoru(upphafH);
      if (!nuHopur) {
        var bf = slodParam('flokkur'), bg = null;
        fjol.hopar.forEach(function (g) { if (g.lykill === bf) bg = g; });
        if (bg && upphafH !== nuHandle) { nuHopur = bg; upphafH = bg.vorur[0]; }
      }
      if (!nuHopur) {
        /* not in any group (another template's product): shown alone, never swapped for another */
        nuHopur = { lykill: 'solo', heiti: '', stutt: '', vorur: [nuHandle], solo: true };
        fjol.hopar = [nuHopur];
        upphafH = nuHandle;
      }
    }

    for (var i = 0; i < afbrigdi.length; i++) if (String(afbrigdi[i].id) === String(idInntak.value)) afb = afbrigdi[i];
    if (!afb) afb = afbrigdi[0] || null;
    kodar = afb ? afb.o.slice() : [];
    /* lysandi slod (K3): readable params pick the variant before the first sync */
    slodSamnefni(gogn.valkostir || [], gogn.vara && gogn.vara.handle);
    /* B1 23.10: which options the link named itself (the rest fell back to a default), for the adapter's forval hook */
    var lysSkyr = [];
    var lysUpph = afbUrLysandiSlod({ valkostir: gogn.valkostir || [] }, afbrigdi, lysSkyr);
    var lysUpphFannst = !!lysUpph;
    if (lysUpph) { afb = lysUpph; kodar = afb.o.slice(); idInntak.value = afb.id; }
    function mundu() {
      /* option values by name, remembered across products (Or keeps the customer's choice) */
      var v = nuVara();
      if (!afb) return;
      (v.valkostir || []).forEach(function (o, j) {
        var g = null;
        (o.gildi || []).forEach(function (x) { if (!g && x.kodi === afb.o[j]) g = x; });
        if (g) pref[o.nafn] = g.nafn;
      });
    }
    mundu();

    /* ---- steps ---- */
    var skref = [];
    var magnSkref = null;
    function hastafaHamur(el) {
      var v = el.getAttribute('data-hastafir');
      return v === null ? '' : v === 'allir' ? 'allir' : 'fremst';
    }
    function lesaSkref() {
      var stikur = {};
      if (stikaListi) $$('[data-vs-stika]', stikaListi).forEach(function (a) { stikur[a.getAttribute('data-vs-stika')] = a; });
      skref = $$('[data-vs-skref]', form).map(function (el) {
        var lykill = el.getAttribute('data-lykill') || '';
        var gerd = el.getAttribute('data-gerd');
        var eigid = gerd === 'sersnid' || gerd === 'vara';
        var gamalt = null;
        skref.forEach(function (x) { if (x.el === el) gamalt = x; });
        var stikaA = stikur[lykill] || null;
        return {
          el: el, lykill: lykill, gerd: gerd,
          p: parseInt(el.getAttribute('data-p'), 10) || 0,
          p2: parseInt(el.getAttribute('data-p2'), 10) || 0,
          birting: el.getAttribute('data-birting') || '',
          skylda: el.getAttribute('data-skylda') === 'true',
          eig: el.getAttribute('data-eiginleiki') || '',
          villaTexti: el.getAttribute('data-villa') || '',
          vantar: el.getAttribute('data-vantar') || '',
          kveikja: (el.getAttribute('data-kveikja') || '').toLowerCase().split(',').map(trim).filter(function (x) { return !!x; }),
          kveikjaSkref: el.getAttribute('data-kveikja-skref') || '',
          hastafir: hastafaHamur(el),
          leyfaTjakn: el.hasAttribute('data-leyfa-tjakn'),
          gildiEl: $('[data-vs-gildi]', el),
          villaEl: $('[data-vs-villa]', el),
          nrEl: $('[data-vs-nr]', el),
          heitiEl: $('.vs-heiti', el),
          inntak: eigid ? null : ($('[data-vs-inntak]', el) || $('[data-vs-magn-tala]', el)),
          radio: eigid ? [] : $$('input[type=radio]', el),
          teljari: $('[data-vs-teljari]', el),
          skraEl: $('[data-sm-skra]', el),
          hjalpEl: $('[data-vs-hjalp]', el),
          stikaA: stikaA,
          stikaLi: stikaA ? stikaA.parentNode : null,
          farid: gamalt ? gamalt.farid : false,
          skilabod: gamalt ? gamalt.skilabod : ''
        };
      });
      magnSkref = null;
      skref.forEach(function (s) { if (s.gerd === 'magn') magnSkref = s; });
    }
    lesaSkref();
    function finnaSkref(lykill) {
      for (var i = 0; i < skref.length; i++) if (skref[i].lykill === lykill) return skref[i];
      return null;
    }

    function magn() {
      if (!magnSkref || !magnSkref.inntak) return 1;
      var n = parseInt(magnSkref.inntak.value, 10);
      var lag = parseInt(magnSkref.el.getAttribute('data-lagmark'), 10) || 1;
      var ham = parseInt(magnSkref.el.getAttribute('data-hamark'), 10) || 99;
      if (!(n >= lag)) n = lag;
      if (n > ham) n = ham;
      return n;
    }
    function valinnKostur(s) {
      for (var i = 0; i < s.radio.length; i++) if (s.radio[i].checked) return s.radio[i];
      return null;
    }
    /* FK 21.10: the name of a chosen tile as SHOWN in the step value. data-nafn stays the Icelandic text (cart property, logic); outside Icelandic
       the value shows the tile's own visible name (its translation). In Icelandic nothing changes. */
    function synNafn(r) {
      var dn = r ? r.getAttribute('data-nafn') || '' : '';
      if (!r || !dn || (window.Shopify && window.Shopify.locale === 'is') || String(document.documentElement.lang || 'is').slice(0, 2) === 'is') return dn;
      var lb = r.closest ? r.closest('label') : null, h = lb ? lb.querySelector('.vs-kostur-heiti') : null;
      if (!h) return dn;
      var t = '';
      for (var n = h.firstChild; n; n = n.nextSibling) if (n.nodeType === 3 || (n.nodeType === 1 && !/vs-sjonlaust/.test(n.className || ''))) t += n.textContent;
      t = trim(t);
      return t || dn;
    }
    function gildiSkrefs(s) {
      if (s.gerd === 'valkostur' || s.gerd === 'val') { var r = valinnKostur(s); return r ? r.value : ''; }
      if (s.gerd === 'rammi') { var rr = valinnKostur(s); return rr ? rr.value : ''; }
      if (s.gerd === 'magn') return magn();
      if (s.gerd === 'vara') return nuHandle;
      if (s.gerd === 'sersnid') return '';
      if (s.inntak) return s.inntak.value;
      return '';
    }

    /* State by stable keys. Typed text is in here too but never goes to the URL. */
    function stada() {
      var st = { vara: gogn.vara, afbrigdi: afb, kodar: kodar.slice(), magn: magn(), voruGogn: nuVara(), hopur: nuHopur };
      skref.forEach(function (s) {
        /* (samraeming E3) a frame step hidden because its frame is not sold here (metal without the frame
           variant) says an: the sign has no frame, so an adapter never keeps an earlier Med ramma */
        if (s.gerd !== 'skra') st[s.lykill] = !s.el.hidden ? gildiSkrefs(s) : s.gerd === 'rammi' && FALIN_OVIRK && s.el.__vsEkki ? 'an' : '';
        if (s.gerd === 'rammi' && s.lykill !== 'rammi') st.rammi = st[s.lykill];
      });
      return st;
    }
    var ctx = {
      rot: rot, teikning: teikning, rammi: rammi, stillingar: stillingar, gogn: gogn,
      /* lysandi slod (K3): true when the incoming link asked for a variant explicitly, the old
         ?variant=<id> or the new readable option params - several adapters (door signs, house numbers,
         badges, artwork, bag tags) use this to tell "a shared link" from "the plain default view" */
      afbUrSlod: lysUpphFannst || /[?&]variant=/.test(window.location.search || ''),
      afbrigdi: afb, kr: kr, esc: esc,
      t: function (k, b) { return fylla(ord[k] || '', b); },
      /* CK 19.10: the one emoji test of every line (ctx.tjakn.test / hreinsa) */
      tjakn: tjakn,
      id: gogn.sid || 'vs',
      vel: gogn.vel || {},
      vara: function () { return nuVara(); },
      /* T1 (18.10): option text. kanon -> Icelandic (logic, cart, file), syn -> shop language (display); h = handle, default the product shown */
      kanon: function (t, teg, h) { return kanon(t, h || nuVara().handle, teg); },
      syn: function (t, teg, h) { return syn(t, h || nuVara().handle, teg); },
      fjolskylda: fjol,
      hopur: function () { return nuHopur; },
      skipta: function (h, id) { if (skiptaVoru(h, id)) samstilla(true); },
      breytt: function () { samstilla(true); },
      teiknaAftur: function () { teikna(); flisarTeikna(100, 'letur'); flisarTeikna(300, 'utlit'); },
      reynt: function () { return reynt; },
      skref: function (l) { var s = finnaSkref(l); return s ? { el: s.el, inntak: s.inntak, synilegt: !s.el.hidden } : null; },
      /* the cart property name of a step (the notes step's name is a block setting) */
      eiginleiki: function (l) { var s = finnaSkref(l); return s ? s.eig || '' : ''; },
      /* K-heiti: stutt (text, or true for the same text) also renames the step bar chip (its first text node;
         the hidden ", lokid" span stays); without it only the heading changes, as before */
      heiti: function (l, t, stutt) {
        var s = finnaSkref(l);
        if (s && s.heitiEl && s.heitiEl.textContent !== t) s.heitiEl.textContent = t;
        if (stutt == null || stutt === false || !s || !s.stikaA) return;
        var st = stutt === true ? String(t) : String(stutt), n = s.stikaA.firstChild;
        if (n && n.nodeType === 3) { if (n.nodeValue !== st) n.nodeValue = st; }
        else s.stikaA.insertBefore(document.createTextNode(st), s.stikaA.firstChild);
      },
      setjaMynd: setjaMynd,
      html: (function () { var h = kitSmidir(ord); h.kostir = function (o) { return kostirSidu(o); }; h.setja = setjaHtml; return h; })(),
      leturHeiti: function (id, n) { return leturHeiti(id, n); },
      rammiGildi: function () { return rammiGildi(); },
      rammiGeo: function () { var r = rammiSkref(); return r ? r.el.__vsGeo || null : null; },
      flisar: function () { flisEpoch += 1; flisarTeikna(0); },
      veljaIVoru: function (h, o) { return fjol && vorur[h] ? veljaIVoru(vorur[h], o || []) : null; },
      uppfaera: function () { return uppfaera(); },
      tilkynna: function (t, synileg) { tilkynna(t, synileg !== false); },
      /* FB 21.10 (E2): the bar's Icelandic word -> the page language (ord.takka_ord, only outside Icelandic), for adapters that write their own labels */
      takkaOrd: function (x) { return takkaOrd(x); },
      /* Takka-hamur fyrir adapter (prufa, section stilling "takkar" -> data-takkar): ctx.takkar({ fela: [lyklar skrefa],
         hopar: [{ heiti, pillur: [{ k, t, til(), kv(), smella() }] }] }). Pillurnar koma nedst a forskodunarmyndinni
         (sama utlit og hurdaskiltatakkarnir: .vs-hurd-takki, CSS i sm-vs-hurd.css); hopur med tveimur pillum er
         tvihnappa rofi. Skrefin i fela eru ennta i DOM (astand er lesid ur theim) en falin og sleppt i numeringu og
         efri pillum. smella() a ad gera NAKVAEMLEGA thad sem skrefin gera. Skilar { uppfaera }; kalla a thad eftir hverja teikningu. */
      takkar: function (o) {
        var bar = document.createElement('div'), pillur = [], hopar = [], tt = takkaOrd;
        (o.fela || []).forEach(function (l) {
          var s = finnaSkref(l);
          if (s && s.el) s.el.setAttribute('data-takka-fel', '');
          var a = rot.querySelector('[data-vs-stika="' + l + '"]'), li = a && a.closest ? a.closest('li') : null;
          if (li) li.setAttribute('data-takka-fel', '');
        });
        bar.className = 'vs-hurd-takkar';
        bar.setAttribute('role', 'group');
        bar.setAttribute('aria-label', ord.takkar_haus || 'Prófaðu á skiltinu');
        var rod = takkarSmida(bar, rammi, { haus: ord.takkar_haus, stutt: ord.takkar_stutt, leid: ord.takkar_leid });
        (o.hopar || []).forEach(function (h) {
          var w = document.createElement('div'), ps = [];
          /* BOLA-ALLT 22.10: a group of one pill is an add-on (Rammi, Enska líka, Logo): in the glass bubble it gets the orange glass, a group of
             two or more the white lens (sm-vs-bola.css); no change in the strip */
          w.className = 'vs-takka-par' + (h.punktar ? ' vs-takka-punktar' : '') + (!h.punktar && h.pillur.length === 1 ? ' vs-takka-stakur' : '');
          w.setAttribute('role', 'group');
          if (h.heiti) w.setAttribute('aria-label', tt(h.heiti));
          h.pillur.forEach(function (p) {
            p.t = tt(p.t);
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'vs-hurd-takki';
            b.setAttribute('data-takki', p.k);
            b.setAttribute('aria-pressed', 'false');
            b.textContent = p.t;
            /* colour choices (pill option punktur: svart, hvitt, hnota, akryl, an, gull, silfur) are small dots, the name in aria-label and title */
            if (p.punktur) { b.setAttribute('data-punktur', p.punktur); b.setAttribute('aria-label', p.t); b.setAttribute('title', p.t); b.textContent = ''; }
            /* a colour of its own (litur: CSS colour, e.g. the ISO colours of the safety signs) is a dot too: --pk on the button */
            if (p.litur) { b.setAttribute('data-punktur', 'litur'); b.style.setProperty('--pk', p.litur); b.setAttribute('aria-label', p.t); b.setAttribute('title', p.t); b.textContent = ''; }
            b.addEventListener('click', function () { p.smella(p.kv()); });
            p.el = b;
            w.appendChild(b);
            pillur.push(p);
            ps.push(p);
          });
          rod.appendChild(w);
          hopar.push({ el: w, pillur: ps, linsa: !h.punktar && ps.length > 1 });
        });
        /* BOLA-ALLT 22.10 (eigandi 21.10, sama og hurðaskiltin): where a pill changes the size or the price (o.lina from the adapter) the glass
           bubble ends with one line "30 × 7,5 cm · 10.990 kr.": the size from the chosen variant's own option values, the price as the buy bar
           counts it for one sign (variant + extras). Only in the bubble (data-takkar-bola); the strip on a phone has no such line. The template
           turns it on (section setting takkar_bola_lina -> data-takkar-bola-lina) for the products whose pills change size or price. */
        var lina = null;
        if ((o.lina || rot.hasAttribute('data-takkar-bola-lina')) && rot.hasAttribute('data-takkar-bola')) {
          lina = document.createElement('span');
          lina.className = 'vs-takka-par-lina vs-takka-lina-kit';
          lina.id = 'vs-' + (gogn.sid || 'vs') + '-takka-lina';
          lina.setAttribute('aria-live', 'polite');
          /* after the row, not in it: the row may scroll, and engines find their last group with .vs-takka-par:last-child */
          rod.parentNode.insertBefore(lina, rod.nextSibling);
          bar.setAttribute('aria-describedby', lina.id);
        }
        function linaTexti() {
          if (!lina) return;
          var mal = '', n = (afb && afb.n) || [];
          for (var i = 0; i < n.length && !mal; i++) {
            var x = String(n[i] || ''), m2 = /(\d+(?:[.,]\d+)?)\s*(?:cm)?\s*[x\u00d7]\s*(\d+(?:[.,]\d+)?)\s*cm/i.exec(x), m1 = m2 ? null : /(\d+(?:[.,]\d+)?)\s*cm\b/i.exec(x);
            if (m2) mal = m2[1].replace('.', ',') + ' \u00d7 ' + m2[2].replace('.', ',') + ' cm';
            else if (m1) mal = m1[1].replace('.', ',') + ' cm';
          }
          var lt = afb ? (mal ? mal + ' \u00b7 ' : '') + kr(afb.verd + aukaVerd()) : '';
          if (lina.textContent !== lt) lina.textContent = lt;
        }
        function haed() { var h = bar.offsetHeight; if (h && rot.style.getPropertyValue('--takka-h') !== h + 'px') rot.style.setProperty('--takka-h', h + 'px'); }
        if (window.ResizeObserver) new ResizeObserver(haed).observe(bar);
        return {
          el: bar,
          uppfaera: function () {
            pillur.forEach(function (p) {
              var til = !!p.til(), kv = til && !!p.kv(), v = kv ? 'true' : 'false';
              if (p.el.hidden !== !til) p.el.hidden = !til;
              if (p.el.getAttribute('aria-pressed') !== v) p.el.setAttribute('aria-pressed', v);
            });
            hopar.forEach(function (h) {
              var allt = h.pillur.every(function (p) { return p.el.hidden; });
              if (h.el.hidden !== allt) h.el.hidden = allt;
              /* BOLA-ALLT 22.10: the white lens sits on the chosen pill's place among the SHOWN pills (sm-vs-bola.css) */
              if (h.linsa) {
                var i = -1, j = 0;
                h.pillur.forEach(function (p) { if (p.el.hidden) return; if (p.el.getAttribute('aria-pressed') === 'true' && i < 0) i = j; j += 1; });
                var dl = i < 0 ? 'nei' : 'ja';
                if (h.el.getAttribute('data-linsa') !== dl) h.el.setAttribute('data-linsa', dl);
                if (i >= 0 && h.el.style.getPropertyValue('--linsa-i') !== String(i)) h.el.style.setProperty('--linsa-i', String(i));
              }
            });
            linaTexti();
            haed();
            if (bar.__skrun) bar.__skrun();
            if (bar.__bolaMal) bar.__bolaMal();
          }
        };
      }
    };

    /* ---- variants ---- */
    function finna(k) {
      for (var i = 0; i < afbrigdi.length; i++) {
        var a = afbrigdi[i], ok = a.o.length === k.length;
        for (var j = 0; ok && j < k.length; j++) if (a.o[j] !== k[j]) ok = false;
        if (ok) return a;
      }
      return null;
    }
    function einhverTil(p0, kodi) {
      for (var i = 0; i < afbrigdi.length; i++) if (afbrigdi[i].o[p0] === kodi && afbrigdi[i].til) return true;
      return false;
    }
    function veljaValkost(p, kodi) {
      var p0 = p - 1, nyr = kodar.slice();
      nyr[p0] = kodi;
      var v = finna(nyr);
      if (!v || !v.til) {
        /* closest variant with this value: available first, then most other values kept */
        var best = null, bestStig = -1;
        afbrigdi.forEach(function (a) {
          if (a.o[p0] !== kodi) return;
          var stig = a.til ? 100 : 0;
          for (var j = 0; j < a.o.length; j++) if (j !== p0 && a.o[j] === nyr[j]) stig += rammiVigt(j);
          if (stig > bestStig) { best = a; bestStig = stig; }
        });
        if (best && (!v || best.til)) v = best;
      }
      afb = v || null;
      kodar = v ? v.o.slice() : nyr;
      mundu();
      slide = 0;
      samstilla(true);
    }

    /* ---- two options in one grid (valkostur_saman): value "code1|code2" ---- */
    function einhverTil2(p0, k1, q0, k2) {
      for (var i = 0; i < afbrigdi.length; i++) if (afbrigdi[i].o[p0] === k1 && afbrigdi[i].o[q0] === k2 && afbrigdi[i].til) return true;
      return false;
    }
    /* the codes of the Shopify option steps above step s ({ p0: code }), and whether a variant has them with
       value k1 at p0 (and k2 at q0) */
    function ofarKodar(s) {
      var o = {};
      for (var i = 0; i < skref.length && skref[i] !== s; i++) {
        var x = skref[i];
        if (x.gerd !== 'valkostur') continue;
        o[x.p - 1] = kodar[x.p - 1];
        if (x.p2) o[x.p2 - 1] = kodar[x.p2 - 1];
      }
      return o;
    }
    function tilOfar(ofar, p0, k1, q0, k2) {
      for (var i = 0; i < afbrigdi.length; i++) {
        var a = afbrigdi[i], ok = a.o[p0] === k1 && (q0 < 0 || k2 == null || a.o[q0] === k2);
        for (var j in ofar) if (ok && Object.prototype.hasOwnProperty.call(ofar, j) && a.o[j] !== ofar[j]) ok = false;
        if (ok) return true;
      }
      return false;
    }
    /* the closest variant holding the fixed positions: available first, then most other values kept */
    function naestaAfb(nyr, fastir) {
      var best = null, bestStig = -1;
      afbrigdi.forEach(function (a) {
        for (var f = 0; f < fastir.length; f++) if (a.o[fastir[f]] !== nyr[fastir[f]]) return;
        var stig = a.til ? 100 : 0;
        for (var j = 0; j < a.o.length; j++) if (fastir.indexOf(j) < 0 && a.o[j] === nyr[j]) stig += rammiVigt(j);
        if (stig > bestStig) { best = a; bestStig = stig; }
      });
      return best;
    }
    function veljaPar(p, p2, gildi) {
      var hl = String(gildi).split('|'), p0 = p - 1, q0 = p2 - 1, nyr = kodar.slice();
      nyr[p0] = hl[0]; nyr[q0] = hl[1];
      var v = finna(nyr);
      if (!v || !v.til) { var b = naestaAfb(nyr, [p0, q0]); if (b && (!v || b.til)) v = b; }
      afb = v || null;
      kodar = v ? v.o.slice() : nyr;
      mundu();
      slide = 0;
      samstilla(true);
    }

    /* ---- product family (E1): swap the product shown, in place ---- */
    function upphafsAfbrigdi(v) {
      /* the product's start variant (the first available one: on safety signs the ISO colour); options the
         adapter lets keep (halda, default yes) take the customer's earlier choice by name when it exists */
      var grunn = null;
      v.afbrigdi.forEach(function (a) { if (!grunn && a.til) grunn = a; });
      grunn = grunn || v.afbrigdi[0] || null;
      if (!grunn) return null;
      var vilja = grunn.o.slice(), annad = false;
      v.valkostir.forEach(function (o, j) {
        if (kalla(adapter, 'halda', [o.nafn, ctx], true) === false) return;
        var p = pref[o.nafn];
        if (p == null) return;
        o.gildi.forEach(function (g) { if (g.nafn === p && vilja[j] !== g.kodi) { vilja[j] = g.kodi; annad = true; } });
      });
      if (!annad) return grunn;
      var f = null;
      v.afbrigdi.forEach(function (a) { if (!f && a.o.join('\u0001') === vilja.join('\u0001')) f = a; });
      return f || grunn;
    }
    function skiptaVoru(h, id) {
      if (!fjol || !vorur[h]) return false;
      var sami = h === nuHandle, v = vorur[h];
      if (!sami) {
        nuHandle = h;
        var g = hopurVoru(h);
        if (g) nuHopur = g;
        gogn.vara = { id: v.id, handle: v.handle, titill: v.titill, url: v.url };
        gogn.valkostir = v.valkostir;
        gogn.afbrigdi = v.afbrigdi;
        gogn.myndir = v.myndir;
        afbrigdi = v.afbrigdi;
      }
      var nyr = null;
      if (id != null) afbrigdi.forEach(function (a) { if (String(a.id) === String(id)) nyr = a; });
      if (!nyr && !sami) nyr = upphafsAfbrigdi(v);
      if (nyr) { afb = nyr; kodar = nyr.o.slice(); }
      else if (!sami) { afb = afbrigdi[0] || null; kodar = afb ? afb.o.slice() : []; }
      /* CK 19.10 (QA3 F1): a value picked on a product tile (the safety sign colour dots) is remembered by name too */
      mundu();
      if (!sami) {
        slide = 0;
        byggjaValkosti();
        hausVoru();
      }
      return true;
    }
    function titillHtml(t) {
      var h = gogn.titill_ahersla || 'stjornur';
      t = String(t || '');
      /* the whole product title is always in the h1 (SEO): the dash is kept in a visually hidden span,
         the part after it follows as em (eftir-bandstriki) or a smaller line (sidasta-ord); same markup as sm-vel.liquid */
      if (h === 'eftir-bandstriki') {
        var p = t.split(' - ');
        return esc(p[0]) + (p.length > 1 ? ' <span class="vs-titill-skil">- </span><em>' + esc(p.slice(1).join(' - ')) + '</em>' : '');
      }
      if (h === 'sidasta-ord') {
        var pp = t.split(' - '), f = pp[0], o = f.split(' ');
        var und = pp.length > 1 ? '<span class="vs-titill-skil"> - </span><span class="vs-titill-undir">' + esc(pp.slice(1).join(' - ')) + '</span>' : '';
        if (o.length < 2) return esc(f) + und;
        var sid = o[o.length - 1];
        return esc(trim(f.slice(0, f.lastIndexOf(sid)))) + ' <em>' + esc(sid) + '</em>' + und;
      }
      return esc(t).split('*').map(function (x, i) { return i % 2 ? '<em>' + x + '</em>' : x; }).join('');
    }
    function hausVoru() {
      var v = nuVara();
      if (titillEl && !gogn.titill_fastur) {
        titillEl.innerHTML = titillHtml(v.titill);
        if (String(v.titill).length > 34) titillEl.setAttribute('data-langur', ''); else titillEl.removeAttribute('data-langur');
      }
      if (inngangurEl && !gogn.inngangur_fastur) { inngangurEl.textContent = v.lede || ''; inngangurEl.hidden = !v.lede; }
      if (molaEl) molaEl.textContent = v.titill;
    }

    /* ---- option steps of a product (E3): the same markup and value codes as
       snippets/sm-sida-valkostur.liquid, rebuilt in place when the product changes ---- */
    var sjalfEl = $('[data-vs-sjalfvalkostir]', form);
    function litavalIx(v) {
      var ix = kalla(adapter, 'litaval', [v.valkostir, ctx], undefined);
      if (typeof ix === 'number') return ix;
      for (var i = 0; i < v.valkostir.length; i++) if (gogn.litarond && v.valkostir[i].nafn === gogn.litarond) return i;
      return -1;
    }
    function flisLitur(nafn) {
      return kalla(adapter, 'litur', [nafn, ctx], null) || flisar[nafn] || null;
    }
    function byggjaValkosti() {
      if (!sjalfEl || gogn.synaValkosti === false) return;
      var v = nuVara(), sid = gogn.sid || 'vs', lit = litavalIx(v), html = '';
      v.valkostir.forEach(function (o, i) {
        if (o.nafn === 'Title' && v.afbrigdi.length < 2) return;
        /* an option a block covers (val, its folded pair, rammi) is never shown twice (samraeming) */
        if (gogn.teknir && gogn.teknir.indexOf(String(i + 1)) >= 0) return;
        var p = i + 1, lykill = 'valkostur' + p, fid = 'vs-' + sid + '-' + lykill, oSyn = syn(o.nafn, v.handle, 'o');
        var litir = i === lit || o.gildi.every(function (g) { return !!flisLitur(g.nafn); });
        /* as the snippet: cards when every value has its own variant photo (the photo says more than a chip) */
        var myndir = o.gildi.map(function (g) {
          var m = null;
          v.afbrigdi.forEach(function (a) { if (!m && a.o[i] === g.kodi && a.mynd) m = a.mynd; });
          return m;
        });
        var olikar = myndir.every(function (m) { return !!m; }) && myndir.some(function (m) { return m.id !== myndir[0].id; });
        var birt = i !== lit && olikar ? 'spjold' : litir ? 'litir' : 'pillur';
        var n = o.gildi.length, dalk = birt === 'litir' ? Math.min(7, Math.max(4, n)) : birt === 'spjold' ? 3 : (n === 1 || n > 4 ? 3 : n);
        html += '<fieldset class="vs-skref" id="' + fid + '" data-vs-skref data-gerd="valkostur" data-lykill="' + lykill + '" data-p="' + p + '" data-sjalf data-stika-heiti="' + esc(oSyn) + '">' +
          '<legend class="vs-skref-haus"><span class="vs-nr" aria-hidden="true" data-vs-nr></span><span class="vs-heiti">' + esc(oSyn) + '</span><span class="vs-gildi" data-vs-gildi></span></legend>' +
          '<div class="vs-val vs-val--' + birt + '" style="--dalkar:' + dalk + '">' +
          o.gildi.map(function (g, gi) {
            var gSyn = String(syn(g.nafn, v.handle, 'v')), nafn = gSyn.charAt(0).toUpperCase() + gSyn.slice(1);
            var inn = '<input type="radio" name="vs-' + sid + '-' + lykill + '" value="' + esc(g.kodi) + '" data-nafn="' + esc(gSyn) + '">';
            if (birt === 'spjold') {
              return '<label class="vs-kostur">' + inn + '<span class="vs-kostur-mynd"><img src="' + esc(myndir[gi].s6 || myndir[gi].src) + '" alt="' + esc(nafn) + '" aria-hidden="true" width="180" height="135" loading="lazy" decoding="async"></span>' +
                '<span class="vs-kostur-texti"><span class="vs-kostur-heiti">' + esc(nafn) + '</span><span class="vs-kostur-verd" data-vs-undir></span></span></label>';
            }
            if (birt === 'litir') {
              var c = flisLitur(g.nafn) || '#ccc';
              return '<label class="vs-kostur">' + inn + '<span class="vs-flis" style="background:' + esc(c) + '"></span><span class="vs-kostur-heiti">' + esc(nafn) + '</span><span class="vs-kostur-verd" data-vs-undir></span></label>';
            }
            return '<label class="vs-kostur">' + inn + '<span class="vs-kostur-heiti">' + esc(nafn) + '</span><small data-vs-undir></small></label>';
          }).join('') +
          '</div></fieldset>';
      });
      sjalfEl.innerHTML = html;
      lesaSkref();
      byggjaStiku();
      teiknaRond(true);
    }
    /* step bar entries follow the steps (the option steps change with the product) */
    function byggjaStiku() {
      if (!stikaListi) return;
      var til = {};
      $$('li', stikaListi).forEach(function (li) { var a = $('[data-vs-stika]', li); if (a) til[a.getAttribute('data-vs-stika')] = li; });
      var rod = [];
      skref.forEach(function (s) {
        var li = s.el.hasAttribute('data-sjalf') ? null : til[s.lykill];
        if (!li && s.el.hasAttribute('data-stika-heiti')) {
          li = document.createElement('li');
          li.innerHTML = '<a href="#' + s.el.id + '" data-vs-stika="' + esc(s.lykill) + '" data-stada="buid">' + esc(s.el.getAttribute('data-stika-heiti')) +
            '<span class="vs-sjonlaust" data-vs-stika-lokid>, ' + esc(ord.lokid || '') + '</span></a>';
        }
        if (li) rod.push(li);
      });
      stikaListi.innerHTML = '';
      rod.forEach(function (li) { stikaListi.appendChild(li); });
      lesaSkref();
    }

    /* ---- colour rail (E4): the colour option once more under the preview, so a customer can change
       colour without scrolling past the list of products ---- */
    var rondLykill = '';
    function teiknaRond(nytt) {
      if (!litarondEl) return;
      if (utlitSida()) { litarondEl.hidden = true; rondLykill = ''; return; }
      var v = nuVara(), ix = litavalIx(v), o = ix >= 0 ? v.valkostir[ix] : null;
      if (!o || o.gildi.length < 2) { litarondEl.hidden = true; rondLykill = ''; return; }
      var punkt = $('[data-vs-litarond-punktar]', litarondEl), lyk = v.handle + '|' + ix;
      if (nytt || lyk !== rondLykill) {
        rondLykill = lyk;
        var hf = $('[data-vs-litarond-heiti]', litarondEl);
        if (hf) hf.textContent = syn(o.nafn, v.handle, 'o');
        if (punkt) {
          punkt.setAttribute('aria-label', syn(o.nafn, v.handle, 'o'));
          punkt.innerHTML = o.gildi.map(function (g) {
            return '<button type="button" data-vs-lit="' + esc(g.kodi) + '" data-p="' + (ix + 1) + '" aria-pressed="false" aria-label="' + esc(syn(g.nafn, v.handle, 'v')) + '" title="' + esc(syn(g.nafn, v.handle, 'v')) + '"><b style="background:' + esc(flisLitur(g.nafn) || '#ccc') + '"></b></button>';
          }).join('');
        }
      }
      var nu = afb ? afb.o[ix] : kodar[ix], nafn = '';
      o.gildi.forEach(function (g) { if (g.kodi === nu) nafn = syn(g.nafn, v.handle, 'v'); });
      var gf = $('[data-vs-litarond-gildi]', litarondEl);
      if (gf && gf.textContent !== nafn) gf.textContent = nafn;
      if (punkt) $$('[data-vs-lit]', punkt).forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-vs-lit') === nu ? 'true' : 'false'); });
      litarondEl.hidden = false;
    }
    if (litarondEl) litarondEl.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-vs-lit]') : null;
      if (!b) return;
      veljaValkost(parseInt(b.getAttribute('data-p'), 10) || 1, b.getAttribute('data-vs-lit'));
    });

    /* ---- the product step (E2): group pills and product cards ---- */
    var voruLykill = '';
    function voruHeiti(v) {
      if (v.sersnid) return ord.eigin_texti || v.titill;
      return String(v.titill).split(' - ')[0];
    }
    function teiknaVorur(s) {
      var box = $('[data-vs-vorur]', s.el), hb = $('[data-vs-hopar]', s.el), meira = $('[data-vs-vorur-meira]', s.el), rod = $('[data-vs-vorur-meira-rod]', s.el);
      if (!box || !fjol || !nuHopur) return;
      var sid = gogn.sid || 'vs', syn = parseInt(s.el.getAttribute('data-synilegt'), 10) || 8, g = nuHopur;
      var nEigin = g.vorur.filter(function (h) { return vorur[h].sersnid; }).length;
      var allt = allirSyndir || g.vorur.length <= syn + 1;
      var lyk = [g.lykill, nuHandle, allt ? 1 : 0].join('|');
      var fok = document.activeElement, fokH = fok && s.el.contains(fok) && fok.name ? [fok.name, fok.value] : null;
      if (lyk !== voruLykill) {
        voruLykill = lyk;
        if (hb) {
          hb.hidden = fjol.hopar.length < 2;
          hb.innerHTML = fjol.hopar.map(function (x) {
            return '<label class="vs-kostur"><input type="radio" name="vs-' + sid + '-hopur" value="' + esc(x.lykill) + '"' + (x === g ? ' checked' : '') + '><span class="vs-kostur-heiti">' + esc(x.stutt) + '</span></label>';
          }).join('');
        }
        box.innerHTML = g.vorur.map(function (h, i) {
          var v = vorur[h];
          if (!allt && !(v.sersnid || i < syn - nEigin || h === nuHandle || h === upphafH)) return '';
          var m = v.myndir[0], a0 = v.afbrigdi[0], mynd = m ? '<img src="' + esc(m.s6 || m.src) + '" alt="' + esc(voruHeiti(v)) + '" aria-hidden="true" width="180" height="135" loading="lazy" decoding="async">' : '';
          return '<label class="vs-kostur"><input type="radio" name="vs-' + sid + '-vara" value="' + esc(h) + '"' + (h === nuHandle ? ' checked' : '') + '>' +
            '<span class="vs-kostur-mynd">' + mynd + '</span><span class="vs-kostur-texti"><span class="vs-kostur-heiti">' + esc(voruHeiti(v)) + '</span>' +
            '<span class="vs-kostur-verd">' + (a0 ? kr(a0.verd) : '') + '</span></span></label>';
        }).join('');
        if (rod) rod.hidden = g.vorur.length <= syn + 1;
        if (meira) {
          meira.textContent = allirSyndir ? (s.el.getAttribute('data-faerri') || ord.syna_faerri || '') : fylla(s.el.getAttribute('data-meira') || ord.sja_oll || '', { n: g.vorur.length });
          meira.setAttribute('aria-expanded', allirSyndir ? 'true' : 'false');
        }
        if (fokH) {
          var aftur = null;
          $$('input[type=radio]', s.el).forEach(function (r) { if (r.name === fokH[0] && r.value === fokH[1]) aftur = r; });
          if (aftur) { try { aftur.focus({ preventScroll: true }); } catch (e) {} }
        }
      }
      var lys = $('[data-vs-vara-lysing]', s.el);
      if (lys) { var t = nuVara().lede || ''; if (lys.textContent !== t) lys.textContent = t; lys.hidden = !t; }
      if (s.gildiEl) { var nt = voruHeiti(nuVara()); if (s.gildiEl.textContent !== nt) s.gildiEl.textContent = nt; }
    }
    form.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-vs-vorur-meira]') : null;
      if (!b) return;
      var s = null;
      skref.forEach(function (x) { if (x.el.contains(b)) s = x; });
      allirSyndir = !allirSyndir;
      voruLykill = '';
      if (s) teiknaVorur(s);
      if (!allirSyndir && s) {
        var box = $('[data-vs-vorur]', s.el);
        if (box && box.getBoundingClientRect().top < ofan()) scrollTil(window.pageYOffset + s.el.getBoundingClientRect().top - ofan());
      }
    });

    /* ---- conditional steps ---- */
    function valdarLysingar(lykill) {
      var ut = [];
      skref.forEach(function (s) {
        if (s.gerd !== 'valkostur' && s.gerd !== 'val') return;
        if (lykill && s.lykill !== lykill) return;
        if (s.el.hidden) return;
        var r = valinnKostur(s);
        if (!r) return;
        ut.push(String(r.value).toLowerCase());
        var dn = r.getAttribute('data-nafn');
        if (dn) { var dk = String(kanon(dn, nuVara().handle, 'v')); ut.push(dk.toLowerCase()); if (dk !== dn) ut.push(dn.toLowerCase()); }
      });
      return ut;
    }
    function aVid(s) {
      if (!s.kveikja.length) return true;
      var g = valdarLysingar(s.kveikjaSkref);
      return s.kveikja.some(function (o) { return g.some(function (x) { return x.indexOf(o) !== -1; }); });
    }
    function birta(s, syn) {
      if (s.el.hidden === !syn) return;
      s.el.hidden = !syn;
      $$('input, textarea, select', s.el).forEach(function (x) {
        if (s.skraEl && s.skraEl.contains(x)) return;
        x.disabled = !syn;
      });
      if (s.skraEl && window.SMSkra) window.SMSkra.syna(s.skraEl, syn);
      if (!syn) { s.farid = false; syndVilla(s, ''); }
      if (s.stikaLi) s.stikaLi.hidden = !syn;
    }

    /* ---- validation ---- */
    function skraVantar(s) {
      if (!window.SMSkra || !s.skraEl) return s.skylda;
      return window.SMSkra.vantar(form).indexOf(s.skraEl) !== -1;
    }
    function skraKomin(s) {
      if (!window.SMSkra || !s.skraEl) return false;
      var eig = s.skraEl.getAttribute('data-eiginleiki');
      return window.SMSkra.skrar(form).some(function (x) { return x.eiginleiki === eig; }) ||
        window.SMSkra.lysingar(form).length > 0 && !skraVantar(s);
    }
    function vandamal() {
      var v = [];
      skref.forEach(function (s) {
        if (s.el.hidden) return;
        if (s.gerd === 'texti' && s.inntak) {
          var t = trim(s.inntak.value);
          if (s.skylda && !t) v.push({ kodi: 'vantar', s: s, stopp: true, texti: s.villaTexti || ord.villa_texti, hnappur: s.vantar || ord.vantar });
          else if (t && !s.leyfaTjakn && (loppaLeyfd ? TJAKN_L : TJAKN).test(t)) v.push({ kodi: 'tjakn', s: s, stopp: true, texti: villaTjakn(), hnappur: ord.hnappur_tjakn || 'Taktu t\u00e1knin \u00fat', strax: true });
        } else if (s.gerd === 'val') {
          if (s.skylda && !valinnKostur(s)) v.push({ kodi: 'vantar', s: s, stopp: true, texti: s.villaTexti || ord.villa_val, hnappur: s.vantar || ord.vantar });
        } else if (s.gerd === 'skra') {
          if (s.skylda && skraVantar(s)) v.push({ kodi: 'skra', s: s, stopp: true, texti: '', hnappur: s.vantar || ord.vantar });
        }
      });
      var fra = kalla(adapter, 'athuga', [stada(), ctx], []) || [];
      fra.forEach(function (x) {
        v.push({ kodi: x.kodi, s: finnaSkref(x.reitur), stopp: x.stopp !== false, texti: x.texti || '', hnappur: x.hnappur || ord.vantar, strax: x.strax !== false, fyrst: !!x.fyrst });
      });
      /* CK 19.10 (QA5 P0): emoji in an adapter's own text fields stop the cart with the same words as the kit's text steps
         (villa_tjakn), unless the adapter said it already; a field or step may opt out with data-leyfa-tjakn */
      skref.forEach(function (s) {
        s.__tjaknEl = null;
        if (s.el.hidden || (s.gerd !== 'sersnid' && s.gerd !== 'vara') || s.leyfaTjakn) return;
        for (var i = 0; i < v.length; i++) if (v[i].s === s && v[i].kodi === 'tjakn') return;
        var reitur = $$('input, textarea', s.el).filter(function (x) {
          return textaReitur(x) && !x.closest('[hidden]') && !x.hasAttribute('data-leyfa-tjakn') && (loppaLeyfd ? TJAKN_L : TJAKN).test(x.value || '');
        })[0];
        if (!reitur) return;
        s.__tjaknEl = reitur;
        /* before anything else said about the same step (a length or strength note measured with the emoji in it) */
        var x = { kodi: 'tjakn', s: s, stopp: true, texti: villaTjakn(), hnappur: ord.hnappur_tjakn || 'Taktu t\u00e1knin \u00fat', strax: true }, j = 0;
        while (j < v.length && v[j].s !== s) j++;
        v.splice(j, 0, x);
      });
      var rsk = rammiSkref();
      if (rammiVantar && rsk && !rsk.el.hidden) v.push({ kodi: 'rammi-verd', s: rsk, stopp: true, texti: ord.villa_rammi_verd || ord.villa_ekki_i_bodi, hnappur: ord.ekki_i_bodi, strax: true });
      if (!afb) v.push({ kodi: 'ekki_i_bodi', s: null, stopp: true, texti: ord.villa_ekki_i_bodi, hnappur: ord.ekki_i_bodi });
      else if (!afb.til) v.push({ kodi: 'uppselt', s: null, stopp: true, texti: ord.villa_uppselt, hnappur: ord.uppselt });
      var rod = skref.map(function (s) { return s.el; });
      /* a sold-out or missing combination is said first (as the old pages did): no field can fix it */
      var stig = function (x) { return x.fyrst ? -2 : x.kodi === 'uppselt' || x.kodi === 'ekki_i_bodi' ? -1 : x.s ? rod.indexOf(x.s.el) : 999; };
      v.sort(function (a, b) { return stig(a) - stig(b); });
      return v;
    }
    /* CK 19.10 (QA9 F-4): one piece has its own word outside Icelandic ("1 pc."), else "{n} pcs." */
    function stkTexti(n) { return fylla(n === 1 && ord.stk_1 ? ord.stk_1 : ord.stk, { n: n }); }
    function syndVilla(s, texti) {
      if (!s) return;
      if (s.villaEl) {
        if (s.villaEl.textContent !== texti) s.villaEl.textContent = texti;
        s.villaEl.hidden = !texti;
      }
      var inn = s.gerd === 'texti' || s.gerd === 'magn' ? s.inntak : null;
      if (inn) { if (texti) inn.setAttribute('aria-invalid', 'true'); else inn.removeAttribute('aria-invalid'); }
      /* P2-G1 21.10 (QA20 A2): an adapter step whose first field has no error line of its own (door sign names) gets the step's
         error line in aria-describedby and aria-invalid while it shows, as the safety signs already had */
      if (!inn && s.gerd === 'sersnid' && s.villaEl && s.villaEl.id) {
        /* the field the error is about: the first shown field of the step without an error line of its own that is still empty,
           else the first shown one (door signs: the names, or the address once it is switched on) */
        var reitir = $$('[data-vs-reitur-inntak]', s.el).filter(function (x) {
          var w = x.closest ? x.closest('[data-vs-reitur]') : null;
          return x.id && !(w && w.hidden) && x.offsetParent !== null && !document.getElementById(x.id + '-v');
        });
        var r = texti ? (reitir.filter(function (x) { return !trim(x.value); })[0] || reitir[0] || null) : null;
        if (s.__villaReitur && s.__villaReitur !== r) s.__villaReitur.removeAttribute('aria-invalid');
        s.__villaReitur = r;
        if (r) {
          r.setAttribute('aria-invalid', 'true');
          var lys = String(r.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (x) { return !!x; });
          if (lys.indexOf(s.villaEl.id) < 0) { lys.push(s.villaEl.id); r.setAttribute('aria-describedby', lys.join(' ')); }
        }
      }
    }

    /* ---- live region and toast ---- */
    var tilkTimi = null, tilkBid = null, tilkFost = false;
    /* fost: a cart error whose own line is out of sight. The toast then stays until the customer acts
       (click, key, typing), sends again or scrolls the error line into view, instead of 5 s. */
    function tilkynna(texti, synileg, villa, fost) {
      if (!tilk) return;
      tilk.textContent = '';
      tilk.removeAttribute('data-synileg');
      if (villa) tilk.setAttribute('data-villa', ''); else tilk.removeAttribute('data-villa');
      clearTimeout(tilkTimi);
      clearTimeout(tilkBid);
      tilkFost = false;
      tilkBid = setTimeout(function () {
        tilk.textContent = texti || '';
        if (synileg && texti) {
          tilk.setAttribute('data-synileg', '');
          if (fost) tilkFost = true;
          else tilkTimi = setTimeout(function () { tilk.removeAttribute('data-synileg'); }, 5000);
        }
      }, 60);
    }
    /* hides a cart error toast; the live region keeps its text unless a new send empties it */
    function feltaVillu(taema) {
      if (!tilk || !tilk.hasAttribute('data-villa')) return;
      clearTimeout(tilkBid);
      clearTimeout(tilkTimi);
      tilkFost = false;
      tilk.removeAttribute('data-synileg');
      if (taema) tilk.textContent = '';
    }
    var feltaEfFost = function () { if (tilkFost) feltaVillu(); };
    document.addEventListener('click', feltaEfFost, true);
    document.addEventListener('keydown', feltaEfFost, true);
    form.addEventListener('input', feltaEfFost);

    /* ---- drawing, photos, dots ---- */
    function myndalisti() {
      var eigin = kalla(adapter, 'myndir', [ctx], null);
      if (eigin && eigin.length != null) return eigin;
      var s0 = kalla(adapter, 'mynd', [ctx], null);
      return (gogn.myndir || []).filter(function (m) { return !s0 || m.id !== s0.id; });
    }
    /* 14.10 (samraeming, eigandi: "allt a ad vera eins"): "Daemi" flaga nedst a midri myndinni medan skiltid synir synishorn. Adapterinn
       segir til med daemi(s, ctx): false = engin flaga, true = sjalfgefinn texti (ord.daemi), strengur = eigin texti. Aldrei a ljosmyndum. */
    var daemiEl = null;
    function daemiKit(st, ljos) {
      if (!rammi) return;
      var d = (slide === 0 && !ljos && lifandiSida() && typeof adapter.daemi === 'function') ? kalla(adapter, 'daemi', [st, ctx], false) : false;
      if (!d) { if (daemiEl && !daemiEl.hidden) daemiEl.hidden = true; return; }
      if (!daemiEl) {
        daemiEl = document.createElement('span');
        daemiEl.className = 'vs-daemi';
        daemiEl.setAttribute('data-vs-daemi', '');
        daemiEl.hidden = true;
        rammi.appendChild(daemiEl);
      }
      var tx = typeof d === 'string' ? d : (ord.daemi || 'Dæmi, skrifaðu þinn texta');
      if (daemiEl.textContent !== tx) daemiEl.textContent = tx;
      if (daemiEl.hidden) daemiEl.hidden = false;
    }
    function teikna() {
      ctx.afbrigdi = afb;
      var st = stada();
      var listi = myndalisti();
      if (slide > listi.length) slide = 0;
      var ljos = false;
      if (slide === 0) {
        var fyl = typeof adapter.fylla === 'function' ? kalla(adapter, 'fylla', [st, ctx], false) : adapter.fylla;
        var sku = typeof adapter.skuggi === 'function' ? kalla(adapter, 'skuggi', [st, ctx], true) : adapter.skuggi;
        if (fyl) teikning.setAttribute('data-fylla', ''); else teikning.removeAttribute('data-fylla');
        kalla(adapter, 'teikna', [ctx, st], null);
        teikning.setAttribute('aria-label', kalla(adapter, 'lysing', [st, ctx], gogn.vara.titill) || gogn.vara.titill);
        teikning.setAttribute('data-skuggi', sku === false ? 'nei' : 'ja');
        /* ljósmyndasíður (engin lifandi teikning): fyrsta myndin er ljósmynd og fyllir sviðið líka */
        var m0 = teikning.firstElementChild;
        if (!lifandiSida() && m0 && m0.tagName === 'IMG' && m0.hasAttribute('data-vs-mynd')) { ljos = true; m0.alt = myndAlt(kalla(adapter, 'mynd', [ctx], null), 1); }
      } else {
        var m = listi[slide - 1];
        teikning.removeAttribute('data-fylla');
        setjaMynd(teikning, m, gogn.vara.titill);
        var mAlt = myndAlt(m, myndNr(slide));
        var mEl = $('img[data-vs-mynd]', teikning);
        if (mEl) mEl.alt = mAlt;
        teikning.setAttribute('aria-label', mAlt);
        teikning.setAttribute('data-skuggi', 'ja');
        ljos = !!mEl;
      }
      /* eigandi 07.10: ljósmynd fyllir sviðið (object-fit cover í sama ramma, ekkert hoppar) */
      if (rammi) { if (ljos) rammi.setAttribute('data-ljosmynd', ''); else rammi.removeAttribute('data-ljosmynd'); }
      var mb = slide === 0 ? kalla(adapter, 'malband', [st, ctx], null) : null;
      if (malbandEl) {
        malbandEl.hidden = !mb;
        if (mb) $('[data-vs-malband-texti]', malbandEl).textContent = mb.texti || (String(mb.b).replace('.', ',') + ' \u00d7 ' + String(mb.h).replace('.', ',') + ' ' + (mb.eining || 'cm'));
      }
      daemiKit(st, ljos);
      teiknaPunkta(listi.length + 1);
      teiknaSmamyndir(listi);
    }
    /* ---- smámyndaröðin undir sviðinu (eigandi 07.10): teikningin fyrst ("Þín hönnun"), svo vörumyndirnar ---- */
    function lifandiSida() { return !!(merkiEl && !merkiEl.hidden); }
    function myndFyrst() { var m0 = lifandiSida() ? null : kalla(adapter, 'mynd', [ctx], null); return m0 && m0.src ? m0 : null; }
    /* númer myndar (1 = fyrsta ljósmynd): á ljósmyndasíðum er fyrsta skyggnan sjálf ljósmynd */
    function myndNr(n) { return myndFyrst() ? n + 1 : n; }
    /* alt: textinn úr Shopify, annars vöruheiti og númer (alt-textum í Shopify er ekki breytt) */
    function myndAlt(m, n) {
      var t = gogn.vara.titill, a = m && m.alt ? trim(m.alt) : '';
      if (a && a !== t) return a;
      return fylla(ord.mynd_alt || '{titill}, mynd {n}', { titill: t, n: n || 1 });
    }
    var smaSig = '';
    function smaSlod(m) { return (m && (m.s2 || m.s6 || m.src)) || ''; }
    function teiknaSmamyndir(listi) {
      if (!smaRadir.length) return;
      var m0 = myndFyrst(), alls = listi.length + 1;
      if (alls < 2) {
        if (smaSig !== '0') { smaSig = '0'; smaRadir.forEach(function (r) { r.hidden = true; r.innerHTML = ''; }); rot.removeAttribute('data-smamyndir'); }
        return;
      }
      var sig = (m0 ? smaSlod(m0) : 'L') + '|' + listi.map(function (m) { return m ? (m.id || m.src) : ''; }).join(',');
      if (sig !== smaSig) {
        smaSig = sig;
        var mynd = function (m, i, n) {
          return '<button type="button" class="vs-smamynd" data-vs-slide="' + i + '" tabindex="-1"><img src="' + esc(smaSlod(m)) + '" alt="' + esc(myndAlt(m, n)) + '" width="60" height="60" loading="lazy" decoding="async"></button>';
        };
        var html = m0 ? mynd(m0, 0, 1)
          : '<button type="button" class="vs-smamynd vs-smamynd--lifandi" data-vs-slide="0" tabindex="-1"><i aria-hidden="true"></i><span>' + esc(ord.thin_honnun || '') + '</span></button>';
        listi.forEach(function (m, i) { html += mynd(m, i + 1, myndNr(i + 1)); });
        smaRadir.forEach(function (r) { r.innerHTML = html; r.hidden = false; });
        rot.setAttribute('data-smamyndir', '');
      }
      smaRadir.forEach(function (r) {
        var virk = null;
        $$('.vs-smamynd', r).forEach(function (b, i) {
          var nu = i === slide;
          b.setAttribute('aria-current', nu ? 'true' : 'false');
          b.tabIndex = nu ? 0 : -1;
          if (nu) virk = b;
        });
        /* virka smámyndin sést í röðinni (aðeins röðin skrunast, aldrei síðan) */
        if (virk && r.offsetParent !== null) {
          var vl = virk.offsetLeft, vr = vl + virk.offsetWidth;
          if (vl < r.scrollLeft + 4) r.scrollLeft = Math.max(0, vl - 16);
          else if (vr > r.scrollLeft + r.clientWidth - 4) r.scrollLeft = vr - r.clientWidth + 16;
        }
      });
    }
    smaRadir.forEach(function (r) {
      r.addEventListener('click', function (e) {
        var b = e.target.closest ? e.target.closest('[data-vs-slide]') : null;
        if (b && r.contains(b)) synaSlide(parseInt(b.getAttribute('data-vs-slide'), 10) || 0);
      });
      /* lyklaborð: eitt stopp í röðinni, örvar, Home og End velja mynd */
      r.addEventListener('keydown', function (e) {
        var takkar = $$('.vs-smamynd', r), i = takkar.indexOf(document.activeElement), k = e.key, n = null;
        if (i < 0) return;
        if (k === 'ArrowRight') n = i + 1;
        else if (k === 'ArrowLeft') n = i - 1;
        else if (k === 'Home') n = 0;
        else if (k === 'End') n = takkar.length - 1;
        if (n === null) return;
        e.preventDefault();
        n = (n + takkar.length) % takkar.length;
        synaSlide(n);
        var b = $$('.vs-smamynd', r)[n];
        if (b) b.focus();
      });
    });
    /* stjörnurnar undir fyrirsögninni fara niður í umsagnirnar (eigandi 07.10); href="#umsagnir" virkar án JS */
    var umsTengill = $('[data-vs-umsagnir]', rot);
    if (umsTengill) {
      var umsMark = document.getElementById('umsagnir');
      if (!umsMark) {
        /* sniðmát án umsagnakafla: engin umsögn til að sýna, tengillinn hverfur með stjörnunum (kökutoppur, 15.10) */
        umsTengill.removeAttribute('href');
        umsTengill.removeAttribute('title');
        var umsRod = umsTengill.closest ? umsTengill.closest('.vs-einkunn') : null;
        if (umsRod) umsRod.style.display = 'none';
      } else {
        umsTengill.addEventListener('click', function (e) {
          if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          /* umsagnirnar eru fyrir neðan síðukerfið, þar fylgir sviðið ekki lengur: aðeins fasti hausinn dregst frá */
          scrollTil(umsMark.getBoundingClientRect().top + (window.pageYOffset || 0) - (parseFloat(rot.style.getPropertyValue('--vs-fast')) || 0) - 16);
          if (!umsMark.hasAttribute('tabindex')) umsMark.setAttribute('tabindex', '-1');
          try { umsMark.focus({ preventScroll: true }); } catch (e2) {}
        });
      }
    }
    var PUNKTAR_MEST = 6;
    function teiknaPunkta(alls) {
      if (!punktar) return;
      if (alls < 2) { punktar.hidden = true; punktar.innerHTML = ''; return; }
      /* many photos (keyrings have 12): back, "n / all", forward instead of a dot each, so the row never runs
         over the ruler on a phone */
      if (alls > PUNKTAR_MEST) {
        if (!punktar.hasAttribute('data-teljari')) {
          punktar.setAttribute('data-teljari', '');
          punktar.innerHTML = '<button type="button" data-vs-slide-skref="-1" aria-label="' + esc(ord.fyrri_mynd || '') + '">\u2039</button><span class="vs-punktar-tala" aria-live="polite"></span><button type="button" data-vs-slide-skref="1" aria-label="' + esc(ord.naesta_mynd || '') + '">\u203a</button>';
        }
        var tala = $('.vs-punktar-tala', punktar), tt = fylla(ord.mynd_af || '{n} / {alls}', { n: slide + 1, alls: alls });
        if (tala.textContent !== tt) tala.textContent = tt;
        punktar.hidden = false;
        return;
      }
      if (punktar.hasAttribute('data-teljari')) { punktar.removeAttribute('data-teljari'); punktar.innerHTML = ''; }
      if (punktar.children.length !== alls) {
        punktar.innerHTML = '';
        for (var i = 0; i < alls; i++) {
          var b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('data-vs-slide', String(i));
          b.setAttribute('aria-label', fylla(ord.mynd, { n: i + 1, alls: alls }));
          punktar.appendChild(b);
        }
      }
      $$('button', punktar).forEach(function (b, i) { b.setAttribute('aria-current', i === slide ? 'true' : 'false'); });
      punktar.hidden = false;
    }
    function synaSlide(n) {
      var alls = myndalisti().length + 1;
      slide = (n + alls) % alls;
      teikna();
    }
    if (punktar) punktar.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-vs-slide]') : null;
      if (b) synaSlide(parseInt(b.getAttribute('data-vs-slide'), 10) || 0);
      var k = e.target.closest ? e.target.closest('[data-vs-slide-skref]') : null;
      if (k) synaSlide(slide + (parseInt(k.getAttribute('data-vs-slide-skref'), 10) || 1));
    });
    if (rammi) {
      var snertX = null, snertY = null;
      rammi.addEventListener('pointerdown', function (e) { snertX = e.clientX; snertY = e.clientY; });
      rammi.addEventListener('pointerup', function (e) {
        if (snertX === null) return;
        var dx = e.clientX - snertX, dy = e.clientY - snertY;
        snertX = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5 && myndalisti().length) synaSlide(slide + (dx < 0 ? 1 : -1));
      });
    }

    /* ---- wall (owner rule: light material on the darker wall, dark on the
       lighter). The adapter may choose; otherwise the variant's colour swatch
       decides (afb.vg, worked out in Liquid), otherwise the default wall. ---- */
    function veggur() {
      if (!rammi || !gogn.veggur || gogn.veggur.stilling !== 'sjalfvirkt' || !gogn.veggur.nytt) return;
      var v = kalla(adapter, 'veggur', [stada(), ctx], null) || (afb && afb.vg) || gogn.veggur.sjalf || 'dokkur';
      if (!gogn.veggur[v] || rammi.getAttribute('data-veggur') === v) return;
      rammi.setAttribute('data-veggur', v);
      veggSetja(v);
    }
    /* CS 19.10 (hraði umferð 2): a phone (< 800 px) gets the 900 px wall (gogn.veggur[v + '_s'], 23-53 KB instead of 135-156 KB),
       the same split as the <style> in sm-vel.liquid; a computer keeps the big wall. Follows the width when it crosses 800 px. */
    var veggMq = window.matchMedia ? window.matchMedia('(max-width: 799px)') : null, veggSidast = null;
    function veggSetja(v) {
      veggSidast = v;
      var u = (veggMq && veggMq.matches && gogn.veggur[v + '_s']) || gogn.veggur[v];
      rot.style.setProperty('--vs-veggur', 'url(' + u + ')');
    }
    if (veggMq && gogn.veggur) {
      var veggBreyting = function () { if (veggSidast) veggSetja(veggSidast); };
      if (veggMq.addEventListener) veggMq.addEventListener('change', veggBreyting); else if (veggMq.addListener) veggMq.addListener(veggBreyting);
    }

    /* ================= samraeming (Kjarni v1) =================
       Tiles (C3): every [data-vs-smamynd] box in a step is a small picture of the customer's own sign with
       that tile's value, drawn by adapter.utlit.teikna. Letur tiles (C4, [data-vs-letur]) show the
       customer's text in each font on the chosen material. The inputs and labels are never rebuilt here:
       only the inside of the picture boxes changes, so focus and the checked state stay. Drawn lazily
       (IntersectionObserver 300 px ahead), the checked tile first, in idle slots, cached (LRU of 64 per
       step, key = the look fields), skipped when unchanged (data-sig). The frame (C6) and the bar total
       with the bulk discount (C8) live here too. */
    var UT = adapter.utlit && typeof adapter.utlit === 'object' ? adapter.utlit : null;
    var LT = adapter.letur && typeof adapter.letur === 'object' ? adapter.letur : null;
    var samGen = 0, flisEpoch = 0, flisSkrif = 0, flisTeiknad = 0, flisRod = [], flisVinna = false;
    var flisT = { utlit: null, letur: null }, flisIo = null, flisCache = {};
    var simiMidill = window.matchMedia ? window.matchMedia('(max-width: 760px)') : null;
    var magnEl = $('[data-sm-magn="vara"]', rot), verdMerki = $$('[data-vs-verd-merki]', rot);
    var rammiSnert = false, rammiVantar = false, rammiVarad = false, flisVarad = {}, rammiOsk = '';
    var leturMap = {}, leturVarad = {};
    if (adapter.skuggi === false) rot.setAttribute('data-vs-flis-skuggi', 'nei');
    /* FI 21.10: leturMap = stílheiti sem framleiðslan (körfueiginleiki, data-nafn) les og eru alltaf íslensk (ord.letur_heiti_is, annars ord.letur_heiti);
       leturMapSyn = heitið sem sést (á ensku á /en, ord.letur_heiti). Flísar sýna Syn en hafa data-nafn úr leturMap. */
    var leturMapSyn = {};
    String(ord.letur_heiti || '').split('|').forEach(function (p) { var i = p.indexOf('='); if (i > 0) leturMapSyn[trim(p.slice(0, i))] = trim(p.slice(i + 1)); });
    String(ord.letur_heiti_is || ord.letur_heiti || '').split('|').forEach(function (p) { var i = p.indexOf('='); if (i > 0) leturMap[trim(p.slice(0, i))] = trim(p.slice(i + 1)); });
    function vidvorun(lyk, t) { if (flisVarad[lyk]) return; flisVarad[lyk] = 1; try { if (window.console) console.warn('SMVelSida: ' + t); } catch (e) {} }
    /* C5: the style name of a font, from the one shop list; never the real name */
    function leturHeiti(id, n) {
      var k = leturLykill(id);
      if (leturMap[k]) return leturMap[k];
      if (!leturVarad[k]) { leturVarad[k] = 1; vidvorun('letur-' + k, 'no style name for font "' + k + '" (add it to letur_heiti in sm-sida-ord)'); }
      return fylla(ord.letur_nr || '', { n: n || 1 });
    }
    function leturHeitiSyn(id, n) { var k = leturLykill(id); return leturMapSyn[k] || leturHeiti(id, n); }
    function utlitSida() { return !!$('.vs-val--utlit:not(.vs-val--rammi)', form); }
    /* html.kostir for adapters: letur tiles get their style names from the shop list */
    function kostirSidu(o) {
      if (o && o.birting === 'letur') {
        (o.kostir || []).forEach(function (k, i) {
          if (k.nafn == null) k.nafn = leturHeiti(k.letur || k.gildi, i + 1);
          if (!k.heiti) k.heiti = leturHeitiSyn(k.letur || k.gildi, i + 1);
          if (!k.letur) k.letur = k.gildi;
        });
        if (o.synishorn == null) o.synishorn = ord.letur_synishorn || '';
      }
      return kostirHtml(o);
    }

    /* ---- the frame (C6) ---- */
    function rammiSkref() { for (var i = 0; i < skref.length; i++) if (skref[i].gerd === 'rammi') return skref[i]; return null; }
    function rammiUppl(s) {
      if (!s) return null;
      if (s.el.__vsR) return s.el.__vsR;
      var el = s.el, g = {};
      String(el.getAttribute('data-gildi') || '').split('|').forEach(function (l) { var i = l.indexOf('='); if (i > 0) g[trim(l.slice(0, i))] = trim(l.slice(i + 1)); });
      el.__vsR = { sjalf: el.getAttribute('data-sjalfgefid') === 'an' ? 'an' : 'med', malm: el.getAttribute('data-sjalfgefid-malmur') === 'med' ? 'med' : 'an',
        p: parseInt(el.getAttribute('data-valkostur'), 10) || 0, gildi: g };
      return el.__vsR;
    }
    /* whether the frame step shows this round (a plate, the frame sellable, its condition and the adapter's rule) */
    function rammiSynist(s) {
      return !!s.el.__vsGeo && !(FALIN_OVIRK && s.el.__vsEkki) && (!s.kveikja.length || aVid(s)) &&
        (typeof adapter.syna !== 'function' || kalla(adapter, 'syna', [s.lykill, stada(), ctx], undefined) !== false);
    }
    function rammiGildi() { var s = rammiSkref(); if (!s || s.el.hidden) return ''; var r = valinnKostur(s); return r ? r.value : ''; }
    function setjaRammiGildi(s, g) { s.radio.forEach(function (r) { r.checked = r.value === g; }); }
    /* the adapter's geometry for the current material and shape; null = no plate (step hidden) */
    function rammiGerd(st) {
      if (typeof adapter.rammi !== 'function') return { gerd: 'lina' };
      var g = kalla(adapter, 'rammi', [st, ctx], null);
      return g && g.gerd ? g : null;
    }
    /* weight of an option when the kit looks for the closest variant: a frame option (the block's own or a
       composite one) weighs 0.1, so a frame choice never moves the material or the size; others 1 */
    function rammiVigt(j) {
      var R = rammiUppl(rammiSkref()), ss = stillingar.samsett || [];
      if (R && R.p && j === R.p - 1) return 0.1;
      for (var i = 0; i < ss.length; i++) if ((parseInt(ss[i].valkostur, 10) || 0) - 1 === j) return 0.1;
      return 1;
    }
    function erMalmur(g) { return !!g && (g.gerd === 'malmur' || g.verd === 'malmur'); }
    function kodiFyrir(o, leit) {
      if (!o || leit == null || leit === '') return null;
      for (var i = 0; i < o.gildi.length; i++) if (o.gildi[i].kodi === leit || o.gildi[i].nafn === leit) return o.gildi[i].kodi;
      return null;
    }
    /* the Shopify options that carry the frame's price for product v: [{ p0, med, an }] (codes). The block's
       own option (valkostur + gildi) and composite options (adapter_stillingar.samsett: one option shared
       with other steps, e.g. "Logo og rammi"; kodar maps "value|value" of its steps to the option value) */
    function rammiValkostir(v, st) {
      var R = rammiUppl(rammiSkref()), ut = [];
      if (R && R.p > 0) {
        var o = v.valkostir[R.p - 1], m = kodiFyrir(o, R.gildi.med), a = kodiFyrir(o, R.gildi.an);
        if (m || a) ut.push({ p0: R.p - 1, med: m, an: a });
      }
      (stillingar.samsett || []).forEach(function (x) {
        var p0 = (parseInt(x.valkostur, 10) || 0) - 1;
        if (p0 < 0 || !x.skref || !x.kodar || !v.valkostir[p0]) return;
        /* each step's code: the kit state (option codes, list values), or a sersnid step's skref[l].kodi(s, ctx) */
        var lyk = function (rg) { return x.skref.map(function (l) {
          if (l === 'rammi') return rg;
          var g = st[l], sk = adapter.skref && adapter.skref[l];
          if ((g == null || g === '') && sk && typeof sk.kodi === 'function') g = kalla(sk, 'kodi', [st, ctx], '');
          return String(g == null ? '' : g);
        }).join('|'); };
        var m2 = kodiFyrir(v.valkostir[p0], x.kodar[lyk('med')]), a2 = kodiFyrir(v.valkostir[p0], x.kodar[lyk('an')]);
        if (m2 || a2) ut.push({ p0: p0, med: m2, an: a2 });
      });
      return ut;
    }
    /* before the variant is shown: the default for this material until the customer chooses (metal has
       its own default), the frame's option value in the variant (sparse: on a free material without a
       "med" variant the "an" variant is used and Rammi still says Med ramma; on metal a missing "med"
       variant is never sold: stop rammi-verd), and the sub lines Okeypis / +1.000 kr */
    function rammiForval() {
      var s = rammiSkref();
      if (!s || rammiSnert) return;
      var R = rammiUppl(s);
      setjaRammiGildi(s, erMalmur(rammiGerd(stada())) ? R.malm : R.sjalf);
    }
    function rammiSamstilla(adValdi) {
      rammiVantar = false;
      var s = rammiSkref();
      if (!s && !(stillingar.samsett && stillingar.samsett.length)) return;
      var st = stada(), g = s ? rammiGerd(st) : null, malm = erMalmur(g), R = rammiUppl(s);
      if (s) {
        s.el.__vsGeo = g;
        if (!rammiSnert) setjaRammiGildi(s, malm ? R.malm : R.sjalf);
        if (!valinnKostur(s)) setjaRammiGildi(s, R.sjalf);
      }
      var gildi = s && g ? (valinnKostur(s) || {}).value || 'an' : 'an';
      st = stada();
      var tar = rammiValkostir(nuVara(), st);
      var engin = !!(s && g && malm && !tar.some(function (t) { return !!t.med; }));
      if (engin && gildi === 'med') rammiVantar = true;
      if (!adValdi) tar.forEach(function (t) {
        var k = kodar.slice(), a = null, vil = gildi === 'med' ? t.med : t.an;
        if (vil) { k[t.p0] = vil; a = finna(k); }
        if (!a && gildi === 'med') {
          if (malm) { rammiVantar = true; return; }
          if (t.an) { k[t.p0] = t.an; a = finna(k); }
        }
        if (a) { afb = a; kodar = a.o.slice(); }
      });
      if (!s) return;
      var dM = 0, ekki = engin;
      tar.forEach(function (t) {
        var k = kodar.slice(), aM = null, aA = null;
        if (t.med) { k[t.p0] = t.med; aM = finna(k); }
        if (t.an) { k[t.p0] = t.an; aA = finna(k); }
        if (aM && aA) dM += aM.verd - aA.verd;
        else if (!aM && malm) ekki = true;
      });
      if (adValdi && gildi === 'med' && ekki) rammiVantar = true;
      if (ekki && gildi === 'med') { if (rammiSnert) rammiOsk = 'med'; rammiVantar = false; setjaRammiGildi(s, 'an'); return 'aftur'; }
      if (!ekki && rammiOsk === 'med' && gildi === 'an' && g) { rammiOsk = ''; setjaRammiGildi(s, 'med'); return 'aftur'; }
      s.el.__vsEkki = !!ekki;
      if (dM && dM !== 100000 && !rammiVarad) { rammiVarad = true; vidvorun('rammi-verd', 'frame price difference ' + dM + ' aurar, expected 100000 (1.000 kr)'); }
      s.radio.forEach(function (r) {
        var u = $('[data-vs-undir]', r.parentNode), t = '';
        if (r.value === 'med') {
          t = ekki ? ord.ekki_i_bodi || '' : dM > 0 ? '+' + kr(dM) : ord.rammi_okeypis || '';
          r.disabled = s.el.hidden || ekki;
        }
        if (u && u.textContent !== t) u.textContent = t;
      });
    }
    /* the frame value a choice would get: the customer's own choice, or while untouched the default of
       the material class that choice lands on (metal has its own default) */
    function rammiFyrir(st2) {
      var s = rammiSkref();
      if (!s) return '';
      if (rammiSnert) { var r = valinnKostur(s); return r ? r.value : ''; }
      var R = rammiUppl(s), g = rammiGerd(st2);
      return !g ? 'an' : erMalmur(g) ? R.malm : R.sjalf;
    }
    /* variant a of product v with its frame option(s) set to rg (sparse: no "med" variant keeps a) */
    function medRamma(v, a, rg, st2) {
      if (!a || !v) return a;
      var k = a.o.slice(), breytt = false;
      rammiValkostir(v, st2).forEach(function (t) { var vil = rg === 'med' ? t.med : t.an; if (vil && k[t.p0] !== vil) { k[t.p0] = vil; breytt = true; } });
      if (!breytt) return a;
      var f = null, lk = k.join('\u0001');
      v.afbrigdi.forEach(function (x) { if (!f && x.o.join('\u0001') === lk) f = x; });
      return f || a;
    }
    /* the variant a tile lands on, with the frame the customer will get there (prices under tiles) */
    function flisAfb(a, k, grunn) {
      if (!a || rammiSnert || !rammiSkref()) return a;
      var st2 = {}, x;
      for (x in grunn) if (grunn.hasOwnProperty(x)) st2[x] = grunn[x];
      st2.afbrigdi = a;
      st2.kodar = k;
      var rg = rammiFyrir(st2);
      return rg ? medRamma(st2.voruGogn || nuVara(), a, rg, st2) : a;
    }
    /* the visible list for the cart: Rammi is always sent while its step shows (also into an adapter's
       own list, E8), and the hidden _letur when the adapter asks for the real font id (letur.falid) */
    function leturValid() {
      var id = '';
      $$('[data-vs-letur]', form).forEach(function (box) {
        if (id) return;
        var lab = box.closest ? box.closest('label') : null, inn = lab ? $('input', lab) : null;
        if (inn && inn.checked && !inn.disabled) id = inn.getAttribute('data-letur') || inn.value;
      });
      return id;
    }
    function eigRammiLetur(listi, erListi) {
      var rs = rammiSkref();
      if (erListi && rs && !rs.el.hidden && rs.eig) {
        var er = listi.some(function (x) { return x && x[0] === rs.eig; }), rr = valinnKostur(rs);
        if (!er && rr) {
          var nyr = [rs.eig, rr.getAttribute('data-nafn') || ''], ix = -1;
          if (adapter.rammiEftir) for (var li = 0; li < listi.length; li++) if (listi[li] && listi[li][0] === adapter.rammiEftir) ix = li;
          listi = ix >= 0 ? listi.slice(0, ix + 1).concat([nyr], listi.slice(ix + 1)) : listi.concat([nyr]);
        }
      }
      var falid = LT ? (typeof LT.falid === 'function' ? kalla(LT, 'falid', [stada(), ctx], false) : LT.falid) : false;
      if (falid) {
        var lid = leturValid();
        if (lid && !listi.some(function (x) { return x && x[0] === '_letur'; })) listi = listi.concat([['_letur', lid]]);
      }
      return listi;
    }

    /* ---- a product of the family by tile codes: o = codes by option position ('' = any); keeps the
       customer's other choices by name where the product has them and the frame choice. synd: the variant a
       tile SHOWS (choices held first, so a sold out size says Uppselt); else the one a click selects
       (available first, as veljaValkost) ---- */
    function veljaIVoru(v, o, synd) {
      var best = null, bestStig = -1, nuN = afb && afb.n ? afb.n : [];
      var R = rammiUppl(rammiSkref()), rg = rammiGildi() || 'an';
      var rNafn = R && R.p ? R.gildi[rg] : null;
      v.afbrigdi.forEach(function (a) {
        for (var i = 0; i < o.length; i++) if (o[i] && a.o[i] !== String(o[i])) return;
        var stig = a.til ? (synd ? 1 : 1000) : 0;
        for (var j = 0; j < a.n.length; j++) if (nuN[j] != null && a.n[j] === nuN[j]) stig += 10 * rammiVigt(j);
        if (rNafn && (a.n[R.p - 1] === rNafn || a.o[R.p - 1] === rNafn)) stig += 5;
        if (stig > bestStig) { best = a; bestStig = stig; }
      });
      return best;
    }
    /* price, sold out and not offered under cross product tiles ([data-vs-vara]) */
    function verdVoruFlisa() {
      if (!fjol) return;
      var hopar = [];
      $$('input[data-vs-vara]', form).forEach(function (inn) {
        var c = inn.closest ? inn.closest('[data-vs-utlit]') : null;
        if (!c) return;
        if (!c.__vsHopur) { c.__vsHopur = []; hopar.push(c); }
        c.__vsHopur.push(inn);
      });
      var grunn = stada();
      hopar.forEach(function (c) {
        var listi = c.__vsHopur, verd = [], olik = false, s = null;
        c.__vsHopur = null;
        for (var i = 0; i < skref.length; i++) if (skref[i].el.contains(c)) { s = skref[i]; break; }
        listi.forEach(function (inn) {
          var a = s ? flisStada({ s: s, inn: inn }, grunn).afbrigdi : null;
          inn.__vsAfb = a;
          if (a && a.til) { if (verd.length && verd[0] !== a.verd) olik = true; verd.push(a.verd); }
        });
        listi.forEach(function (inn) {
          var a = inn.__vsAfb, t = !a ? ord.ekki_i_bodi || '' : !a.til ? ord.uppselt || '' : olik ? kr(a.verd) : '';
          var v = vorur[inn.getAttribute('data-vs-vara')], o = String(inn.getAttribute('data-vs-o') || '').split(','), einhver = false;
          if (v) v.afbrigdi.forEach(function (x) { if (einhver || !x.til) return; for (var i = 0; i < o.length; i++) if (o[i] && x.o[i] !== o[i]) return; einhver = true; });
          if (!einhver && !adapter.veljaUppselt) inn.disabled = true;
          var u = $('[data-vs-undir]', inn.parentNode);
          if (u && u.textContent !== t) u.textContent = t;
        });
      });
    }

    /* ---- the tiles ---- */
    function flisaListi() {
      var ut = [], tala = {};
      $$('[data-vs-smamynd], [data-vs-letur]', form).forEach(function (box) {
        var lab = box.closest ? box.closest('label') : null, inn = lab ? $('input', lab) : null;
        if (!inn) return;
        var s = null;
        for (var i = 0; i < skref.length; i++) if (skref[i].el.contains(box)) { s = skref[i]; break; }
        if (!s) return;
        var letur = box.hasAttribute('data-vs-letur');
        var n = tala[s.lykill] = (tala[s.lykill] || 0) + 1;
        ut.push({ box: box, inn: inn, s: s, letur: letur, n: n, teg: letur ? 'letur' : 'utlit' });
      });
      return ut;
    }
    function flisFylgjast(listi) {
      if (!('IntersectionObserver' in window)) return;
      if (!flisIo) flisIo = new IntersectionObserver(function (ff) {
        var nytt = false;
        ff.forEach(function (f) { if (f.isIntersecting && !f.target.__vsSyn) nytt = true; f.target.__vsSyn = f.isIntersecting; });
        if (nytt) flisarTeikna(0);
      }, { rootMargin: '300px 0px' });
      listi.forEach(function (x) { if (!x.s.el.__vsObs) { x.s.el.__vsObs = true; flisIo.observe(x.s.el); } });
    }
    /* flisarTeikna(bid, tegund): redraw the tiles of one kind ('letur' | 'utlit') or both, after bid ms */
    function flisarTeikna(bid, tegund) {
      (tegund ? [tegund] : ['utlit', 'letur']).forEach(function (t) {
        clearTimeout(flisT[t]);
        flisT[t] = setTimeout(function () { flisT[t] = null; flisByrja(t); }, bid || 0);
      });
    }
    /* typing may change what the adapter says about the plate (adapter.rammi): the frame step follows */
    function rammiAthuga() {
      var s = rammiSkref();
      if (!s) return false;
      var g = rammiGerd(stada());
      if (JSON.stringify(g || null) === JSON.stringify(s.el.__vsGeo || null)) return false;
      samstilla(false);
      return true;
    }
    function flisByrja(t) {
      if (t === 'utlit' && rammiAthuga()) return;
      var listi = flisaListi();
      if (!listi.length) return;
      flisFylgjast(listi);
      var syn = function (x) { return !x.s.el.hidden && (!flisIo || x.s.el.__vsSyn === true); };
      var min = listi.filter(function (x) { return x.teg === t && syn(x); });
      /* a new state replaces the queue of its kind: stale work stops */
      flisRod = flisRod.filter(function (x) { return x.teg !== t; })
        .concat(min.filter(function (x) { return x.inn.checked; }), min.filter(function (x) { return !x.inn.checked; }));
      flisKeyra();
    }
    function flisKeyra() {
      if (flisVinna || !flisRod.length) return;
      flisVinna = true;
      var lota = function (dl) {
        var grunn = stada(), t0 = Date.now();
        do {
          var x = flisRod.shift();
          if (!x) break;
          if (!form.contains(x.box)) continue;
          try { teiknaFlis(x, grunn); } catch (e) { vidvorun('flis', 'tile ' + x.s.lykill + ': ' + (e && e.message)); }
        } while (flisRod.length && (dl && typeof dl.timeRemaining === 'function' ? dl.timeRemaining() > 6 : Date.now() - t0 < 8));
        flisVinna = false;
        if (flisRod.length) flisKeyra();
      };
      if (window.requestIdleCallback) window.requestIdleCallback(lota, { timeout: 1200 });
      else setTimeout(lota, 30);
    }
    /* the page state with one tile's value applied (as sm-herbergi.js did for its tiles) */
    function flisStada(x, grunn) {
      var s = x.s, inn = x.inn, g = inn.value, s2 = {};
      for (var k in grunn) if (grunn.hasOwnProperty(k)) s2[k] = grunn[k];
      s2.kodar = grunn.kodar.slice();
      if (s.gerd === 'valkostur') {
        var p0 = s.p - 1, q0 = s.p2 ? s.p2 - 1 : -1, hl = q0 >= 0 ? String(g).split('|') : [g];
        var kk = kodar.slice();
        kk[p0] = hl[0];
        if (q0 >= 0) kk[q0] = hl[1];
        var a = finna(kk) || naestaAfb(kk, q0 >= 0 ? [p0, q0] : [p0]);
        s2.kodar = a ? a.o.slice() : kk;
        s2.afbrigdi = a;
      }
      var vh = inn.getAttribute('data-vs-vara');
      if (vh && fjol && vorur[vh]) {
        var v = vorur[vh], a2 = veljaIVoru(v, String(inn.getAttribute('data-vs-o') || '').split(','), true);
        s2.vara = { id: v.id, handle: v.handle, titill: v.titill, url: v.url };
        s2.voruGogn = v;
        s2.afbrigdi = a2;
        s2.kodar = a2 ? a2.o.slice() : [];
      }
      /* while the frame is untouched, a tile shows the frame (and price) that material gets by default */
      if (s.gerd !== 'rammi' && rammiSkref()) {
        var rg = rammiFyrir(s2);
        if (rg) {
          s2.rammi = rg;
          var a3 = medRamma(s2.voruGogn || nuVara(), s2.afbrigdi, rg, s2);
          if (a3 && a3 !== s2.afbrigdi) { s2.afbrigdi = a3; s2.kodar = a3.o.slice(); }
        }
      }
      if (s.gerd === 'rammi') s2.rammi = g;
      s2[s.lykill] = g;
      return s2;
    }
    /* the default key: the look fields of the kit's own state; adapters that keep state in sersnid steps
       must give utlit.lykill / letur.lykill (without it their tiles are redrawn after every sync) */
    var harSersnid = skref.some(function (s) { return s.gerd === 'sersnid' && !!(adapter.skref && adapter.skref[s.lykill]); });
    function sjalfLykill(s2) {
      var o = { v: s2.vara && s2.vara.handle, a: s2.afbrigdi ? s2.afbrigdi.id : null, k: s2.kodar, r: s2.rammi || '' };
      skref.forEach(function (s) { if (s.gerd === 'magn' || s.gerd === 'aths' || s.gerd === 'skra') return; o['_' + s.lykill] = s2[s.lykill]; });
      if (harSersnid) o.__ = samGen;
      return JSON.stringify(o);
    }
    function cacheFa(l, k) { var c = flisCache[l]; return c && c.k.hasOwnProperty(k) ? c.k[k] : undefined; }
    function cacheSetja(l, k, v) {
      var c = flisCache[l] || (flisCache[l] = { k: {}, rod: [] });
      if (!c.k.hasOwnProperty(k)) c.rod.push(k);
      c.k[k] = v;
      while (c.rod.length > 64) delete c.k[c.rod.shift()];
    }
    /* adapter result -> tile markup; null = keep the fallback picture */
    function flisMarkup(r, o) {
      if (!r) return null;
      var h = null;
      if (r.svg) h = einkvaemId(String(r.svg), o.id).replace(/<title>[\s\S]*?<\/title>/g, '').replace(/<svg\b(?![^>]*aria-hidden)/, '<svg aria-hidden="true" focusable="false"');
      else if (r.html) h = einkvaemId(String(r.html), o.id);
      else if (r.src) {
        if (r.klippa && r.mal) h = '<svg viewBox="' + r.klippa.join(' ') + '" preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false"><image href="' + esc(r.src) + '" x="0" y="0" width="' + r.mal[0] + '" height="' + r.mal[1] + '"/></svg>';
        else h = '<img src="' + esc(r.src) + '" alt="" decoding="async">';
      }
      if (h && h.length > 40000) { vidvorun('stor-' + o.lykill, 'tile over 40 KB in step ' + o.lykill + ': fallback picture used'); return null; }
      return h;
    }
    function setjaFlis(box, html) {
      if (box.__vsVara == null) { box.__vsVara = box.innerHTML; box.__vsHtml = box.__vsVara; }
      var nytt = html == null ? box.__vsVara : html;
      if (box.__vsHtml !== nytt) { box.innerHTML = nytt; box.__vsHtml = nytt; flisSkrif += 1; }
      if (html == null) box.removeAttribute('data-teiknad'); else box.setAttribute('data-teiknad', '');
    }
    /* letur tiles: the customer's first line, emoji stripped; the sample when empty */
    function leturTextiNu(st) {
      var t = '';
      if (LT && typeof LT.texti === 'function') t = kalla(LT, 'texti', [st, ctx], '') || '';
      else for (var i = 0; i < skref.length; i++) {
        var s = skref[i];
        if (s.gerd === 'texti' && !s.el.hidden && s.inntak) { t = s.inntak.value; break; }
      }
      t = trim(String(t).split(/\r?\n/)[0].replace(loppaLeyfd ? TJAKN_LG : TJAKN_G, ''));
      return t || ord.letur_synishorn || '';
    }
    var leturCssSkra = null;
    function sjalfCss(id) {
      /* no adapter css: the SMFram font registry names the family and weight of the id (if it has it) */
      try { if (!leturCssSkra && window.SMFram && window.SMFram.letur) leturCssSkra = window.SMFram.letur(); } catch (e) { leturCssSkra = {}; }
      var e2 = leturCssSkra && leturCssSkra[id];
      return e2 ? { fontFamily: '"' + e2.nafn + '"', fontWeight: String(e2.thykkt || 400), fontStyle: e2.skaletrad ? 'italic' : 'normal' } : {};
    }
    function leturStaerd() { return simiMidill && simiMidill.matches ? 20 : 22; }
    /* one line at 22 px (20 on phones) while it fits at 17 px or more; a longer text with a space or a
       hyphen goes on two or three balanced lines at the largest size from 22/20 down to 13 px that fits
       the box; else one line down to 15 px, then cut with an ellipsis (CSS) */
    function maetaLetur(box, sp) {
      var st = leturStaerd();
      if (sp.hasAttribute('data-linur')) sp.removeAttribute('data-linur');
      sp.style.fontSize = st + 'px';
      var bw = box.clientWidth - 12, sw = sp.scrollWidth;
      if (!(bw > 0) || sw <= bw) return;
      var f1 = Math.floor(st * bw / sw);
      if (f1 >= 17 || !/[\s-]/.test(sp.textContent)) { sp.style.fontSize = Math.max(15, f1) + 'px'; return; }
      var h = box.clientHeight - 4;
      sp.setAttribute('data-linur', '');
      for (var f = st; f >= 13; f--) {
        sp.style.fontSize = f + 'px';
        if (sp.scrollWidth <= sp.clientWidth + 1 && sp.scrollHeight <= h) return;
      }
    }
    function setjaLeturFlis(x, html, texti, fl, css) {
      var box = x.box, bak = fl && fl.bak ? String(fl.bak) : '', blek = fl && fl.blek ? String(fl.blek) : '';
      if (box.__vsBak !== bak) { box.__vsBak = bak; box.style.background = bak; }
      if (box.__vsBlek !== blek) { box.__vsBlek = blek; box.style.color = blek; }
      if (html) {
        if (box.__vsHtml !== html) { box.innerHTML = html; box.__vsHtml = html; box.__vsSp = null; flisSkrif += 1; }
        box.setAttribute('data-teiknad', '');
        return;
      }
      var sp = box.__vsSp;
      if (!sp || sp.parentNode !== box) {
        sp = $('.vs-letur-texti', box);
        if (!sp || box.children.length !== 1) { box.innerHTML = '<span class="vs-letur-texti"></span>'; flisSkrif += 1; sp = box.firstChild; }
        box.__vsSp = sp;
        box.__vsHtml = null;
      }
      if (sp.textContent !== texti) sp.textContent = texti;
      var tok = {};
      box.__vsTok = tok;
      var beita = function (hladid) {
        if (box.__vsTok !== tok) return;
        sp.style.fontFamily = hladid && css.fontFamily ? css.fontFamily : '';
        sp.style.fontWeight = css.fontWeight || '';
        sp.style.fontStyle = css.fontStyle || '';
        sp.style.textTransform = css.textTransform || '';
        sp.style.letterSpacing = css.letterSpacing || '';
        maetaLetur(box, sp);
        box.setAttribute('data-teiknad', '');
      };
      if (css.fontFamily && document.fonts && document.fonts.load) {
        var spec = (css.fontStyle === 'italic' ? 'italic ' : '') + (css.fontWeight || '400') + ' ' + leturStaerd() + 'px ' + css.fontFamily, til = false;
        try { til = document.fonts.check(spec, texti); } catch (e) { til = false; }
        if (til) beita(true);
        else {
          beita(false);
          innan(document.fonts.load(spec, texti), 2500).then(function (f) { beita(!!(f && f.length)); }, function () { beita(false); });
        }
      } else beita(!!css.fontFamily);
    }
    function teiknaFlis(x, grunn) {
      var s = x.s, inn = x.inn, box = x.box, gildi = inn.value;
      var s2 = flisStada(x, grunn);
      var o = { id: 'vs-' + idHluti(gogn.sid || 'vs') + '-' + idHluti(s.lykill) + '-' + x.n + '-', smamynd: true, lykill: s.lykill, gildi: gildi,
        tegund: x.letur ? 'letur' : s.gerd === 'rammi' ? 'rammi' : 'utlit', vara: inn.getAttribute('data-vs-vara') || '', letur: inn.getAttribute('data-letur') || '' };
      var A = x.letur ? LT : UT, lk = null, k, html;
      if (A && typeof A.lykill === 'function') lk = kalla(A, 'lykill', [s2, ctx, o], null);
      if (x.letur) {
        var texti = leturTextiNu(s2), fl = LT && typeof LT.flotur === 'function' ? kalla(LT, 'flotur', [s2, ctx], null) : null;
        var lid = o.letur || gildi, css = LT && typeof LT.css === 'function' ? kalla(LT, 'css', [lid, ctx], null) || {} : sjalfCss(lid);
        o.texti = texti;
        o.flotur = fl;
        var utlina = !!(LT && typeof LT.teikna === 'function');
        k = 'letur\u0001' + gildi + '\u0001' + flisEpoch + '\u0001' + texti + '\u0001' + JSON.stringify(fl || null) + '\u0001' + JSON.stringify(css) + '\u0001' +
          (utlina ? (lk != null ? String(lk) : sjalfLykill(s2)) : '');
        if (box.getAttribute('data-sig') === k) return;
        html = utlina ? cacheFa(s.lykill, k) : null;
        if (utlina && html === undefined) {
          flisTeiknad += 1;
          html = flisMarkup(kalla(LT, 'teikna', [s2, ctx, o], null), o);
          cacheSetja(s.lykill, k, html);
        }
        setjaLeturFlis(x, html || null, texti, fl, css);
        box.setAttribute('data-sig', k);
        return;
      }
      k = o.tegund + '\u0001' + gildi + '\u0001' + flisEpoch + '\u0001' + (lk != null ? String(lk) : sjalfLykill(s2));
      if (box.getAttribute('data-sig') === k) return;
      html = cacheFa(s.lykill, k);
      if (html === undefined) {
        html = null;
        if (UT && typeof UT.teikna === 'function') { flisTeiknad += 1; html = flisMarkup(kalla(UT, 'teikna', [s2, ctx, o], null), o); }
        cacheSetja(s.lykill, k, html);
      }
      /* E5: skuggi as a function (gaeludyr): per tile, so photo blanks drawn with multiply get no isolating filter */
      if (typeof adapter.skuggi === 'function') { if (kalla(adapter, 'skuggi', [s2, ctx], true) === false) box.setAttribute('data-skuggi', 'nei'); else box.removeAttribute('data-skuggi'); }
      setjaFlis(box, html);
      box.setAttribute('data-sig', k);
    }

    /* ---- the bar (C8): the bulk discount tier the discount note shows ---- */
    function magnPct() {
      if (!magnEl) return 0;
      var p = 0;
      $$('[data-sm-magn-t]', magnEl).forEach(function (t) {
        if ((' ' + t.className + ' ').indexOf(' er-nad ') >= 0) p = Math.max(p, parseInt(t.getAttribute('data-sm-magn-t'), 10) || 0);
      });
      return p;
    }
    /* the Shopify Inbox chat bubble never lies over the bar: lifted just above it where they meet */
    var spjallLyft = 0, spjallHn = null;
    /* 18.10: Inbox-hnappurinn er nú inni í öðru skuggatré (shopify-chat > shopify-agent-runtime), svo hann er leitaður í
       hreiðruðum skuggatrjám (mest 3 lög) og geymdur */
    function spjallLeita(rot, d) {
      var b = rot.querySelector('button.activator');
      if (b || d > 2) return b;
      var oll = rot.querySelectorAll('*');
      for (var i = 0; i < oll.length; i++) if (oll[i].shadowRoot) { b = spjallLeita(oll[i].shadowRoot, d + 1); if (b) return b; }
      return null;
    }
    function spjallBoli() {
      var h = document.querySelector('shopify-chat');
      if (!h || !kaupstika) return;
      var sr = h.shadowRoot;
      if (!spjallHn || !spjallHn.isConnected) spjallHn = sr ? (spjallLeita(sr, 0) || sr.querySelector('button')) : null;
      var b = spjallHn;
      if (!b) return;
      var br = b.getBoundingClientRect(), kb = kaupstika.getBoundingClientRect();
      if (!br.width || !kb.width) return;
      /* "Kaupa núna" röðin undir stikunni telur líka (18.10) */
      var vh = window.innerHeight || document.documentElement.clientHeight, botn = br.bottom + spjallLyft, efst = null;
      [kb, knRod && !knRod.hidden ? knRod.getBoundingClientRect() : null].forEach(function (r) {
        if (r && r.width && r.bottom > 0 && r.top < vh && br.right > r.left && br.left < r.right && (efst === null || r.top < efst)) efst = r.top;
      });
      var thorf = efst !== null ? Math.max(0, Math.ceil(botn - (efst - 12))) : 0;
      if (thorf !== spjallLyft) { spjallLyft = thorf; h.style.transform = thorf ? 'translateY(-' + thorf + 'px)' : ''; }
    }

    /* ---- sync everything after a change ---- */
    var lifandiFra = merkiEl ? !merkiEl.hidden : false;
    /* lysandi slod (K3): the DOM radios of every plain option step, set to match kodar - run once at
       boot when readable params resolved a non-default variant, before the adapter's own afbrigdi()
       hook (sersnid steps) reads the checked state back out through stada() */
    function kodarIRadio() {
      skref.forEach(function (s) {
        if (s.gerd !== 'valkostur') return;
        var p0 = s.p - 1, q0 = s.p2 ? s.p2 - 1 : -1;
        s.radio.forEach(function (r) {
          var hl = q0 >= 0 ? String(r.value).split('|') : null;
          r.checked = q0 >= 0 ? (hl[0] === kodar[p0] && hl[1] === kodar[q0]) : r.value === kodar[p0];
        });
      });
    }
    /* lysandi slod (K3): writes the readable option params (replacing ?variant=<id>) with
       history.replaceState. Called on every customer change (samstilla(true)), and once at boot when
       the incoming URL already carried a query string (a shared ?variant= or readable link is rewritten
       to the canonical readable form; a bare /products/<handle> load is left untouched). */
    function lysandiSlodSkrifa() {
      if (!afb || !window.history || !history.replaceState) return;
      try {
        var u = new URL(window.location.href);
        /* a family page shows the product's own address, so it can be shared and reloaded */
        if (fjol && nuVara().url && u.pathname !== nuVara().url) u = new URL(nuVara().url, window.location.href);
        if (fjol) { u.searchParams.delete('skilti'); u.searchParams.delete('flokkur'); }
        /* readable option params replace ?variant=<id> in the address bar; a shared/incoming ?variant=
           link still works (Liquid reads it), it is only rewritten here */
        u.searchParams.delete('variant');
        var lysBr = lysandiBreytur(nuVara(), afb);
        Object.keys(lysandiSed).forEach(function (k) { if (!lysBr.hasOwnProperty(k)) u.searchParams.delete(k); });
        Object.keys(lysBr).forEach(function (k) { lysandiSed[k] = 1; u.searchParams.set(k, lysBr[k]); });
        /* the frame: rammi=1|0 while its step shows (an adapter may drop it through slod) */
        if (rammiSkref()) { var rsU = rammiSkref(), rgl = !FALIN_OVIRK ? rammiGildi() : rammiSynist(rsU) ? (valinnKostur(rsU) || {}).value || '' : ''; if (rgl) u.searchParams.set('rammi', rgl); else u.searchParams.delete('rammi'); }
        var auka = kalla(adapter, 'slod', [stada(), ctx], null) || {};
        Object.keys(auka).forEach(function (k) { if (auka[k] == null || auka[k] === '') u.searchParams.delete(k); else u.searchParams.set(k, auka[k]); });
        history.replaceState(history.state, '', u.toString().replace(/%2C/g, ','));
      } catch (e) { /* old browser: URL stays */ }
    }
    function samstilla(notandi) {
      var adValdi = false;
      samGen += 1;
      /* an untouched frame follows the material before the adapter sees the state (v1.1) */
      rammiForval();
      /* the adapter may pick the product and variant from its own steps (E6) */
      var adVelja = function () {
        if (typeof adapter.afbrigdi !== 'function') return;
        var ad = kalla(adapter, 'afbrigdi', [stada(), ctx], undefined);
        if (ad !== undefined) {
          adValdi = true;
          if (ad && ad.vara && fjol && ad.vara !== nuHandle) skiptaVoru(ad.vara, ad.id);
          var fa = null;
          if (ad) afbrigdi.forEach(function (a) { if (String(a.id) === String(ad.id)) fa = a; });
          afb = fa;
          if (fa) kodar = fa.o.slice();
          /* CK 19.10 (QA3 F1): a choice made in the adapter's own steps (the safety sign colour dots) is remembered by name
             like a kit option, so the next product keeps it where the adapter lets it (halda) */
          if (fa) mundu();
        }
      };
      adVelja();
      /* the frame: default per material, its price option (samraeming C6); when it moved the frame
         (metal without a frame variant, or back again) the adapter picks once more */
      if (rammiSamstilla(adValdi) === 'aftur') { adVelja(); rammiSamstilla(adValdi); }
      ctx.afbrigdi = afb;
      /* option radios, step values, value sub-lines (a pair step holds two options: "code1|code2") */
      var grunnV = rammiSkref() ? stada() : null;
      skref.forEach(function (s) {
        if (s.gerd !== 'valkostur') return;
        var p0 = s.p - 1, q0 = s.p2 ? s.p2 - 1 : -1;
        var verd = [], olik = false;
        s.radio.forEach(function (r) {
          var hl = q0 >= 0 ? String(r.value).split('|') : [r.value];
          r.checked = q0 >= 0 ? hl[0] === kodar[p0] && hl[1] === kodar[q0] : r.value === kodar[p0];
          var k = kodar.slice(); k[p0] = hl[0];
          if (q0 >= 0) k[q0] = hl[1];
          var a = flisAfb(finna(k), k, grunnV);
          r.__vsAfb = a;
          if (a && a.til) { if (verd.length && verd[0] !== a.verd) olik = true; verd.push(a.verd); }
        });
        /* owner 30.09: a value no variant has with the choices of the option steps above is hidden (not
           "Ekki i bodi"); a value that exists there but not with the choices below moves those (no label) */
        var ofar = FALIN_OVIRK ? ofarKodar(s) : null;
        s.radio.forEach(function (r) {
          var a = r.__vsAfb, undir = '';
          var hl2 = q0 >= 0 ? String(r.value).split('|') : null;
          var til = hl2 ? einhverTil2(p0, hl2[0], q0, hl2[1]) : einhverTil(p0, r.value);
          var lab = r.parentNode, falid = !!ofar && !r.checked && !tilOfar(ofar, p0, hl2 ? hl2[0] : r.value, q0, hl2 ? hl2[1] : null);
          if (lab && lab.hidden !== falid && (falid || lab.__vsFalid)) { lab.hidden = falid; lab.__vsFalid = falid; }
          r.disabled = s.el.hidden || falid || (!til && !(adapter && adapter.veljaUppselt && a));
          if (!til) undir = ord.uppselt;
          else if (!a) undir = ofar ? '' : ord.ekki_i_bodi;
          else if (!a.til) undir = ord.uppselt;
          else if (olik) undir = kr(a.verd);
          var u = $('[data-vs-undir]', r.parentNode);
          if (u && u.textContent !== undir) u.textContent = undir;
        });
        var v = valinnKostur(s);
        if (s.gildiEl) {
          var nafn = synNafn(v);
          s.gildiEl.textContent = nafn ? nafn.charAt(0).toUpperCase() + nafn.slice(1) : '';
        }
      });
      teiknaRond(false);
      if (afb) {
        idInntak.value = afb.id;
        if (notandi) lysandiSlodSkrifa();
      }
      /* material text per variant */
      var efniA = $$('[data-vs-efni-afbrigdi]', form), syndi = false;
      efniA.forEach(function (d) { var s2 = !!afb && d.getAttribute('data-vs-efni-afbrigdi') === String(afb.id); d.hidden = !s2; if (s2) syndi = true; });
      var grunnur = $('[data-vs-efni-grunnur]', form);
      if (grunnur && efniA.length) grunnur.hidden = syndi || !trim(grunnur.textContent);
      /* conditional steps, twice so a step shown by another conditional step settles; then the
         adapter's own rule (E7), which may hide a step the words would show */
      for (var umferd = 0; umferd < 2; umferd++) skref.forEach(function (s) { if (s.kveikja.length) birta(s, aVid(s)); });
      if (typeof adapter.syna === 'function') {
        for (var umf2 = 0; umf2 < 2; umf2++) {
          var st0 = stada();
          skref.forEach(function (s) {
            var sy = kalla(adapter, 'syna', [s.lykill, st0, ctx], undefined);
            if (typeof sy === 'boolean') birta(s, sy && (!s.kveikja.length || aVid(s)));
          });
        }
      }
      /* owner 30.09: an option step left with one choice is hidden (its value stays in the variant) and shows
         again when it has a choice */
      if (FALIN_OVIRK) skref.forEach(function (s) {
        if (s.gerd !== 'valkostur' || s.radio.length < 2) return;
        var n = 0;
        s.radio.forEach(function (r) { if (!(r.parentNode && r.parentNode.__vsFalid)) n++; });
        if (n <= 1) { if (!s.el.hidden) { birta(s, false); s.__vsEinn = true; } }
        else if (s.__vsEinn) {
          s.__vsEinn = false;
          if (s.el.hidden) birta(s, (!s.kveikja.length || aVid(s)) &&
            (typeof adapter.syna !== 'function' || kalla(adapter, 'syna', [s.lykill, stada(), ctx], undefined) !== false));
        }
      });
      /* the frame step shows only while the sign has a plate (adapter.rammi) */
      var rsyn = rammiSkref();
      if (rsyn) birta(rsyn, rammiSynist(rsyn));
      /* the adapter's step bodies (E5), the product step (E2) and help lines that follow the state */
      var st1 = stada();
      skref.forEach(function (s) {
        if (s.gerd === 'vara') teiknaVorur(s);
        if (s.gerd !== 'sersnid' || !adapter.skref || !adapter.skref[s.lykill]) return;
        var body = $('[data-vs-sersnid]', s.el);
        try { if (body) adapter.skref[s.lykill].teikna(body, st1, ctx); }
        catch (e) { if (window.console) console.warn('SMVelSida adapter.skref.' + s.lykill, e); }
        /* owner 30.09: an adapter step whose only choice group lost choices that are not offered (data-vs-siad) and
           has one left is hidden, like the option steps above; the adapter keeps its value. It shows again when
           it has a choice. A step that only ever had one choice (e.g. a size shown as information) is left alone. */
        if (FALIN_OVIRK && body) {
          var inn = $$('input, textarea, select', body).filter(function (x) { return x.type !== 'hidden'; });
          var ein = inn.length === 1 && inn[0].type === 'radio' && !!(inn[0].closest && inn[0].closest('[data-vs-siad]'));
          if (ein) { if (!s.el.hidden) { birta(s, false); s.__vsEinn = true; } }
          else if (s.__vsEinn) {
            s.__vsEinn = false;
            if (s.el.hidden) birta(s, (!s.kveikja.length || aVid(s)) &&
              (typeof adapter.syna !== 'function' || kalla(adapter, 'syna', [s.lykill, st1, ctx], undefined) !== false));
          }
        }
      });
      verdVoruFlisa();
      var hj = kalla(adapter, 'hjalp', [st1, ctx], null);
      if (hj) skref.forEach(function (s) {
        var lyk = s.lykill;
        if (!(lyk in hj) && s.gerd === 'valkostur') { var o = nuVara().valkostir[s.p - 1]; if (o && o.nafn in hj) lyk = o.nafn; }
        if (!(lyk in hj)) return;
        var t = hj[lyk] || '', el = s.hjalpEl;
        if (!el && t) {
          el = document.createElement('p');
          el.className = 'vs-hjalp';
          el.setAttribute('data-vs-hjalp', '');
          el.id = s.el.id + '-hjalp2';
          s.el.insertBefore(el, s.villaEl || null);
          s.hjalpEl = el;
        }
        if (!el) return;
        if (el.textContent !== t) el.textContent = t;
        el.hidden = !t;
      });
      if (merkiEl) {
        var lif = typeof adapter.lifandi === 'function' ? !!kalla(adapter, 'lifandi', [st1, ctx], lifandiFra) : lifandiFra;
        merkiEl.hidden = !lif;
      }
      numera();
      verdTexti();
      uppfaera();
      veggur();
      teikna();
      flisarTeikna(0);
      if (notandi) {
        try { rot.dispatchEvent(new CustomEvent('sm:sida:breyting', { bubbles: true, detail: { stada: stada() } })); } catch (e) {}
      }
    }
    function numera() {
      var n = 0;
      skref.forEach(function (s) {
        if (!s.nrEl || s.el.hidden || s.el.hasAttribute('data-takka-fel')) return;
        n += 1;
        if (s.nrEl.textContent !== String(n)) s.nrEl.textContent = n;
      });
    }
    /* Kjarni v1.2 (toskumerki logo, 2026-10-01): an add-on sold as its own cart line (an UNLISTED 'Aukagjald'
       product, e.g. the club logo on a bag tag). adapter.aukaverd(s, ctx) -> aurar per piece, shown in every
       price; adapter.aukalinur(s, ctx) -> [{ id, properties }], each posted as a line of the same quantity right
       after the piece itself (JSON /cart/add.js, one at a time). A failed add-on never undoes the piece: it is
       warned on the console and the sheet still opens. */
    function aukaVerd() { return Math.max(0, Math.round(Number(kalla(adapter, 'aukaverd', [stada(), ctx], 0)) || 0)); }
    /* P2-G1 21.10 (X02): all extra lines go in ONE /cart/add.js request (items); equal lines (the plugs: one entry per plug) are
       merged first, so the cart gets the same line and quantity as before (Shopify merged them) from one call instead of 2-4 */
    function aukaSenda(linur, q, baeta) {
      var items = [], lyk = {};
      (linur || []).forEach(function (l) {
        if (!l || !l.id) return;
        var k = l.id + '|' + JSON.stringify(l.properties || {});
        if (lyk.hasOwnProperty(k)) { items[lyk[k]].quantity += q; return; }
        lyk[k] = items.length;
        items.push({ id: l.id, quantity: q, properties: l.properties || {} });
      });
      if (!items.length) return Promise.resolve();
      var nofn = items.map(function (x) { return x.id; }).join(',');
      return fetch(baeta, { method: 'POST', body: JSON.stringify({ items: items }),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) framVidvorun('aukalinur ' + nofn + ' fylgdu ekki (' + r.status + ')'); }, function () { framVidvorun('aukalinur ' + nofn + ' fylgdu ekki (net)'); });
    }
    function verdTexti() {
      /* samraeming C8: the total as it will be charged, with the bulk discount tier the discount note on
         the page shows (sm-magnafslattur.js decides it from the cart and the quantity) */
      /* F1 23.10: the bulk discount goes on the sign only, never on the add-on lines (plugs, logo; product_type 'Aukagjald',
         verdkerfi.json magnafslattur: "aldrei a aukahluti"), so the button equals /cart.js to the krona */
      /* F1 23.10: Shopify takes the discount per piece in whole kronur, rounded down (15 % of 8.490 = 1.273, of 6.990 = 1.048;
         real carts 36.085 and 29.710), so the button does the same */
      var pct = magnPct(), heild = afb ? afb.verd * magn() : 0;
      if (pct && afb) heild = (afb.verd - Math.floor(afb.verd * pct / 10000 + 1e-9) * 100) * magn();
      if (afb) heild += aukaVerd() * magn();
      var t = afb ? kr(heild) : '';
      verdEl.forEach(function (el) { if (el.textContent !== t) el.textContent = t; });
      var mt = pct ? fylla(ord.heildarverd_afsl || '', { p: pct }) : (ord.heildarverd || '');
      if (mt) verdMerki.forEach(function (el) {
        if (el.textContent !== mt) el.textContent = mt;
        /* P2-G1 21.10: "Með 10% magnafslætti" is longer than "Heildarverð"; sm-sida.css wraps it on a computer so the price stays whole */
        if (el.hasAttribute('data-afsl') !== !!pct) { if (pct) el.setAttribute('data-afsl', ''); else el.removeAttribute('data-afsl'); }
      });
    }
    function skrefLokid(s) {
      if (s.el.hidden) return false;
      if (s.gerd === 'valkostur') return !!valinnKostur(s);
      if (s.gerd === 'val') return !!valinnKostur(s) || (!s.skylda && s.farid);
      if (s.gerd === 'texti') {
        var t = trim(s.inntak ? s.inntak.value : '');
        if (t) return s.leyfaTjakn || !TJAKN.test(t);
        return !s.skylda && s.farid;
      }
      if (s.gerd === 'skra') return skraKomin(s) || (!s.skylda && s.farid);
      if (s.gerd === 'sersnid') {
        var sk = adapter.skref && adapter.skref[s.lykill];
        return sk && typeof sk.buid === 'function' ? !!kalla(sk, 'buid', [stada(), ctx], true) : true;
      }
      return true;
    }
    function uppfaera() {
      var v = vandamal();
      var fyrsta = null;
      for (var i = 0; i < v.length; i++) if (v[i].stopp) { fyrsta = v[i]; break; }
      /* field errors: after the first attempt, or at once for problems a customer cannot guess */
      skref.forEach(function (s) {
        var mitt = null;
        v.forEach(function (x) { if (!mitt && x.s === s && (reynt || x.strax)) mitt = x; });
        if (s.gerd !== 'skra') syndVilla(s, mitt ? mitt.texti : (s.skilabod || ''));
      });
      /* step values */
      skref.forEach(function (s) {
        if (!s.gildiEl) return;
        if (s.gerd === 'texti' || s.gerd === 'aths') {
          var t = trim(s.inntak ? s.inntak.value : '').split(/\r?\n/)[0];
          var synd = t || (s.skylda ? '' : ord.valfrjalst);
          if (s.gildiEl.textContent !== synd) s.gildiEl.textContent = synd;
          if (t) s.gildiEl.removeAttribute('data-valfrjalst'); else s.gildiEl.setAttribute('data-valfrjalst', '');
        } else if (s.gerd === 'val') {
          var r = valinnKostur(s);
          var g = r ? (s.birting === 'letur' ? synNafn(r) : (synNafn(r) || r.value)) : (s.skylda ? '' : ord.valfrjalst);
          if (s.gildiEl.textContent !== g) s.gildiEl.textContent = g;
        } else if (s.gerd === 'rammi') {
          var rr = valinnKostur(s), rg = synNafn(rr);
          if (s.gildiEl.textContent !== rg) s.gildiEl.textContent = rg;
        } else if (s.gerd === 'magn') {
          var mt = stkTexti(magn());
          if (s.gildiEl.textContent !== mt) s.gildiEl.textContent = mt;
        } else if (s.gerd === 'sersnid') {
          var sk = adapter.skref && adapter.skref[s.lykill];
          var gt = sk && typeof sk.gildi === 'function' ? String(kalla(sk, 'gildi', [stada(), ctx], '') || '') : '';
          if (s.gildiEl.textContent !== gt) s.gildiEl.textContent = gt;
        }
        if (s.teljari && s.inntak) {
          var len = stafafjoldi(s.inntak.value), ham = +(s.inntak.getAttribute('data-sm-hamark0') || s.inntak.maxLength);
          s.teljari.textContent = len + '/' + ham;
          if (ham > 0 && len >= ham) s.teljari.setAttribute('data-fullt', ''); else s.teljari.removeAttribute('data-fullt');
        }
      });
      /* counters of the kit fields (ctx.html.reitur) in the adapters' own steps */
      kitTeljarar(form);
      loppaTakkar();
      /* step bar */
      var nu = null;
      skref.forEach(function (s) {
        if (!s.stikaA) return;
        var lokid = skrefLokid(s);
        if (lokid) s.stikaA.setAttribute('data-stada', 'buid'); else s.stikaA.removeAttribute('data-stada');
        var sr = $('[data-vs-stika-lokid]', s.stikaA);
        if (sr) sr.hidden = !lokid;
        if (!lokid && !nu && !s.el.hidden) nu = s;
      });
      skref.forEach(function (s) {
        if (!s.stikaA) return;
        if (s === nu) s.stikaA.setAttribute('aria-current', 'step'); else s.stikaA.removeAttribute('aria-current');
      });
      if (stikaListi) stikaListi.hidden = !skref.some(function (s) { return s.stikaLi && !s.stikaLi.hidden; });
      /* a sold-out or missing-combination message from the last add goes when the customer picks one that exists */
      if (kvillaStopp && !v.some(function (x) { return x.stopp && !x.s && x.texti === kvillaStopp; })) kaupaVilla('');
      /* the adapter shows its own field messages from the same list (E5) */
      kalla(adapter, 'vandamal', [v, ctx], null);
      /* no variant for this choice: the price line says what stops it (as the old pages did) */
      if (!afb) {
        var vt = (fyrsta && fyrsta.hnappur) || ord.ekki_i_bodi || '';
        verdEl.forEach(function (el) { if (el.textContent !== vt) el.textContent = vt; });
      }
      /* "Kaupa núna": aðeins þegar ekkert stöðvar (sama og aðalhnappurinn), og ekki á meðan lína er á leiðinni */
      if (knRod) {
        var knSyn = !fyrsta && !!afb;
        if (knRod.hidden === knSyn && !sendir) knRod.hidden = !knSyn;
      }
      /* buttons */
      if (!sendir) {
        var stadaH = fyrsta ? 'vantar' : '';
        var texti = fyrsta ? (fyrsta.hnappur || ord.vantar) : ord.baeta;
        hnappar.forEach(function (b) {
          if (b.getAttribute('data-stada') === 'komid') return;
          if (stadaH) b.setAttribute('data-stada', stadaH); else b.removeAttribute('data-stada');
          var te = $('[data-vs-hnappur-texti]', b);
          if (te && te.textContent !== texti) te.textContent = texti;
        });
      }
      return v;
    }

    /* ---- scrolling and focus below the sticky preview ---- */
    function ofan() {
      var fast = parseFloat(rot.style.getPropertyValue('--vs-fast')) || 0;
      if (window.innerWidth >= 1001 || !svid) return fast + 16;
      /* CK 19.10 (QA20 A1): on a low screen the preview is not sticky (sm-sida.css), nothing covers the form */
      if (window.getComputedStyle(svid).position !== 'sticky') return fast + 16;
      return fast - 33 + svid.offsetHeight + 12;
    }
    var sjalfSkrun = 0;
    function fokusa(s, kodi) {
      if (!s) return;
      /* 14.10: falinn skráarreitur (sm-skra-inntak) tekur ekki lengur fókusinn á undan fokus-króki adaptersins (listaverk: "Vantar nafn") */
      var skraInn = $('[data-sm-skra-inntak]', s.el);
      var mark = s.inntak || valinnKostur(s) || s.radio[0] || null;
      if (kodi === 'tjakn' && s.__tjaknEl && s.el.contains(s.__tjaknEl)) mark = s.__tjaknEl;
      if (!mark && (s.gerd === 'sersnid' || s.gerd === 'vara')) {
        var sk = adapter.skref && adapter.skref[s.lykill];
        var fyrr = document.activeElement;
        if (sk && typeof sk.fokus === 'function') {
          mark = kalla(sk, 'fokus', [$('[data-vs-sersnid]', s.el), kodi, ctx], null);
          /* 14.10: krokur sem faerdi baendilinn sjalfur en skilar engu: sa reitur gildir (adur yfirskrifadi kitid hann med fyrsta reit) */
          if (!mark) { var nu = document.activeElement; if (nu && nu !== fyrr && nu !== document.body && s.el.contains(nu)) mark = nu; }
        }
        if (!mark) mark = skraInn;
        if (!mark) {
          var inn = $$('input:not([disabled]), button:not([disabled]), textarea:not([disabled])', s.el);
          /* 14.10: "Vantar ..." = baendillinn i FYRSTA TOMA skyldureitinn, ekki fyrsta reit */
          inn.forEach(function (x) {
            if (mark || !(x.tagName === 'TEXTAREA' || (x.tagName === 'INPUT' && /^(text|tel|email|number|search|url)?$/.test(x.type)))) return;
            if ((x.getAttribute('aria-required') === 'true' || x.required) && !trim(x.value) && !x.closest('[hidden]')) mark = x;
          });
          inn.forEach(function (x) { if (!mark && x.type === 'radio' && x.checked) mark = x; });
          mark = mark || inn[0] || null;
        }
      }
      mark = mark || skraInn || s.el;
      var y = window.pageYOffset + s.el.getBoundingClientRect().top - ofan();
      sjalfSkrun = Date.now();
      scrollTil(y);
      try { mark.focus({ preventScroll: true }); } catch (e) { try { mark.focus(); } catch (e2) {} }
    }
    if (stikaListi) stikaListi.addEventListener('click', function (e) {
      var a = e.target.closest ? e.target.closest('[data-vs-stika]') : null;
      if (!a) return;
      var s = finnaSkref(a.getAttribute('data-vs-stika'));
      if (!s || s.el.hidden) return;
      e.preventDefault();
      fokusa(s);
    });

    /* ---- inputs ---- */
    /* owner 07.10: a customer who is looking at another picture of the strip (e.g. 5 of 33) and types names or changes
       the look (font, material, shape, size, frame, address yes/no) is taken back to the live preview ("Thin honnun",
       picture 1) so the change is always seen. Only on pages that have a live preview (lifandiSida), never for the
       quantity or the note, and nothing happens (no redraw) when the preview is already shown. Capture phase: it runs
       before the handlers below. */
    function aLifandiForskodun(e) {
      var t = e.target;
      if (slide === 0 || !t || !lifandiSida()) return;
      for (var i = 0; i < skref.length; i++) {
        var x = skref[i];
        if (!x.el.contains(t)) continue;
        if (x.gerd === 'magn' || x.gerd === 'aths') return;
        synaSlide(0);
        return;
      }
    }
    form.addEventListener('input', aLifandiForskodun, true);
    form.addEventListener('change', aLifandiForskodun, true);
    form.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.type !== 'radio') return;
      /* the product step: a group pill opens the group's first product, a card its product */
      var sid = gogn.sid || 'vs';
      if (fjol && t.name === 'vs-' + sid + '-vara') { if (skiptaVoru(t.value)) samstilla(true); return; }
      if (fjol && t.name === 'vs-' + sid + '-hopur') {
        var g = null;
        fjol.hopar.forEach(function (x) { if (x.lykill === t.value) g = x; });
        if (g && g !== nuHopur) {
          nuHopur = g; allirSyndir = false; voruLykill = ''; if (skiptaVoru(g.vorur[0])) samstilla(true);
          /* P2-G1 21.10 (QA20 A4): the new list chose its first sign while focus stays on the category: say both */
          var gv = nuVara();
          tilkynna((g.heiti ? g.heiti + ': ' : '') + ((gv && gv.titill) || ''), false);
        }
        return;
      }
      /* a cross product tile (samraeming): the kit switches product and variant */
      var vh = t.getAttribute('data-vs-vara');
      if (vh && fjol && vorur[vh]) {
        var va = veljaIVoru(vorur[vh], String(t.getAttribute('data-vs-o') || '').split(','));
        if (skiptaVoru(vh, va ? va.id : null)) samstilla(true);
        return;
      }
      for (var i = 0; i < skref.length; i++) {
        var s = skref[i];
        if (s.radio.indexOf(t) === -1) continue;
        if (s.gerd === 'valkostur') { if (s.p2) veljaPar(s.p, s.p2, t.value); else veljaValkost(s.p, t.value); }
        else { if (s.gerd === 'rammi') { rammiSnert = true; rammiOsk = ''; } samstilla(true); }
        return;
      }
    });
    /* an optional list choice can be cleared with a second click */
    form.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || t.type !== 'radio') return;
      skref.forEach(function (s) {
        if (s.gerd !== 'val' || s.skylda || s.radio.indexOf(t) === -1) return;
        if (t.__vsVar) { t.checked = false; t.__vsVar = false; samstilla(true); }
        else s.radio.forEach(function (r) { r.__vsVar = r === t; });
      });
    });
    form.addEventListener('input', function (e) {
      var t = e.target;
      skref.forEach(function (s) {
        if (s.inntak !== t) return;
        if (s.gerd === 'magn') { verdTexti(); speglaMagn(); }
        if (s.skilabod && s.gerd !== 'magn' && e.inputType !== 'insertFromPaste') s.skilabod = '';
        /* a drawn preview follows the typed text (one frame per burst) */
        if ((s.gerd === 'texti' || s.gerd === 'aths') && adapter.gerd !== 'ljosmynd') teiknaSeinna();
      });
      /* tiles follow the typed text: letur tiles after 150 ms, the other tiles after 300 ms without typing */
      var ts = null;
      skref.forEach(function (x) { if (x.el.contains(t)) ts = x; });
      if (ts && ts.gerd !== 'magn' && ts.gerd !== 'aths') { flisarTeikna(100, 'letur'); flisarTeikna(300, 'utlit'); }
      uppfaera();
    });
    form.addEventListener('paste', function (e) {
      var t = e.target;
      skref.forEach(function (s) {
        if (s.inntak !== t || !(t.maxLength > 0)) return;
        var cd = e.clipboardData || window.clipboardData;
        var inn = cd ? String(cd.getData('text') || '') : '';
        var sel = (t.selectionEnd || 0) - (t.selectionStart || 0);
        if (t.value.length - sel + inn.length <= t.maxLength) return;
        s.skilabod = fylla(ord.villa_klippt, { n: t.maxLength });
        setTimeout(function () { uppfaera(); tilkynna(s.skilabod, false); }, 0);
      });
    });
    form.addEventListener('focusout', function (e) {
      var s = null;
      skref.forEach(function (x) { if (x.el.contains(e.target)) s = x; });
      if (!s || (e.relatedTarget && s.el.contains(e.relatedTarget))) return;
      if (!e.relatedTarget && !document.hasFocus()) return;
      s.farid = true;
      if (s.gerd === 'magn') setjaMagn(parseInt(s.inntak.value, 10));
      uppfaera();
    });
    form.addEventListener('sm:skra', function () { uppfaera(); });

    /* quantity */
    function speglaMagn() {
      if (!magnSkref) return;
      var n = magn();
      var sp = $('[data-vs-magn-spegill]', magnSkref.el);
      if (sp && sp.textContent !== String(n)) sp.textContent = n;
      var lag = parseInt(magnSkref.el.getAttribute('data-lagmark'), 10) || 1;
      var ham = parseInt(magnSkref.el.getAttribute('data-hamark'), 10) || 99;
      $$('[data-vs-magn]', magnSkref.el).forEach(function (b) {
        var d = parseInt(b.getAttribute('data-vs-magn'), 10);
        b.disabled = d < 0 ? n <= lag : n >= ham;
      });
      if (parseInt(magnSkref.inntak.value, 10) > ham) magnSkref.skilabod = fylla(ord.fjoldi_hamark, { n: ham });
      else if (n < ham) magnSkref.skilabod = '';
      syndVilla(magnSkref, magnSkref.skilabod);
    }
    function setjaMagn(n) {
      if (!magnSkref) return;
      var lag = parseInt(magnSkref.el.getAttribute('data-lagmark'), 10) || 1;
      var ham = parseInt(magnSkref.el.getAttribute('data-hamark'), 10) || 99;
      if (!(n >= lag)) n = lag;
      var yfir = n > ham;
      n = Math.min(ham, n);
      magnSkref.inntak.value = n;
      magnSkref.skilabod = yfir ? fylla(ord.fjoldi_hamark, { n: ham }) : '';
      speglaMagn();
      if (yfir) tilkynna(magnSkref.skilabod, false);
      verdTexti();
      uppfaera();
    }
    if (magnSkref) $$('[data-vs-magn]', magnSkref.el).forEach(function (b) {
      b.addEventListener('click', function () { setjaMagn(magn() + parseInt(b.getAttribute('data-vs-magn'), 10)); });
    });

    /* ---- buy bar.
       Desktop (from 1001 px): the bar IS the price and add button row. It sticks to the foot of the
       screen while the config column reaches below it, rests in its own slot as the last row of the
       form (below the delivery line and the trust row), then leaves with the form. The price row and
       button inside .vs-kaupa are not shown there (CSS), so exactly one price and one button are on
       screen and nothing lies under the resting bar. data-limd marks the stuck state (shadow only).
       Phones and tablets: a fixed bar, hidden only while the price and the main button are both fully
       in view, below whatever covers the top (the fixed site header, the pinned preview) and above
       the bar's own place at the bottom. So price and button are never both out of sight. ---- */
    var kaupHnappur = kaupa ? $('[data-vs-hnappur]', kaupa) : null;
    var kaupVerd = kaupa ? $('[data-vs-verd]', kaupa) : null;
    var tolvuMidill = window.matchMedia ? window.matchMedia('(min-width: 1001px)') : null;
    function erTolva() { return tolvuMidill ? tolvuMidill.matches : window.innerWidth >= 1001; }
    var stikaSyn = null, stikaLimd = false;
    function setjaLimd(limd) {
      if (limd === stikaLimd) return;
      stikaLimd = limd;
      if (limd) kaupstika.setAttribute('data-limd', ''); else kaupstika.removeAttribute('data-limd');
    }
    function stikuSynileg(syn) {
      if (!kaupstika || syn === stikaSyn) return;
      stikaSyn = syn;
      if (syn) { kaupstika.removeAttribute('data-falin'); kaupstika.removeAttribute('aria-hidden'); kaupstika.removeAttribute('inert'); }
      else { kaupstika.setAttribute('data-falin', ''); kaupstika.setAttribute('aria-hidden', 'true'); kaupstika.setAttribute('inert', ''); }
      $$('button', kaupstika).forEach(function (b) { if (syn) b.removeAttribute('tabindex'); else b.setAttribute('tabindex', '-1'); });
    }
    /* samraeming C8: ONE price and ONE button, in the bar, always on screen on phones and desktop (the
       price row and button of .vs-kaupa are not shown, CSS). Desktop: the bar is sticky at the foot of the
       form column (stops where the form ends, never follows further down the page); data-limd (shadow)
       while it is stuck over content, not while it rests as the form's last row. The chat bubble keeps clear of it. */
        function metaStiku() {
      if (!kaupstika) return;
      stikuSynileg(true);
      if (erTolva()) {
        /* the bar is position:sticky at the foot of the form (CSS): it follows while the form reaches below
           the screen and rests as the form's last row. Shadow (data-limd) only while it is stuck over content. */
        var fm = kaupstika.parentNode ? kaupstika.parentNode.getBoundingClientRect() : null, vh = window.innerHeight || document.documentElement.clientHeight;
        setjaLimd(!!(fm && fm.height > 0 && fm.bottom > vh + 1));
      } else setjaLimd(false);
      spjallBoli();
    }

    /* ---- header height: the preview fills the screen below the visible header ---- */
    var hausSidast = -1, fastSidast = -1;
    function festurHaus() {
      var x = document.elementFromPoint(Math.round(window.innerWidth / 2), 2);
      while (x && x !== document.body && x !== document.documentElement) {
        if (rot.contains(x)) return 0;
        var pos = window.getComputedStyle(x).position;
        if (pos === 'fixed' || pos === 'sticky') {
          var r = x.getBoundingClientRect();
          return r.top <= 1 && r.height < window.innerHeight / 3 ? Math.max(0, r.bottom) : 0;
        }
        x = x.parentElement;
      }
      return 0;
    }
    function maelaHaus() {
      var toppur = rot.getBoundingClientRect().top;
      var fast = toppur > 0 ? 0 : festurHaus();
      var h = Math.round(Math.max(fast, Math.min(Math.max(0, toppur), window.innerHeight * 0.6)));
      if (h !== hausSidast) { hausSidast = h; rot.style.setProperty('--vs-header-h', h + 'px'); }
      if (fast !== fastSidast) { fastSidast = fast; rot.style.setProperty('--vs-fast', Math.round(fast) + 'px'); }
    }
    /* one frame per scroll burst: header height (skipped once settled with no fixed header) and the buy bar */
    var rammiBidur = false;
    /* SÍMABÓLA 2 23.10: while the sticky preview is pushed up by the end of the form, the buy bar (resting as the last row of the form)
       lies over the phone bubble; data-ytt on the preview hides the bubble (sm-vs-bola.css) until the preview is back in place */
    function yttBola() {
      if (!svid || !rot.hasAttribute('data-takkar-bola-simi')) return;
      /* the bar starts to lie over the bubble about 100 px before the preview itself is pushed, so the test is the bar's box against
         the preview's box (both measured in this frame), not the preview's top */
      var er = false;
      if (window.innerWidth < 1001 && kaupstika && window.getComputedStyle(svid).position === 'sticky') {
        var ks = kaupstika.getBoundingClientRect(), ss = svid.getBoundingClientRect();
        er = ks.height > 0 && ks.top < ss.bottom - 2 && ks.bottom > ss.top + 2;
      }
      if (er !== svid.hasAttribute('data-ytt')) { if (er) svid.setAttribute('data-ytt', ''); else svid.removeAttribute('data-ytt'); }
    }
    function iRamma() {
      rammiBidur = false;
      if (!(hausSidast === 0 && fastSidast === 0 && rot.getBoundingClientRect().top < -50)) maelaHaus();
      metaStiku();
      yttBola();
      umsagnaGluggi();
      if (tilkFost && kvilla && !kvilla.hidden && obyrgt(kvilla)) feltaVillu();
    }
    function aSkrun() {
      if (rammiBidur) return;
      rammiBidur = true;
      (window.requestAnimationFrame || setTimeout)(iRamma);
    }
    window.addEventListener('scroll', aSkrun, { passive: true });
    window.addEventListener('resize', function () { hausSidast = -2; aSkrun(); });
    /* steps opening, an error line or the preview changing size move the buy area without a scroll */
    if ('ResizeObserver' in window) {
      var stikuRo = new ResizeObserver(aSkrun);
      stikuRo.observe(rot);
      if (svid) stikuRo.observe(svid);
      if (kaupa) stikuRo.observe(kaupa);
    }

    /* ---- the Judge.me review popup (fixed, bottom left, phones and tablets only) ----
       CSS lifts it above the fixed bar. Here it steps aside when it would lie over the main price or
       button, or over the control that has keyboard focus: the nearest free place above or below them,
       between the top cover and the bar. */
    var gluggiBotn = '';
    function umsagnaGluggi() {
      var g = document.querySelector('.jdgm-popup-widget__cards-container');
      if (!g) return;
      var botn = '', fela = false;
      if (!erTolva() && kaupHnappur && kaupVerd && kaupstika) {
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var gh = Math.max(g.offsetHeight, 147), bil = 12;
        if (gluggiBotn) g.style.bottom = '';
        var sjalfgefid = parseFloat(window.getComputedStyle(g).bottom) || 88;
        var gl = g.getBoundingClientRect().left, gr = gl + Math.max(g.offsetWidth, 360);
        var efst = Math.max(0, fastSidast);
        if (svid) { var s = svid.getBoundingClientRect(); if (s.bottom > efst && s.left < gr && s.right > gl) efst = s.bottom; }
        var nedst = vh - kaupstika.offsetHeight;
        var hindr = [];
        var baeta = function (r) {
          var t = Math.max(r.top, efst), b = Math.min(r.bottom, nedst);
          if (b > t && r.right > gl && r.left < gr) hindr.push([t, b]);
        };
        var v = kaupVerd.getBoundingClientRect(), h = kaupHnappur.getBoundingClientRect();
        baeta({ top: Math.min(v.top, h.top), bottom: Math.max(v.bottom, h.bottom), left: Math.min(v.left, h.left), right: Math.max(v.right, h.right) });
        var a = document.activeElement;
        if (a && a !== document.body && rot.contains(a) && !(svid && svid.contains(a)) && !kaupstika.contains(a)) {
          var am = (a.type === 'radio' || a.type === 'checkbox') && a.closest ? (a.closest('label') || a) : a;
          baeta(am.getBoundingClientRect());
        }
        var frjals = function (topp) {
          if (topp < efst + bil || topp + gh > nedst - bil + 1) return false;
          for (var i = 0; i < hindr.length; i++) if (topp < hindr[i][1] + bil && topp + gh > hindr[i][0] - bil) return false;
          return true;
        };
        var heima = vh - sjalfgefid - gh;
        if (!frjals(heima) && hindr.length) {
          var kostir = [];
          hindr.forEach(function (o) { kostir.push(o[0] - bil - gh, o[1] + bil); });
          kostir.sort(function (x, y) { return Math.abs(x - heima) - Math.abs(y - heima); });
          for (var k = 0; k < kostir.length; k++) {
            if (frjals(kostir[k])) { botn = Math.round(vh - kostir[k] - gh) + 'px'; break; }
          }
          /* no free place (low landscape phones, where the pinned preview and the bar leave too little
             room): rather than lie over the focused control, the price or the button, the popup steps
             out of sight until there is room again */
          if (!botn) for (var z = 0; z < hindr.length; z++) if (heima < hindr[z][1] && heima + gh > hindr[z][0]) fela = true;
        }
      }
      if (g.style.bottom !== botn) g.style.bottom = botn;
      var sjon = fela ? 'hidden' : '';
      if (g.style.visibility !== sjon) g.style.visibility = sjon;
      gluggiBotn = botn;
    }
    /* the popup comes and goes on its own timer: place it when it is added to the page */
    if ('MutationObserver' in window) {
      var gluggiMo = new MutationObserver(function () {
        if (document.querySelector('.jdgm-popup-widget__cards-container')) { gluggiMo.disconnect(); aSkrun(); }
      });
      gluggiMo.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () { gluggiMo.disconnect(); }, 60000);
    }

    /* ---- a focused control is never left under the bar, the pinned preview or a fixed header
       (WCAG 2.2 2.4.11). CSS scroll-padding-bottom already makes browsers that honour it scroll far
       enough; this covers the rest and the top edge. Keyboard focus only: a click or tap lands on
       something already visible, and the kit's own jumps to a step scroll by themselves. ---- */
    var bendill = 0;
    document.addEventListener('pointerdown', function () { bendill = Date.now(); }, true);
    function fokusSest(el) {
      if (document.activeElement !== el || !el.getBoundingClientRect) return;
      if ((kaupstika && kaupstika.contains(el)) || (svid && svid.contains(el))) return;
      for (var x = el; x && x !== document.body; x = x.parentElement) {
        if (window.getComputedStyle(x).position === 'fixed') return;
      }
      var m = (el.type === 'radio' || el.type === 'checkbox') && el.closest ? (el.closest('label') || el) : el;
      var r = m.getBoundingClientRect();
      if (!r.height) return;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var efst = Math.max(0, fastSidast), nedst = vh, bil = 12;
      if (svid && rot.contains(el)) {
        var s = svid.getBoundingClientRect();
        if (s.bottom > efst && s.left < r.right && s.right > r.left) efst = s.bottom;
      }
      if (kaupstika && stikaSyn !== false) {
        var b = kaupstika.getBoundingClientRect();
        if (b.bottom >= vh - 1 && b.top > efst && b.left < r.right && b.right > r.left) nedst = b.top;
      }
      if (r.height > nedst - efst - 2 * bil) return;
      var d = 0;
      if (r.bottom > nedst - 2) d = r.bottom - nedst + bil;
      else if (r.top < efst + 2) d = r.top - efst - bil;
      if (d) window.scrollBy(0, Math.round(d));
    }
    document.addEventListener('focusin', function (e) {
      var el = e.target;
      if (!el || el === document.body || el === document.documentElement) return;
      var lykill = !(Date.now() - bendill < 700 || Date.now() - sjalfSkrun < 1200);
      (window.requestAnimationFrame || setTimeout)(function () { if (lykill) fokusSest(el); aSkrun(); });
    });

    /* ---- add to cart ---- */
    function uppteknir(syn, texti) {
      hnappar.forEach(function (b) {
        if (syn) {
          b.setAttribute('aria-busy', 'true');
          b.setAttribute('aria-disabled', 'true');
          var te = $('[data-vs-hnappur-texti]', b);
          if (te) te.textContent = texti;
        } else {
          b.removeAttribute('aria-busy');
          b.removeAttribute('aria-disabled');
        }
      });
    }
    /* the add button the customer sees: the main one on phones, the bar on desktop */
    function synilegurHnappur() {
      for (var i = 0; i < hnappar.length; i++) {
        var b = hnappar[i];
        if (b.getClientRects().length && !(b.closest && b.closest('[data-vs-kaupstika][data-falin]'))) return b;
      }
      return hnappar[0];
    }
    /* whole in view: below the fixed header and the pinned preview, above the bar where it lies */
    function obyrgt(el) {
      var r = el.getBoundingClientRect();
      if (!r.height) return false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var efst = Math.max(0, fastSidast), nedst = vh;
      if (svid) {
        var s = svid.getBoundingClientRect();
        if (s.bottom > efst && s.left < r.right && s.right > r.left) efst = s.bottom;
      }
      if (kaupstika && stikaSyn !== false) {
        var b = kaupstika.getBoundingClientRect();
        if (b.bottom >= vh - 1 && b.top < vh && b.top > r.top && b.left < r.right && b.right > r.left) nedst = b.top;
      }
      return r.top >= efst - 1 && r.bottom <= nedst + 1;
    }
    /* kvillaStopp: the message came from a stop without a field (sold out, missing combination); it goes
       as soon as that stop no longer holds, as on the old pages (uppfaera) */
    var kvillaStopp = '';
    function kaupaVilla(texti, stopp) {
      kvillaStopp = stopp && texti ? texti : '';
      if (kvilla) { kvilla.textContent = texti; kvilla.hidden = !texti; }
      if (!texti) { feltaVillu(true); return; }
      /* the toast shows only when the message line in the buy area is out of sight (on desktop it sits
         above the bar's resting place, far below a bar stuck at the foot of the screen); then it stays */
      var sest = !!kvilla && obyrgt(kvilla);
      tilkynna(texti, !sest, true, !sest);
    }
    function sidustuLysing(lina, eig) {
      var hlutar = [], athsEig = {};
      skref.forEach(function (s) { if (s.gerd === 'aths' && s.eig) athsEig[s.eig] = true; });
      /* P2-V04 (21.10): the variant's option values are left out of the sheet line when the readable properties below already say
         the same (size, material, colour, frame) and when they are bare yes/no values, so the line does not repeat itself */
      var eigGildi = [];
      Object.keys(eig).forEach(function (k) {
        var v = eig[k];
        if (k.charAt(0) !== '_' && typeof v === 'string' && !/^https?:/.test(v)) eigGildi.push(v.toLowerCase());
      });
      var afbHrar = afb && afb.heiti && afb.heiti !== 'Default Title' ? (window.SMValkostir && afb.n && afb.n.length ? afb.n : [afb.heiti]) : [];
      var afbHlutar = [];
      afbHrar.forEach(function (x) {
        var lx = String(x).toLowerCase().trim();
        if (!lx || /^(nei|j\u00e1|yes|no)$/.test(lx)) return;
        if (eigGildi.some(function (g) { return g.indexOf(lx) !== -1 || (g.length > 2 && lx.indexOf(g) !== -1); })) return;
        afbHlutar.push(window.SMValkostir && afb.n && afb.n.length ? syn(x, nuVara().handle, 'v') : x);
      });
      if (afbHlutar.length) hlutar.push(afbHlutar.join(' / '));
      Object.keys(eig).forEach(function (k) {
        var v = eig[k];
        if (k.charAt(0) === '_' || k === 'Athugasemdir me\u00f0 p\u00f6ntun' || athsEig[k] || typeof v !== 'string' || /^https?:/.test(v)) return;
        hlutar.push(k + ': ' + (v.length > 40 ? v.slice(0, 39) + '\u2026' : v));
      });
      var q = lina && lina.__magn > 1 ? stkTexti(lina.__magn) : '';
      if (q) hlutar.push(q);
      return hlutar.join(' \u00b7 ');
    }
    /* ---- B13 (17.10): "Annað skilti eins" ----
       After a line is in the cart, a button on the buy bar (and in the cart sheet) starts the next sign with the
       same choices: size, colour, material, fixing, frame and the other options stay, the sign's own text is
       emptied (the street or address of the building stays, so the next sign of the same house only needs its
       own text) and the cursor goes to the first emptied field. Quantity goes back to its lowest. Nothing is
       added by itself: the customer writes the text and adds the line as before, so the line and the production
       file are made by the same code. Not on door signs (owner: the door sign page stays as it is). */
    var ANNAD_VELAR = { husfelag: 'skilti', husnumer: 'skilti', herbergi: 'skilti', oryggisskilti: 'skilti', wc: 'skilti', barmmerki: 'merki', toskumerki: 'merki' };
    var ANNAD_HALDA = { husfelag: { sel: '[data-hf-gata]' }, husnumer: { skref: ['texti'] }, toskumerki: { skref: ['bak'] } };
    var annadGerd = ANNAD_VELAR[gogn.adapter] || '';
    var annadHnappar = [];
    function annadTexti() { return ord.annad_eins || ord['annad_eins_' + (annadGerd === 'merki' ? 'merki' : 'skilti')] || (annadGerd === 'merki' ? 'Annað merki eins' : 'Annað skilti eins'); }
    function annadInntok() {
      var halda = ANNAD_HALDA[gogn.adapter] || {}, ut = [];
      skref.forEach(function (s) {
        if (s.el.hidden || /^(magn|aths|skra|val|valkostur|rammi|vara)$/.test(s.gerd)) return;
        if (halda.skref && halda.skref.indexOf(s.lykill) !== -1) return;
        $$('input, textarea', s.el).forEach(function (x) {
          if (x.disabled || x.readOnly || x.closest('[hidden]') || x.hasAttribute('data-vs-magn-tala')) return;
          if (x.tagName === 'INPUT' && !/^(text|search|tel|number)?$/.test(x.type)) return;
          if (halda.sel && x.matches(halda.sel)) return;
          ut.push({ s: s, el: x });
        });
      });
      return ut;
    }
    function annadSmida(stadur) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'vs-annad vs-annad--' + stadur;
      b.setAttribute('data-vs-annad', stadur);
      b.hidden = true;
      b.innerHTML = '<span class="vs-annad-plus" aria-hidden="true">+</span><span>' + esc(annadTexti()) + '</span>';
      b.addEventListener('click', annadByrja);
      annadHnappar.push(b);
      return b;
    }
    function annadSyna() {
      if (!annadGerd) return;
      if (!annadHnappar.length) {
        /* the bar is the one price and button on every width (.vs-kaupa hides its own, CSS) */
        if (kaupstika) kaupstika.appendChild(annadSmida('stika'));
      }
      annadHnappar.forEach(function (b) { b.hidden = false; });
      if (kaupstika) kaupstika.setAttribute('data-annad', '');
      /* the cart sheet: the same button above "Halda áfram" / "Sjá körfu" */
      var sh = $('[data-sm-cart-sheet]'), act = sh && $('.cart-sheet-actions', sh);
      if (act) {
        var gamall = $('[data-vs-annad=bordi]', sh);
        if (gamall) gamall.parentNode.removeChild(gamall);
        var bb = document.createElement('button');
        bb.type = 'button';
        bb.className = 'btn btn-ghost cart-sheet-annad';
        bb.setAttribute('data-vs-annad', 'bordi');
        bb.innerHTML = '<span aria-hidden="true">+</span> ' + esc(annadTexti());
        bb.addEventListener('click', function () {
          if (window.SMKarfa && window.SMKarfa.loka) window.SMKarfa.loka();
          if (bb.parentNode) bb.parentNode.removeChild(bb);
          skilaFokus = null;
          setTimeout(annadByrja, 30);
        });
        act.parentNode.insertBefore(bb, act);
        if (!sh.__vsAnnadMo && 'MutationObserver' in window) {
          sh.__vsAnnadMo = new MutationObserver(function () {
            if (sh.classList.contains('open')) return;
            var x = $('[data-vs-annad=bordi]', sh);
            if (x) x.parentNode.removeChild(x);
          });
          sh.__vsAnnadMo.observe(sh, { attributes: true, attributeFilter: ['class'] });
        }
      }
    }
    function annadByrja() {
      var inn = annadInntok(), fyrsta = null;
      inn.forEach(function (x) {
        if (x.el.value === '') return;
        x.el.value = '';
        if (!fyrsta) fyrsta = x;
        try { x.el.dispatchEvent(new Event('input', { bubbles: true })); x.el.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
      });
      if (!fyrsta && inn.length) fyrsta = inn[0];
      reynt = false;
      skref.forEach(function (s) { s.farid = false; if (s.gerd !== 'magn') s.skilabod = ''; });
      kaupaVilla('');
      if (magnSkref) setjaMagn(1);
      annadHnappar.forEach(function (b) { b.hidden = true; });
      if (kaupstika) kaupstika.removeAttribute('data-annad');
      samstilla(true);
      teikna();
      if (fyrsta) {
        var y = window.pageYOffset + fyrsta.s.el.getBoundingClientRect().top - ofan();
        sjalfSkrun = Date.now();
        scrollTil(y);
        try { fyrsta.el.focus({ preventScroll: true }); } catch (e) { try { fyrsta.el.focus(); } catch (e2) {} }
      } else {
        /* nothing to empty (e.g. a common area sign with a fixed text): the next sign is a new choice in the first step */
        var s1 = null;
        skref.forEach(function (s) { if (!s1 && !s.el.hidden && s.gerd !== 'magn' && s.gerd !== 'aths') s1 = s; });
        var val1 = s1 ? $$('input[type=radio]:checked', s1.el).filter(function (r) { return !r.closest('[hidden]'); })[0] : null;
        if (val1) {
          var y1 = window.pageYOffset + s1.el.getBoundingClientRect().top - ofan();
          sjalfSkrun = Date.now();
          scrollTil(y1);
          try { val1.focus({ preventScroll: true }); } catch (e) {}
        } else if (s1) fokusa(s1);
      }
      tilkynna(ord.annad_eins_tilkynning || ord['annad_tilk_' + (fyrsta ? 'skrifa' : 'veldu') + '_' + (annadGerd === 'merki' ? 'merki' : 'skilti')] || (fyrsta ? 'Sömu stillingar. Skrifaðu textann á næsta ' + (annadGerd === 'merki' ? 'merki.' : 'skilti.') : 'Sömu stillingar. Veldu næsta ' + (annadGerd === 'merki' ? 'merki.' : 'skilti.')), false);
    }

    /* C1 (17.10, styttri fost forskodun a sima eftir skref 1) tekid ut 18.10 a ollum vorum (eigandi: "thad minnkar glugginn
       vid skrun, galli"): fasta forskodunin heldur somu haed vid skrun, eins og hurdaskiltid. */
    function komid(lina, eig, heildar) {
      var karfaSlod = (gogn.karfa && gogn.karfa.karfa) || '/cart';
      var smam = kalla(adapter, 'smamynd', [stada(), ctx], null);
      /* CK 19.10: door signs give { html }, house numbers an HTML string as svg; both are drawn as HTML */
      var smHtml = smam ? (smam.html || (smam.svg && !erSvg(smam.svg) ? smam.svg : '')) : '';
      var opna = function (cart) {
        if (cart && typeof cart.item_count === 'number') {
          $$('[data-sm-cart-count]').forEach(function (c) { c.textContent = cart.item_count; });
        }
        try { document.dispatchEvent(new CustomEvent('sm:karfa', { detail: { fjoldi: cart ? cart.item_count : null, lina: lina } })); } catch (e) {}
        if (window.SMKarfa && window.SMKarfa.opna) {
          var tk = null;
          if (smHtml) {
            tk = document.createElement('span');
            tk.className = 'vs-karfa-teikning';
            tk.style.cssText = 'display:grid;place-items:center;width:100%;height:100%;background:#fff;padding:4px;box-sizing:border-box;overflow:hidden';
            hreinsaInn(tk, smHtml);
          } else if (smam && smam.svg) {
            /* the page's own drawing as the thumbnail (a DOM node: SMKarfa.opna takes it as it is) */
            tk = document.createElement('span');
            tk.className = 'vs-karfa-teikning';
            tk.style.cssText = 'display:grid;place-items:center;width:100%;height:100%;background:#fff;padding:4px;box-sizing:border-box';
            hreinsaInn(tk, smam.svg);
          }
          window.SMKarfa.opna({
            title: (lina && lina.product_title) || gogn.vara.titill,
            spec: sidustuLysing(lina, eig),
            price: kr(heildar),
            teikning: tk,
            image: (smam && smam.src) || (lina && lina.image) || '',
            fokus: true
          });
        }
      };
      fetch(karfaSlod + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(opna, function () { opna(null); })
        .then(function () { try { annadSyna(); } catch (e) {} });
      /* B1: the design as the cart picture (localStorage, never a line property) */
      /* the key: the line's production token _framleidsla_nr (a line key changes when a bulk discount comes on), else the line key */
      var myndLykill = lina && ((lina.properties && lina.properties._framleidsla_nr) || lina.key);
      if (smam && (smam.svg || smHtml) && myndLykill) {
        try {
          (smHtml ? htmlMynd(smHtml, rot) : rastaSvg(smam.svg)).then(function (u) { karfaMyndGeyma(String(myndLykill), u); }, function (e) { try { if (window.console) console.info('[SMVelSida] körfumynd: ' + (e && e.message)); } catch (e2) {} });
        } catch (e) {}
      }
      hnappar.forEach(function (b) {
        b.setAttribute('data-stada', 'komid');
        var te = $('[data-vs-hnappur-texti]', b);
        if (te) te.textContent = ord.komid;
      });
      tilkynna(ord.komid_tilkynning, false);
      clearTimeout(komidTimi);
      komidTimi = setTimeout(function () {
        hnappar.forEach(function (b) { b.removeAttribute('data-stada'); });
        uppfaera();
      }, 2400);
    }
    /* production data for this add (see FRAM_TEXTI): appends _framleidsla, and the file and its token
       when built in time. Resolves true when the file is in fd. Never rejects. */
    var framK = FRAM_TEXTI[gogn.vara && gogn.vara.handle] || null;
    /* FB 21.10 (C2, rule of the kit from now: NEVER a cart line without a valid production file when the design has one).
       A character the font lacks stops the add with its name (like the emoji rule); a font or file that cannot be made stops it
       with a short message; a file that was not ready in time is tried once more with more time (senda), then stops. */
    function stafBirta(c) {
      if (/^[\u0300-\u036f\u1ab0-\u1aff\u20d0-\u20ff\ufe20-\ufe2f]$/.test(c) || !trim(c)) return 'U+' + ('000' + c.charCodeAt(0).toString(16).toUpperCase()).slice(-4);
      return c;
    }
    function stafirUrVillu(e) {
      var m = /stafur-vantar:\s*\S+\s([\s\S]*)$/.exec(String((e && e.message) || ''));
      return m ? m[1] : '';
    }
    function stafaStopp(stafir) {
      var listi = [];
      String(stafir || '').replace(/[\ud800-\udbff][\udc00-\udfff]|[\s\S]/g, function (c) { var b = stafBirta(c); if (listi.indexOf(b) < 0) listi.push(b); return c; });
      var fleiri = listi.length > 1, texti = fleiri ? (ord.villa_stafir || 'Stafirnir {s} eru ekki til í letrinu. Veldu annað letur eða taktu þá út.') : (ord.villa_stafur || 'Stafurinn {s} er ekki til í letrinu. Veldu annað letur eða taktu hann út.');
      return { stopp: texti.replace('{s}', listi.join(' ')), kodi: 'stafur-vantar' };
    }
    function framStopp(e, spec) {
      var kodi = (e && e.kodi) || (typeof e === 'string' ? e : 'villa');
      if (kodi === 'timi') return Promise.resolve({ stopp: ord.villa_framskra_timi || 'Skiltið er ekki alveg tilbúið. Reyndu aftur eftir augnablik.', kodi: 'timi' });
      if (kodi === 'stafur-vantar') {
        var st = stafirUrVillu(e);
        if (st) return Promise.resolve(stafaStopp(st));
        /* the file maker said only "stafur-vantar": find the characters text by text */
        var SM = window.SMFram, hl = (spec && spec.hlutir) || [], fannst = '';
        if (!SM || !SM._innri || !SM._innri.uppsetning) return Promise.resolve(stafaStopp(''));
        return Promise.all(hl.map(function (h) {
          if (!h || h.gerd !== 'texti' || !trim(String(h.texti || ''))) return null;
          return SM._innri.uppsetning(h.letur, { texti: String(h.texti), mm: h.mm || 5 }).then(null, function (x) { if (x && x.kodi === 'stafur-vantar') fannst += stafirUrVillu(x); });
        })).then(function () { return stafaStopp(fannst); }, function () { return stafaStopp(fannst); });
      }
      return Promise.resolve({ stopp: ord.villa_framskra || 'Ekki tókst að útbúa framleiðsluskrá fyrir skiltið, svo það fór ekki í körfu. Reyndu aftur eða hafðu samband.', kodi: kodi });
    }
    function framleidsla(fd, eig, margf) {
      var SM = window.SMFram;
      if (!SM || !SM.json || !SM.baetaVid || !SM.lyklar || !afb) return Promise.resolve(false);
      var kJ = 'properties[' + SM.lyklar.json + ']', sp = null, grunnJson = '', bid = null, t0 = Date.now();
      try {
        sp = grunnSpec(SM, gogn, afb);
        if (adapter && typeof adapter.framleidsla === 'function') {
          grunnJson = SM.json(sp);
          bid = adapter.framleidsla(stada(), ctx, sp, eig);
          /* false: this line carries no production data at all (as the collar page today) */
          if (bid === false) return Promise.resolve(false);
        } else if (framK) {
          var texti = typeof eig[framK.eig] === 'string' ? trim(eig[framK.eig].replace(/\s+/g, ' ')) : '';
          if (texti) sp.gogn.nafn = texti;
          grunnJson = SM.json(sp);
          if (texti && SM._innri && SM._innri.uppsetning) bid = textaSpec(SM, sp, framK, texti);
        } else grunnJson = SM.json(sp);
      } catch (e) { framVidvorun('framleidsla: ' + (e && e.message)); return framStopp(e, null); }
      var lokaJson = function (r) {
        if (r && r.stopp) return r;
        if (grunnJson && !fdHefur(fd, kJ)) fd.append(kJ, grunnJson);
        return !!(r && r.skra);
      };
      if (!bid) return Promise.resolve(lokaJson(null));
      var FT = (adapter && adapter.framTimi > 0 ? adapter.framTimi : FRAM_TIMI) * (margf || 1), framSpec = null;
      return innan(Promise.resolve(bid), FT).then(function (spec) {
        if (!spec) return null;
        framSpec = spec;
        /* { stopp: texti }: the adapter refuses this add (v1.1) */
        if (spec.stopp) return { stopp: String(spec.stopp) };
        /* { json }: the JSON only, e.g. the old pages' fallback when the drawing could not be specified */
        if (spec.json && !spec.hlutir) return SM.baetaVid(fd, null, { skra: false, json: spec.json });
        var hlutir = !!(spec.hlutir && spec.hlutir.length), sj = null;
        /* json next to hlutir: the adapter's own _framleidsla, the file from the spec (v1.1, L3 K1) */
        if (spec.json != null) {
          sj = spec.json;
          var sp2 = {};
          for (var kk in spec) if (spec.hasOwnProperty(kk) && kk !== 'json') sp2[kk] = spec[kk];
          spec = sp2;
        }
        return SM.baetaVid(fd, spec, { tima: Math.max(1, FT - (Date.now() - t0)), skra: hlutir, json: sj }).then(function (r) {
          /* FB 21.10 (C2): the design has a file but it could not be made: the add stops (baetaVid already warned in the console) */
          if (hlutir && r && !r.skra) return framStopp(r.villa || 'villa', spec);
          return r;
        });
      }).then(null, function (e) {
        framVidvorun('framleidsluskra fylgir ekki (' + ((e && e.kodi) || 'villa') + '): ' + (e && e.message ? e.message : e));
        return framStopp(e, framSpec);
      }).then(lokaJson, function () { return lokaJson(null); });
    }
    /* fonts and libraries load when the customer starts typing the text that is cut, so the file is ready */
    if (framK && !(adapter && adapter.framleidsla)) {
      var framForhladid = false;
      form.addEventListener('focusin', function (e) {
        if (framForhladid || !window.SMFram || !window.SMFram.forhlada) return;
        var s = null;
        skref.forEach(function (x) { if (x.eig === framK.eig && x.inntak && x.inntak === e.target) s = x; });
        if (!s) return;
        framForhladid = true;
        try { window.SMFram.forhlada([framK.letur]); } catch (er) {}
      });
    }

    /* "Kaupa núna" (18.10): eftir körfuflæðið fer síðan beint í greiðslu (karfan fylgir með). knNy: línan með þessu vali
       er þegar komin í körfu (Bæta í körfu eða Kaupa núna) og ekkert hefur breyst síðan, svo smellur bætir ekki við
       annarri eins línu heldur fer beint í greiðslu. */
    var knNy = false, knTexti = knHnappur ? $('[data-vs-kaupa-nuna-texti]', knHnappur) : null, knTextiUpph = knTexti ? knTexti.textContent : '';
    form.addEventListener('input', function () { knNy = false; }, true);
    form.addEventListener('change', function () { knNy = false; }, true);
    form.addEventListener('click', function (e) { if (e.target && e.target.closest && e.target.closest('[data-vs-magn], .vs-hurd-takki, [data-vs-annad]')) knNy = false; }, true);
    function knUpptekinn(ja) {
      if (!knHnappur) return;
      if (ja) { knHnappur.setAttribute('aria-busy', 'true'); knHnappur.setAttribute('aria-disabled', 'true'); if (knTexti) knTexti.textContent = ord.kaupa_nuna_fer || knTextiUpph; }
      else { knHnappur.removeAttribute('aria-busy'); knHnappur.removeAttribute('aria-disabled'); if (knTexti) knTexti.textContent = knTextiUpph; }
    }
    window.addEventListener('pageshow', function (e) { if (e.persisted) { knUpptekinn(false); sendir = false; uppteknir(false); } });
    function kassaSlod() { return ((gogn.karfa && gogn.karfa.karfa) || '/cart').replace(/\/cart\/?$/, '') + '/checkout'; }
    function knFara(lina) {
      knUpptekinn(true);
      /* B1: hönnunin sem körfumynd, eins og eftir Bæta í körfu (mest 1,5 s bið, svo er farið hvort sem er) */
      var smam = null, lyk = lina && ((lina.properties && lina.properties._framleidsla_nr) || lina.key), p = Promise.resolve();
      try { smam = kalla(adapter, 'smamynd', [stada(), ctx], null); } catch (e) { smam = null; }
      var smH = smam ? (smam.html || (smam.svg && !erSvg(smam.svg) ? smam.svg : '')) : '';
      if (smam && (smam.svg || smH) && lyk) { try { p = (smH ? htmlMynd(smH, rot) : rastaSvg(smam.svg)).then(function (u) { karfaMyndGeyma(String(lyk), u); }); } catch (e) {} }
      var fara = function () { window.location.href = kassaSlod(); };
      innan(p, 1500).then(fara, fara);
    }
    function senda(takki) {
      if (sendir) return;
      if (adapterVantar) { kaupaVilla(ord.villa_hlada || 'Ekki t\u00f3kst a\u00f0 hla\u00f0a h\u00f6nnuninni. Hladdu s\u00ed\u00f0una aftur e\u00f0a haf\u00f0u samband.'); return; }
      var kaupaNu = !!(knHnappur && takki === knHnappur);
      if (kaupaNu && knNy) { knFara(null); return; }
      reynt = true;
      kaupaVilla('');
      var v = uppfaera().filter(function (x) { return x.stopp; });
      if (v.length) {
        var f = v[0];
        if (f.kodi === 'skra' && window.SMSkra) { window.SMSkra.athuga(form); tilkynna(f.hnappur, false); return; }
        tilkynna(f.texti || f.hnappur, !f.s, !f.s);
        if (f.s) fokusa(f.s, f.kodi);
        else if (f.texti) kaupaVilla(f.texti, true);
        return;
      }
      var skraSkref = skref.filter(function (s) { return s.gerd === 'skra' && !s.el.hidden; });
      if (skraSkref.length && window.SMSkra && !window.SMSkra.athuga(form)) return;

      var q = magn();
      var fd = new FormData();
      fd.append('id', String(afb.id));
      fd.append('quantity', String(q));
      var eig = {}, kitEig = [];
      skref.forEach(function (s) {
        if (s.el.hidden || !s.eig) return;
        var gildi = '';
        if (s.gerd === 'val') { var r = valinnKostur(s); gildi = r ? (s.birting === 'letur' ? r.getAttribute('data-nafn') || '' : r.value) : ''; }
        else if (s.gerd === 'rammi') { var rr = valinnKostur(s); gildi = rr ? rr.getAttribute('data-nafn') || '' : ''; }
        else if ((s.gerd === 'texti' || s.gerd === 'aths') && s.inntak) {
          gildi = trim(s.inntak.value);
          if (s.hastafir === 'allir') gildi = gildi.toUpperCase();
          else if (s.hastafir) gildi = hastafir(gildi);
          if (s.inntak.value !== gildi) s.inntak.value = gildi;
        }
        if (!gildi) return;
        kitEig.push([s.eig, gildi]);
      });
      var auka = kalla(adapter, 'eiginleikar', [stada(), ctx], {}) || {};
      /* an array from the adapter is the whole visible list in its order (E8); else the steps, then the extras */
      var listi = auka.length != null && typeof auka !== 'string' ? auka : kitEig.concat(Object.keys(auka).map(function (k) { return [k, auka[k]]; }));
      listi = eigRammiLetur(listi, auka.length != null && typeof auka !== 'string');
      listi.forEach(function (x) {
        if (!x || x[1] === '' || x[1] == null) return;
        var g = String(x[0]).charAt(0) === '_' ? String(x[1]) : hreinsaEig(x[1]);
        if (g === '') return;
        eig[x[0]] = g;
        fd.append('properties[' + x[0] + ']', g);
      });
      var skrar = window.SMSkra && skraSkref.length ? window.SMSkra.fylla(fd, form) : 0;
      (kalla(adapter, 'skrar', [stada(), ctx], []) || []).forEach(function (x) {
        if (x && x.skra) { fd.append('properties[' + x.eiginleiki + ']', x.skra, x.skra.name); skrar += 1; }
      });

      sendir = true;
      skilaFokus = takki || synilegurHnappur();
      uppteknir(true, skrar ? ord.sendir_skra : ord.sendir);
      if (kaupaNu) knUpptekinn(true);
      var heildar = (afb.verd + aukaVerd()) * q;
      var aukalinur = kalla(adapter, 'aukalinur', [stada(), ctx], []) || [];
      var baeta = ((gogn.karfa && gogn.karfa.baeta) || '/cart/add') + '.js';
      var karfaJs = ((gogn.karfa && gogn.karfa.karfa) || '/cart') + '.js';
      var lokid = function () { sendir = false; uppteknir(false); knUpptekinn(false); uppfaera(); };
      var fram;
      try { fram = framleidsla(fd, eig); } catch (e) { fram = Promise.resolve(false); }
      /* FB 21.10 (C2): a file that was not ready in time (slow phone, fonts still loading) is made once more with four times the time */
      fram = fram.then(function (r) { return r && r.stopp && r.kodi === 'timi' ? framleidsla(fd, eig, 4) : r; });
      fram
        .then(function (medSkra) {
          if (medSkra && medSkra.stopp) throw { vsStopp: medSkra.stopp };
          /* with the production file: SMFram.senda (XMLHttpRequest, bounded, never twice); else as before */
          if (medSkra && window.SMFram && window.SMFram.senda) return window.SMFram.senda(fd, { url: baeta, karfa: karfaJs });
          return fetch(baeta, { method: 'POST', body: fd, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' });
        })
        .then(function (r) {
          return r.text().then(function (t) {
            var j = null;
            try { j = t ? JSON.parse(t) : null; } catch (e) { j = null; }
            return { stada: r.status, ok: r.ok, j: j };
          });
        })
        .then(function (svar) {
          sendir = false;
          uppteknir(false);
          if (svar.ok && svar.j && !svar.j.status) {
            var lina = svar.j.items ? svar.j.items[0] : svar.j;
            if (lina) lina.__magn = q;
            knNy = true;
            if (kaupaNu) {
              try { document.dispatchEvent(new CustomEvent('sm:karfa', { detail: { fjoldi: null, lina: lina, kaupaNuna: true } })); } catch (e) {}
              if (aukalinur.length) return aukaSenda(aukalinur, q, baeta).then(function () { knFara(lina); });
              knFara(lina);
              return;
            }
            if (aukalinur.length) return aukaSenda(aukalinur, q, baeta).then(function () { komid(lina, eig, heildar); });
            komid(lina, eig, heildar);
            return;
          }
          knUpptekinn(false);
          var texti;
          if (svar.stada === 413) texti = fylla(ord.villa_413, { mb: window.SMSkra ? window.SMSkra.hamarkTexti(form) : '19 MB' });
          else if (svar.stada === 429) texti = ord.villa_429;
          else if (svar.stada === 422 || svar.stada === 400) {
            var lys = svar.j && (svar.j.description || svar.j.message);
            texti = lys && typeof lys === 'string' ? lys : ord.villa_karfa;
          } else texti = ord.villa_almenn;
          if (skrar && window.SMSkra && svar.stada === 413) window.SMSkra.villa(form, texti);
          kaupaVilla(texti);
          uppfaera();
        }, function (e) {
          lokid();
          if (e && e.vsStopp) kaupaVilla(e.vsStopp);
          else kaupaVilla(ord.villa_net);
        });
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      e.stopPropagation();
      senda(e.submitter || null);
    });

    /* focus back to the button after the cart sheet closes */
    var sheet = $('[data-sm-cart-sheet]');
    if (sheet && 'MutationObserver' in window) {
      new MutationObserver(function () {
        if (!skilaFokus || sheet.classList.contains('open')) return;
        var a = document.activeElement;
        if (!a || a === document.body || sheet.contains(a)) {
          var mark = skilaFokus;
          if (!mark.getClientRects().length || (mark.closest && mark.closest('[data-vs-kaupstika][data-falin]'))) mark = synilegurHnappur();
          try { mark.focus({ preventScroll: true }); } catch (e) {}
        }
        skilaFokus = null;
      }).observe(sheet, { attributes: true, attributeFilter: ['class'] });
    }

    /* one redraw per animation frame while typing */
    var teiknaBidur = false;
    function teiknaSeinna() {
      if (teiknaBidur) return;
      teiknaBidur = true;
      (window.requestAnimationFrame || setTimeout)(function () { teiknaBidur = false; teikna(); });
    }
    /* fonts and libraries for the production file: the adapter decides when (E9) */
    if (typeof adapter.forhlada === 'function') {
      var forhlada = function () { kalla(adapter, 'forhlada', [stada(), ctx], null); };
      form.addEventListener('focusin', forhlada);
      form.addEventListener('pointerdown', forhlada, { passive: true });
    }

    /* ---- start ---- */
    var upphafHafdiSlod = !!window.location.search;
    rot.setAttribute('data-vs-tilbuin', '');
    if (lysUpphFannst) kodarIRadio();
    if (fjol && upphafH !== nuHandle) skiptaVoru(upphafH, slodParam('variant'));
    kalla(adapter, 'upphaf', [ctx], null);
    /* B1 23.10 (Google: a variant URL must open on its variant, with its price): an adapter whose own state picks the variant
       (afbrigdi hook: door signs, house numbers, crosses) gets the variant the link asked for (?variant=<id> or the readable
       params, the JSON-LD hasVariant URLs) before the first sync, so the engine, preview, price, toggles and the id in the form
       all start on it and the address written after boot says the same. skyr[j] = the link named option j itself (null =
       every option, ?variant=). Not called on a plain /products/<handle> load, so the default view is unchanged. */
    var forvalA = null;
    if (ctx.afbUrSlod && afb) (nuVara().afbrigdi || []).forEach(function (x) { if (!forvalA && String(x.id) === String(afb.id)) forvalA = x; });
    if (forvalA) kalla(adapter, 'forval', [forvalA, lysUpphFannst ? lysSkyr : null, ctx], null);
    var hl = kalla(adapter, 'hlada', [ctx], null);
    /* ?rammi=1|0 (the frame, as chosen before a reload or a shared link) */
    var rSlod = slodParam('rammi'), rUpph = rammiSkref();
    if (rUpph && (rSlod === 'med' || rSlod === 'an' || rSlod === '1' || rSlod === '0')) { setjaRammiGildi(rUpph, (rSlod === 'med' || rSlod === '1') ? 'med' : 'an'); rammiSnert = true; }
    else if (rUpph && afb && slodParam('variant') && String(afb.id) === String(slodParam('variant'))) {
      /* ?variant= of a frame variant and no rammi=: the linked variant is a frame choice (v1.1) */
      var rStada = stada();
      if (rammiValkostir(nuVara(), rStada).some(function (t) { return t.med && t.med !== t.an && afb.o[t.p0] === t.med; })) { setjaRammiGildi(rUpph, 'med'); rammiSnert = true; }
    }
    samstilla(false);
    /* lysandi slod (K3): an incoming link with a query string (?variant=<id>, an old readable link, a
       stale combination) is rewritten to the current canonical readable form once, right after boot */
    if (upphafHafdiSlod) lysandiSlodSkrifa();
    /* (samraeming E3) a ?rammi= from an old link on a sign without a frame (or on a kit with no frame step at all) is dropped from the address even
       when the variant does not change (the round above only writes the address on a customer's change) */
    var rammiSlodHreinsa = function () {
      if (!FALIN_OVIRK || rSlod == null || (rUpph && rammiGerd(stada()) && !rUpph.el.__vsEkki) || !window.history || !history.replaceState) return;
      try {
        var uR = new URL(window.location.href);
        if (uR.searchParams.has('rammi')) { uR.searchParams.delete('rammi'); history.replaceState(history.state, '', uR.toString().replace(/%2C/g, ',')); }
      } catch (e) { /* old browser: URL stays */ }
    };
    speglaMagn();
    maelaHaus();
    metaStiku();
    /* after the adapter is ready (its engine decides whether the sign has a frame) */
    if (hl && hl.then) hl.then(rammiSlodHreinsa, function () {}); else rammiSlodHreinsa();
    /* FB 21.10 (C1): Bæta í körfu / Kaupa núna come disabled from sm-vel.liquid (data-vs-bida), so nothing reaches the cart
       before this script and the engine are ready (slow 3G, no JavaScript). Enabled after the engine has loaded and drawn;
       an engine that fails to load keeps them disabled and says so. */
    var virkjaHnappa = function () {
      teikna();
      $$('[data-vs-bida]', form).forEach(function (b) { b.disabled = false; b.removeAttribute('data-vs-bida'); });
    };
    if (adapterVantar) { teikna(); kaupaVilla(ord.villa_hlada || 'Ekki tókst að hlaða hönnuninni. Hladdu síðuna aftur eða hafðu samband.'); }
    else if (hl && hl.then) hl.then(virkjaHnappa, function () { kaupaVilla(ord.villa_hlada || 'Ekki tókst að hlaða hönnuninni. Hladdu síðuna aftur eða hafðu samband.'); });
    else virkjaHnappa();
    if (hl && hl.then) hl.then(function () { teikna(); flisEpoch += 1; flisarTeikna(0); }, function () {});
    /* FG 21.10 (eigandi): the fields that go on the sign show the typed text in the sign's own font, on every engine, and follow
       a font change at once. The font is read from the drawing: the text element that shows the field's line gives family,
       weight and style (fonts the preview has loaded already, so nothing extra loads). An engine that draws outlines says it
       with adapter.reitLetur(el, s, ctx) -> {fontFamily, fontWeight, fontStyle} | {id: SMFram font id} | false (keep the page
       font); an id is turned into a face from the engine's own font file (SMFramSlodir.letur, the same URL, so the cache
       answers). The placeholder stays upright Manrope (sm-sida.css, [data-vs-reitletur]); line-height is fixed, so CLS 0. */
    (function reitaLetur() {
      if (!teikning || !window.getComputedStyle) return;
      var reitur = function (x) {
        if (!x || !(x.tagName === 'TEXTAREA' || (x.tagName === 'INPUT' && /^(text|search|tel)?$/i.test(x.getAttribute('type') || '')))) return false;
        if (x.closest('[data-vs-smamynd],[data-vs-letur],[data-vs-ekki-letur]')) return false;
        var sk = x.closest('[data-vs-skref]');
        return !(sk && sk.getAttribute('data-gerd') === 'aths') && !x.classList.contains('vs-aths');
      };
      var reitir = function () { return $$('input,textarea', rot).filter(reitur); };
      var hreint = function (t) { return trim(String(t || '').toLowerCase().replace(/\s+/g, ' ')); };
      var stillAf = function (el) { var c = getComputedStyle(el); return { fontFamily: c.fontFamily, fontWeight: c.fontWeight, fontStyle: c.fontStyle, st: parseFloat(c.fontSize) || 0 }; };
      var andlit = {};
      var andlitAf = function (id) {
        if (andlit[id]) return andlit[id];
        var sl = window.SMFramSlodir && window.SMFramSlodir.letur, url = sl && sl[id];
        if (!url || !window.FontFace || !document.fonts || !window.fetch) return (andlit[id] = Promise.resolve(''));
        var nafn = 'SM reitur ' + id;
        andlit[id] = fetch(url, { credentials: 'same-origin' }).then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); }).then(function (t) {
          var m = /\bb64"?\s*:\s*(["'])([A-Za-z0-9+\/=\\\s]+)\1/.exec(t);
          if (!m) throw new Error('b64');
          var bin = window.atob(m[2].replace(/[^A-Za-z0-9+\/=]/g, '')), u = new Uint8Array(bin.length);
          for (var i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
          var f = new FontFace(nafn, u.buffer);
          return f.load().then(function () { document.fonts.add(f); return '"' + nafn + '"'; });
        })['catch'](function () { return ''; });
        return andlit[id];
      };
      var setja = function (x, c) {
        var k = c ? c.fontFamily + '|' + (c.fontWeight || '') + '|' + (c.fontStyle || '') : '';
        if (x.__reitLetur === k) return;
        x.__reitLetur = k;
        x.style.fontFamily = c ? c.fontFamily : '';
        x.style.fontWeight = c && c.fontWeight ? String(c.fontWeight) : '';
        x.style.fontStyle = c && c.fontStyle ? c.fontStyle : '';
      };
      var hlutir = function () {
        var ut = [], w = document.createTreeWalker(teikning, 4, null, false), n;
        while ((n = w.nextNode())) {
          var e = n.parentElement, t = hreint(n.nodeValue);
          if (!t || !e || /^(script|style|title|desc)$/i.test(e.tagName)) continue;
          ut.push({ t: t, e: e });
        }
        return ut;
      };
      var skanna = function () {
        var rr = reitir();
        rr.forEach(function (x) { if (!x.hasAttribute('data-vs-reitletur')) x.setAttribute('data-vs-reitletur', ''); });
        var s = null, H = null, h2 = null;
        rr.forEach(function (x) {
          var linur = String(x.value || '').split(/\r?\n/).map(hreint).filter(Boolean);
          if (!s) s = stada();
          var kr = kalla(adapter, 'reitLetur', [x, s, ctx], null);
          if (kr === null && adapter === ADAPTERS.mynd && FRAM_TEXTI[gogn.vara && gogn.vara.handle]) kr = { id: FRAM_TEXTI[gogn.vara.handle].letur };
          if (kr === false) { setja(x, null); return; }
          if (kr && kr.id) {
            if (!linur.length && !x.__reitLetur) return;
            andlitAf(kr.id).then(function (fam) { if (fam) setja(x, { fontFamily: fam + ', ' + (getComputedStyle(x).fontFamily || 'serif') .split(',').slice(-1)[0], fontWeight: '400', fontStyle: 'normal' }); });
            return;
          }
          if (kr && kr.fontFamily) { setja(x, kr); return; }
          if (!linur.length) return;
          if (!H) H = hlutir();
          var hit = null, i, j;
          for (j = 0; j < linur.length && !hit; j++) for (i = 0; i < H.length; i++) if (H[i].t === linur[j]) { hit = H[i].e; break; }
          for (j = 0; j < linur.length && !hit; j++) for (i = 0; i < H.length; i++) if (H[i].t.indexOf(linur[j]) > -1 || (H[i].t.length > 2 && linur[j].indexOf(H[i].t) > -1)) { hit = H[i].e; break; }
          if (hit) setja(x, stillAf(hit));
          else if (!x.__reitLetur) {
            /* nothing to match yet (the first letter, the drawing still draws): the largest letters of the drawing */
            if (h2 === null) { h2 = ''; H.forEach(function (o) { var c = stillAf(o.e); if (!h2 || c.st > h2.st) h2 = c; }); }
            if (h2) setja(x, h2);
          }
        });
      };
      var bid = null;
      var seinna = function () { if (bid) return; bid = setTimeout(function () { bid = null; skanna(); }, 30); };
      if (window.MutationObserver) new MutationObserver(seinna).observe(teikning, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['font-family', 'font-weight', 'font-style', 'style', 'class'] });
      rot.addEventListener('input', seinna, true);
      rot.addEventListener('change', seinna, true);
      skanna();
    })();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { teikna(); flisEpoch += 1; flisarTeikna(0); });
    /* Hleðsluflökt (eigandi 08.10): á lifandi síðu liggur tjald yfir sviðinu (sm-sida.css, .vs-rammi::after) þar til fyrsta
       heila teikningin er tilbúin: vélin hefur skilað (hl), letur komin, myndir í teikningunni og veggurinn sóttir, tvær
       athuganir í röð. Þá er data-vs-tilbuid sett og tjaldið dofnar út (veggur, plata og texti birtast saman). Hámark 2,2 s. */
    (function svidAfhjupa() {
      var upphaf = Date.now(), lokid = false, tel = 0, veggMynd = null;
      function opna() {
        if (lokid) return; lokid = true; rot.setAttribute('data-vs-tilbuid', '');
        /* CS 19.10: a reserved place for the button bar that no engine filled goes away (a product with takkar on but no bar) */
        setTimeout(function () { var st = $('[data-vs-takkar-stadur]', rot); if (st && st.parentNode) st.parentNode.removeChild(st); }, 1500);
      }
      if (!rammi || !merkiEl || !lifandiFra || rot.hasAttribute('data-vs-tilbuid')) { opna(); return; }
      function tilbuid() {
        void rammi.offsetWidth;
        if (document.fonts && document.fonts.status === 'loading') return false;
        var imgs = teikning.querySelectorAll('img'), i;
        for (i = 0; i < imgs.length; i++) {
          if (!imgs[i].getAttribute('src') && !imgs[i].currentSrc) { if (Date.now() - upphaf < 700) return false; continue; }
          if (!imgs[i].complete) return false;
        }
        var m = /url\(["']?([^"')]+)/.exec(getComputedStyle(rammi).backgroundImage || '');
        if (m) {
          if (!veggMynd || veggMynd.__u !== m[1]) { veggMynd = new Image(); veggMynd.__u = m[1]; veggMynd.src = m[1]; }
          if (!veggMynd.complete) return false;
        }
        return true;
      }
      function lokaSkref() {
        var bid = [];
        [].forEach.call(teikning.querySelectorAll('img'), function (im) { if (im.decode) bid.push(im.decode().catch(function () {})); });
        Promise.all(bid).then(function () { requestAnimationFrame(function () { requestAnimationFrame(opna); }); });
      }
      function kanna() {
        if (lokid) return;
        if (Date.now() - upphaf > 2200) { opna(); return; }
        if (tilbuid()) { tel += 1; if (tel >= 2) { lokaSkref(); return; } } else tel = 0;
        setTimeout(kanna, 50);
      }
      if (hl && hl.then) hl.then(kanna, kanna); else kanna();
      setTimeout(opna, 2400);
    })();
    /* ---- CK 19.10 (QA1): a field that gets focus is never left under the sticky preview on a phone ----
       scroll-margin-top (sm-sida.css, var --vs-ofan) serves the browser's own scrolling (tab, scrollIntoView);
       after a focus (and once more when the keyboard has opened) a field hidden under the preview is scrolled
       to just below it. All fields of every line, the kit's and the adapters' own. */
    function ofanMerkja() {
      var o = Math.round(ofan());
      if (rot.__vsOfan !== o) { rot.__vsOfan = o; rot.style.setProperty('--vs-ofan', o + 'px'); }
    }
    function synaReit(el) {
      if (!el || !el.getBoundingClientRect || window.innerWidth >= 1001 || !svid) return;
      var r = el.getBoundingClientRect(), o = ofan();
      if (r.height && (r.top < o - 2 || r.top > window.innerHeight - Math.min(160, window.innerHeight / 4))) {
        sjalfSkrun = Date.now();
        scrollTil(window.pageYOffset + r.top - o);
      }
    }
    ctx.synaReit = synaReit;
    form.addEventListener('focusin', function (e) {
      var t = e.target;
      if (!t || !(textaReitur(t) || t.tagName === 'SELECT')) return;
      ofanMerkja();
      setTimeout(function () { if (document.activeElement === t && Date.now() - sjalfSkrun > 500) synaReit(t); }, 80);
      setTimeout(function () { if (document.activeElement === t && Date.now() - sjalfSkrun > 500) synaReit(t); }, 450);
    });
    window.addEventListener('resize', ofanMerkja);
    window.addEventListener('scroll', function () { if (!rot.__vsOfanT) rot.__vsOfanT = setTimeout(function () { rot.__vsOfanT = null; ofanMerkja(); }, 200); }, { passive: true });
    ofanMerkja();

    /* ---- CK 19.10 (QA5): typed text and choices come back after Back when the browser had dropped the page ----
       (no bfcache, e.g. an iPhone short on memory). Kept in sessionStorage for this page address only (never the
       URL, never another tab); put back only on a back/forward load, into empty fields, with the same events a
       customer's typing gives, text first, then switches and lists, then the choices (a chosen size stays chosen). */
    /* P2-G1 21.10 (V09): the key is read at save time (a family tile changes the address with replaceState, and Back loads that
       address) and the field keys leave out the section id (the other product's page has another template section id) */
    function geymaLykill() { return 'sm-vs-stada:' + (window.location.pathname || ''); }
    function anSnids(v) { return String(v || '').replace(/^[^\[\]]*?__/, ''); }
    function reitirAllir() {
      return $$('input, textarea, select', form).filter(function (x) {
        return x.type !== 'file' && x.type !== 'hidden' && x.type !== 'submit' && x.type !== 'button' && !x.disabled && !x.hasAttribute('data-vs-afbrigdi');
      });
    }
    function reitLykill(x, fjoldi) {
      var a = [x.tagName, x.type || '', anSnids(x.name), anSnids(x.id)];
      [].forEach.call(x.attributes, function (at) { if (/^data-/.test(at.name) && !/^data-(vs-fokus|stada)$/.test(at.name)) a.push(at.name + '=' + at.value); });
      var k = a.join('|');
      fjoldi[k] = (fjoldi[k] || 0) + 1;
      return k + '#' + fjoldi[k];
    }
    var geymaT = null;
    function geymaStodu() {
      clearTimeout(geymaT); geymaT = null;
      var d = {}, fjoldi = {}, eitthvad = false;
      reitirAllir().forEach(function (x) {
        var k = reitLykill(x, fjoldi);
        if (x.type === 'radio') { if (x.checked) { d[k] = 1; eitthvad = true; } }
        else if (x.type === 'checkbox') d[k] = x.checked ? 1 : 0;
        else if (x.value && (textaReitur(x) || x.tagName === 'SELECT' || x.type === 'number')) { d[k] = x.value; if (textaReitur(x)) eitthvad = true; }
      });
      try {
        if (eitthvad) window.sessionStorage.setItem(geymaLykill(), JSON.stringify({ t: Date.now(), d: d }));
        else window.sessionStorage.removeItem(geymaLykill());
      } catch (e) {}
    }
    form.addEventListener('input', function () { clearTimeout(geymaT); geymaT = setTimeout(geymaStodu, 400); });
    form.addEventListener('change', function () { clearTimeout(geymaT); geymaT = setTimeout(geymaStodu, 400); });
    window.addEventListener('pagehide', function () { if (geymaT) geymaStodu(); });
    (function endurheimta() {
      var nav = null;
      try { nav = performance.getEntriesByType('navigation')[0]; } catch (e) {}
      if (!nav || nav.type !== 'back_forward') return;
      var g = null;
      try { g = JSON.parse(window.sessionStorage.getItem(geymaLykill()) || 'null'); } catch (e) { g = null; }
      if (!g || !g.d || !(Date.now() - g.t < 7200000)) return;
      var gert = {}, umferd = 0;
      var atburdur = function (x, nafn) {
        var ev;
        try { ev = new Event(nafn, { bubbles: true }); } catch (e) { ev = document.createEvent('Event'); ev.initEvent(nafn, true, false); }
        x.dispatchEvent(ev);
      };
      /* one stage at a time with a pause between (a typed name redraws the next steps a moment later): text, then
         switches and lists, then the choices; fields that appear after a switch come in a later round */
      var stig1 = function (stig) {
        var fjoldi = {};
        reitirAllir().forEach(function (x) {
          var k = reitLykill(x, fjoldi), v = g.d[k];
          if (gert[k] || v == null) return;
          var teg = x.type === 'radio' ? 2 : (x.type === 'checkbox' || x.tagName === 'SELECT') ? 1 : 0;
          if (teg !== stig) return;
          gert[k] = 1;
          if (teg === 0) { if (!x.value) { x.value = v; atburdur(x, 'input'); } }
          else if (x.type === 'checkbox') { if (x.checked !== !!v) { x.checked = !!v; atburdur(x, 'change'); } }
          else if (x.tagName === 'SELECT') { if (x.value !== v) { x.value = v; atburdur(x, 'change'); } }
          else { x.checked = true; atburdur(x, 'change'); }
        });
      };
      var reyna = function () {
        umferd += 1;
        stig1(0);
        setTimeout(function () { stig1(1); setTimeout(function () { stig1(2); if (umferd < 3) setTimeout(reyna, 300); }, 350); }, 350);
      };
      setTimeout(reyna, 250);
    })();

    if (!ERIS && 'MutationObserver' in window) {
      var tugaT = null;
      var tugaMo = new MutationObserver(function () { if (!tugaT) tugaT = (window.requestAnimationFrame || setTimeout)(function () { tugaT = null; tugaPunktar(rot); }); });
      tugaMo.observe(rot, { childList: true, subtree: true, characterData: true });
      tugaPunktar(rot);
    }

    /* the bulk discount note decides the tier (cart plus quantity): the total follows its classes */
    if (magnEl && 'MutationObserver' in window) new MutationObserver(function () { verdTexti(); }).observe(magnEl, { attributes: true, subtree: true, attributeFilter: ['class'] });
    verdTexti();
    /* the chat bubble (Shopify Inbox) arrives late: keep it clear of the bar */
    if ('MutationObserver' in window) {
      var spjallMo = new MutationObserver(function () { if (document.querySelector('shopify-chat')) { aSkrun(); } });
      spjallMo.observe(document.body, { childList: true });
      setTimeout(function () { spjallMo.disconnect(); }, 60000);
    }
    if (simiMidill && simiMidill.addEventListener) simiMidill.addEventListener('change', function () { flisEpoch += 1; flisarTeikna(0); });

    return {
      rot: rot,
      ctx: ctx,
      stada: stada,
      afbrigdi: function () { return afb; },
      vandamal: vandamal,
      flisTolur: function () { return { skrif: flisSkrif, teiknad: flisTeiknad, rod: flisRod.length }; },
      teikna: teikna,
      setja: function (lykill, gildi) {
        var s = finnaSkref(lykill);
        if (!s) return false;
        if (s.gerd === 'valkostur') { if (s.p2) veljaPar(s.p, s.p2, String(gildi)); else veljaValkost(s.p, String(gildi)); return true; }
        if (s.gerd === 'val') { s.radio.forEach(function (r) { r.checked = r.value === gildi; }); samstilla(true); return true; }
        if (s.gerd === 'rammi') { rammiSnert = true; rammiOsk = ''; setjaRammiGildi(s, String(gildi)); samstilla(true); return true; }
        if (s.gerd === 'magn') { setjaMagn(parseInt(gildi, 10) || 1); return true; }
        if (s.inntak) { s.inntak.value = gildi; uppfaera(); teikna(); return true; }
        return false;
      }
    };
  }

  /* A page waits for its adapter (engine and adapter scripts load beside this one); if it never
     comes, the page starts with product photos once the window has loaded, so it is never dead. */
  var neyd = false;
  function byrja(rot) {
    $$('[data-sm-sida]', rot || document).forEach(function (el) {
      if (el.__smSida) return;
      var nafn = el.getAttribute('data-adapter') || 'mynd';
      if (!ADAPTERS[nafn] && !neyd) return;
      el.__smSida = true;
      var s = Sida(el);
      if (s) SIDUR.push(s);
    });
  }
  function skraOgByrja(kodi, a) {
    skraAdapter(kodi, a);
    if (document.readyState !== 'loading') byrja();
  }
  (window.SMVelSidaBid || []).forEach(function (x) { if (x && x[0]) skraAdapter(x[0], x[1]); });
  window.addEventListener('load', function () { setTimeout(function () { neyd = true; byrja(); }, 4000); });

  window.SMVelSida = { skra: skraOgByrja, byrja: byrja, kr: kr, sidur: SIDUR, adapterar: ADAPTERS,
    utgafa: 'kjarni-1.1', framJson: true, einkvaemId: einkvaemId, rammi: rammiRumfraedi, leturLykill: leturLykill,
    handleizeIS: handleizeIS, kanon: kanon, syn: syn, kit: kitSmidir, kitUtgafa: 'k0', tjakn: tjakn, htmlMynd: htmlMynd };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { byrja(); });
  else byrja();
  /* theme editor: re-boot a section after it is re-rendered */
  document.addEventListener('shopify:section:load', function (e) { byrja(e.target); });
})();
